import React, { useState, useEffect, createContext, useContext, useRef, useCallback, useImperativeHandle } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import { login as apiLogin, register as apiRegister, getCurrentUser, getSkills, getLeaderboard, BASE_PATH, sharedSession } from './api';
import { SHARED_SESSION_KEY } from './sharedSession';
import { io } from 'socket.io-client';
import { Link, Navigate, useNavigate, SocialImage, SocialVideo, SocialAudio } from './socialRouting';
import { API_URL, AUTH_TOKEN_KEY, AUTH_USER_KEY, SAVED_ACCOUNTS_KEY, SOCKET_PATH, mediaUrl, richMediaHtml } from './siteConfig';
import { ReactComponent as CreateIcon } from './images/create.svg';
import comingSoonImg from './images/coming-soon.png';
import nonbianaryImg from './images/nonbianary.png';
import transgirlImg from './images/transgirl.png';
import lesbianImg from './images/lesbian.png';
import belongingMark from './logo-belonging.svg';
import belongingWordmark from './belonging.svg';
import arawraFavicon from './assets/arawra-favicon.svg';
import { ThemeProvider, ThemeToggle, ThemeSetting } from './Theme';

const AppBrand = ({ className = '' }) => <span className={`belonging-brand ${className}`}>
  <img src={belongingMark} className="belonging-brand-mark" alt="" />
  <img src={belongingWordmark} className="belonging-brand-wordmark" alt="Belonging" />
</span>;

const HeaderBrand = () => <span className="arawra-header-brand">
  <img src={arawraFavicon} className="arawra-header-favicon" alt="" />
  <span className="arawra-header-wordmark" style={{ fontSize: 'clamp(1.296rem, 3.6vw, 1.944rem)' }}>Arawra<span style={{ marginInline: '0.12em' }}>|</span>Social</span>
  <img src={`${BASE_PATH}/favicon/favicon.svg?v=a792fa4f`} className="arawra-header-favicon" alt="" style={{ borderRadius: '25%' }} />
</span>;

// ── Socket.io singleton ──────────────────────────────────────────────────────
let _socket = null;

function getSocket() {
  if (!_socket) {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    _socket = io(window.location.origin, {
      auth: { token },
      transports: ['websocket', 'polling'],
      path: SOCKET_PATH
    });
  }
  return _socket;
}

function resetSocket() {
  if (_socket) {
    _socket.disconnect();
    _socket = null;
  }
}

// ── Simple event bus for triggering calls from any component ─────────────────
const callEventBus = {
  _listeners: [],
  emit(data) { this._listeners.forEach(l => l(data)); },
  on(l) { this._listeners.push(l); },
  off(l) { this._listeners = this._listeners.filter(x => x !== l); }
};

// ── URL detection utility ────────────────────────────────────────────────────
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
function detectUrls(text) {
  if (!text) return [];
  return [...new Set(text.match(URL_REGEX) || [])];
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Parse the tribe icon field into { img, flags }
// Supports: new JSON {img, flags}, old JSON flags-array, single path string, emoji
function parseTribeIcon(raw) {
  try {
    const p = JSON.parse(raw);
    if (p && typeof p === 'object' && !Array.isArray(p)) return { img: p.img || null, flags: p.flags || [] };
    if (Array.isArray(p)) return { img: null, flags: p };
  } catch {}
  if (raw && raw.startsWith('flags/')) return { img: null, flags: [raw] };
  return { img: raw || null, flags: [] };
}

// Renders the main tribe icon image (square, from tribe-icons/ or /uploads/)
function TribeIcon({ icon, size = 48, style = {} }) {
  const { img } = parseTribeIcon(icon);
  const src = img
    ? (img.startsWith('/') ? img : `${BASE_PATH}/${img}`)
    : `${BASE_PATH}/tribe-icons/people.svg`;
  if (src.startsWith('data:') || (img && !img.includes('/'))) {
    // Emoji fallback
    return <span style={{ fontSize: size * 0.75, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, ...style }}>{img}</span>;
  }
  return <SocialImage src={src} alt="" style={{ width: size, height: size, objectFit: 'contain', borderRadius: 8, display: 'block', flexShrink: 0, ...style }} />;
}

// Renders the tribe flags as a small horizontal strip
function TribeFlagStrip({ icon, height = 14, maxShow = 4, style = {} }) {
  const { flags } = parseTribeIcon(icon);
  if (!flags.length) return null;
  const shown = flags.slice(0, maxShow);
  const extra = flags.length - shown.length;
  return (
    <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center', ...style }}>
      {shown.map((f, i) => (
        <SocialImage key={i} src={`${BASE_PATH}/${f}`} alt="" style={{ height, width: height * 1.5, objectFit: 'cover', borderRadius: 1, display: 'block' }} />
      ))}
      {extra > 0 && <span style={{ fontSize: height * 0.9, color: "var(--ui-muted)", lineHeight: 1 }}>+{extra}</span>}
    </span>
  );
}

// All LGBTQIA+ pride flags — sorted from most widely recognised to most niche
const PRIDE_FLAGS = [
  // ── The big classics ──────────────────────────────────────────────────────
  {file:"rainbow-flag1.svg",label:"Rainbow Pride"},
  {file:"progress-pride-flag1.svg",label:"Progress Pride"},
  {file:"philadelphia-pride-flag.svg",label:"Philadelphia Pride"},
  {file:"gay-flag-baker.svg",label:"Gay (Baker / Original)"},
  // ── Core identities ───────────────────────────────────────────────────────
  {file:"transgender-flag.svg",label:"Transgender"},
  {file:"bisexual-flag.svg",label:"Bisexual"},
  {file:"lesbian-flag-5-stripe.svg",label:"Lesbian"},
  {file:"pansexual-flag.svg",label:"Pansexual"},
  {file:"asexual-flag.svg",label:"Asexual"},
  {file:"non-binary-flag.svg",label:"Non-Binary"},
  {file:"gay-men-pride-flag-2019.svg",label:"Gay Men"},
  {file:"genderqueer-flag.svg",label:"Genderqueer"},
  {file:"genderfluid-flag.svg",label:"Genderfluid"},
  {file:"intersex-flag.svg",label:"Intersex"},
  {file:"queer-flag.svg",label:"Queer"},
  {file:"aromantic-flag.svg",label:"Aromantic"},
  {file:"agender-flag.svg",label:"Agender"},
  {file:"polyamory-flag.svg",label:"Polyamory"},
  {file:"sapphic-flag.svg",label:"Sapphic"},
  {file:"two-spirit-flag.png",label:"Two-Spirit"},
  {file:"questioning-flag.svg",label:"Questioning"},
  // ── Well-known sub-identities ─────────────────────────────────────────────
  {file:"demisexual-flag.svg",label:"Demisexual"},
  {file:"demiromantic-flag.svg",label:"Demiromantic"},
  {file:"demiboy-flag.svg",label:"Demiboy"},
  {file:"demigirl-flag.svg",label:"Demigirl"},
  {file:"demigender-flag.svg",label:"Demigender"},
  {file:"aroace-flag.svg",label:"Aroace"},
  {file:"bigender-flag.svg",label:"Bigender"},
  {file:"omnisexual-flag.svg",label:"Omnisexual"},
  {file:"polysexual-flag.svg",label:"Polysexual"},
  {file:"achillean-flag.svg",label:"Achillean (MLM)"},
  {file:"community-lesbian-flag.svg",label:"Community Lesbian"},
  {file:"labrys-lesbian-flag.svg",label:"Labrys Lesbian"},
  {file:"lesbian-pride-double-venus1.svg",label:"Lesbian (Double Venus)"},
  {file:"lesbian-flags-7-stripes.png",label:"Lesbian (7-Stripe)"},
  {file:"butch-flag.svg",label:"Butch"},
  {file:"femme-flag.svg",label:"Femme"},
  {file:"twink-flag.svg",label:"Twink"},
  {file:"bear-flag.svg",label:"Bear"},
  {file:"transmasculine-flag.svg",label:"Transmasculine"},
  {file:"transfeminine2.svg",label:"Transfeminine"},
  {file:"transmasc-flag.png",label:"Transmasc"},
  {file:"androgynous-flag.svg",label:"Androgynous"},
  {file:"androgyne-pride-flag.png",label:"Androgyne"},
  {file:"pangender-flag.png",label:"Pangender"},
  {file:"multigender-flag.svg",label:"Multigender"},
  {file:"neutrois.svg",label:"Neutrois"},
  {file:"gender-neutral-flag.svg",label:"Gender Neutral"},
  {file:"unlabeled-flag.svg",label:"Unlabeled"},
  {file:"biromantic-flag-(by-pride-flags).png",label:"Biromantic"},
  {file:"panromantic-pride-flag.png",label:"Panromantic"},
  {file:"asexual-spectrum-flag.svg",label:"Asexual Spectrum"},
  {file:"aromantic-spectrum-flag.svg",label:"Aromantic Spectrum"},
  {file:"agender-spectrum-flag.png",label:"Agender Spectrum"},
  {file:"queerplatonic-attraction-flag.png",label:"Queerplatonic"},
  {file:"straight-ally-flag.png",label:"Straight Ally"},
  {file:"lgbti-flag.svg",label:"LGBTI"},
  {file:"cougar-lipstick-pink.png",label:"Lipstick Lesbian"},
  {file:"sex-worker-inclusive-progress-flag.svg",label:"Sex Worker Inclusive Progress"},
  // ── Ace / Aro spectrum ────────────────────────────────────────────────────
  {file:"gray-asexual-flag.svg",label:"Gray-Asexual"},
  {file:"gray-aromantic-flag.svg",label:"Gray-Aromantic"},
  {file:"aroace-flag-alt.svg",label:"Aroace (Alt)"},
  {file:"aroaceflux.png",label:"Aroaceflux"},
  {file:"aroflux-flag.svg",label:"Aroflux"},
  {file:"aceflux-flag.svg",label:"Aceflux"},
  {file:"alloace-flag.svg",label:"Alloace"},
  {file:"alloaro-flag.svg",label:"Alloaro"},
  {file:"oriented-aroace-flag.svg",label:"Oriented Aroace"},
  {file:"angled-aroace-flag.svg",label:"Angled Aroace"},
  {file:"apothisexual-flag.svg",label:"Apothisexual"},
  {file:"apothiromanticflag.png",label:"Apothiromantic"},
  {file:"aegosexual-flag.svg",label:"Aegosexual"},
  {file:"aegoromantic-flag.svg",label:"Aegoromantic"},
  {file:"cupiosexual-flag.svg",label:"Cupiosexual"},
  {file:"cupioromantic-flag.svg",label:"Cupioromantic"},
  {file:"fraysexual-flag.svg",label:"Fraysexual"},
  {file:"frayromantic-flag.svg",label:"Frayromantic"},
  {file:"abrosexual-flag.svg",label:"Abrosexual"},
  {file:"abroromantic-flag.png",label:"Abroromantic"},
  {file:"bicurious.png",label:"Bicurious"},
  // ── Gender diverse ────────────────────────────────────────────────────────
  {file:"agender-4-stripe-flag.svg",label:"Agender (4-Stripe)"},
  {file:"agenderflux-flag.png",label:"Agenderflux"},
  {file:"genderflux-flag.png",label:"Genderflux"},
  {file:"genderfuck-flag.svg",label:"Genderfuck"},
  {file:"genderfae-flag.svg",label:"Genderfae"},
  {file:"genderfaun-flag.svg",label:"Genderfaun"},
  {file:"genderflor-flag.svg",label:"Genderflor"},
  {file:"gender-creative.svg",label:"Gender Creative"},
  {file:"neurogender-flag.svg",label:"Neurogender"},
  {file:"maverique-flag.png",label:"Maverique"},
  {file:"intergender-flag.svg",label:"Intergender"},
  {file:"intergender-flag-2020.png",label:"Intergender (2020)"},
  {file:"polygender.webp",label:"Polygender"},
  {file:"trigender.jpg",label:"Trigender"},
  {file:"nonbinary-boy-flag.png",label:"Nonbinary Boy"},
  {file:"nonbinary-girl-flag.png",label:"Nonbinary Girl"},
  {file:"toric.png",label:"Toric"},
  {file:"trixic.png",label:"Trixic"},
  {file:"enbianflag.png",label:"Enbian"},
  {file:"demifluid.png",label:"Demifluid"},
  {file:"demiflux.jpg",label:"Demiflux"},
  {file:"diamoric-flag.svg",label:"Diamoric"},
  {file:"transmasculine-2.svg",label:"Transmasculine (Alt)"},
  {file:"transmasculine-flag-3.svg",label:"Transmasculine (Alt 2)"},
  {file:"transfeminine-flag2.png",label:"Transfeminine (Alt)"},
  {file:"androgyne-flag-2-.png",label:"Androgyne (Alt)"},
  {file:"androsexual-flag.svg",label:"Androsexual"},
  {file:"gynesexual.svg",label:"Gynesexual"},
  {file:"egogender-momma-mogai-sphinx.png",label:"Egogender"},
  {file:"graygender-flag-original-file.png",label:"Graygender"},
  // ── Romantic attractions ──────────────────────────────────────────────────
  {file:"omniromantic-flag.png",label:"Omniromantic"},
  {file:"polyromantic-flag.png",label:"Polyromantic"},
  {file:"biromantic-flag1.png",label:"Biromantic (Alt)"},
  {file:"ceteroromantic-flag.png",label:"Ceteroromantic"},
  {file:"androromanticflag.png",label:"Androromantic"},
  {file:"gyneromanticflag.png",label:"Gyneromantic"},
  {file:"grayromantic.png",label:"Grayromantic"},
  {file:"desinoromantic-flag.svg",label:"Desinoromantic"},
  // ── Historical / alt versions ─────────────────────────────────────────────
  {file:"gay-flag-seven-stripe.svg",label:"Gay (7-Stripe)"},
  {file:"gilbert-baker-lavender-flag.svg",label:"Gilbert Baker Lavender"},
  {file:"gilbert-baker-lavender.png",label:"Gilbert Baker Lavender (Alt)"},
  {file:"first-aromantic-flag.svg",label:"Aromantic (First)"},
  {file:"second-aromantic-flag.svg",label:"Aromantic (Second)"},
  {file:"achillean-flag-original.jpg",label:"Achillean (Original)"},
  {file:"original-sapphic-flag.png",label:"Sapphic (Original)"},
  {file:"lydia-sapphic-flag.svg",label:"Lydia Sapphic"},
  {file:"oldintersexflag.png",label:"Intersex (Old)"},
  {file:"oldintergender-flag.png",label:"Intergender (Old)"},
  {file:"oldaltintergender.png",label:"Intergender (Old Alt)"},
  {file:"cupioromantic-flag-original-file.png",label:"Cupioromantic (Original)"},
  {file:"cupiosexual.png",label:"Cupiosexual (Alt)"},
  {file:"ceterosexual.jpg",label:"Ceterosexual"},
  {file:"ceterosexual2.png",label:"Ceterosexual (Alt)"},
  {file:"fraysexual-flag.jpg",label:"Fraysexual (Alt)"},
  {file:"frayromantic-alt.png",label:"Frayromantic (Alt)"},
  {file:"fluid-flag-original.png",label:"Fluid (Original)"},
  {file:"desinoromantic-flag-original.png",label:"Desinoromantic (Original)"},
  {file:"the-butch-flag.png",label:"Butch (Alt)"},
  {file:"the-femme-flag.png",label:"Femme (Alt)"},
  {file:"alt-genderfluid-flag.png",label:"Genderfluid (Alt)"},
  {file:"alternative-pansexual-flag.svg",label:"Alternative Pansexual"},
  {file:"gendercreativeprideflag.png",label:"Gender Creative (Alt)"},
  {file:"gynesexualflag.png",label:"Gynesexual (Alt)"},
  {file:"transmasculine-flag.png",label:"Transmasculine (Alt 3)"},
  {file:"transmasculine.png",label:"Transmasculine (Alt 4)"},
  {file:"unlabeledprideflag.png",label:"Unlabeled (Alt)"},
  {file:"polyamory.png",label:"Polyamory (Alt)"},
  {file:"polyromantic.png",label:"Polyromantic (Alt)"},
  {file:"tricolor-polyamory-pride-flag.png",label:"Tricolor Polyamory"},
  // ── Niche / community-specific ────────────────────────────────────────────
  {file:"a-spec-flag.svg",label:"A-Spec"},
  {file:"a-spec-flag-aloe-vera.svg",label:"A-Spec (Aloe Vera)"},
  {file:"aplatonic-flag.svg",label:"Aplatonic"},
  {file:"aplatonic-flag.png",label:"Aplatonic (Alt)"},
  {file:"another-qpr-flag.jpg",label:"QPR"},
  {file:"multisexual-flag.png",label:"Multisexual"},
  {file:"monosexual.png",label:"Monosexual"},
  {file:"pomosexual.png",label:"Pomosexual"},
  {file:"novosexual-flag-alt.png",label:"Novosexual"},
  {file:"novo-original-hq.png",label:"Novo"},
  {file:"fluidflux-flag.svg",label:"Fluidflux"},
  {file:"fluidflux.png",label:"Fluidflux (Alt)"},
  {file:"chevron-queer-flag.svg",label:"Chevron Queer"},
  {file:"straight-queer.svg",label:"Straight Queer"},
  {file:"heteroqueer-gayer9000.jpg",label:"Heteroqueer"},
  {file:"queerhet-kaestral.png",label:"Queerhet"},
  {file:"hijra-flag-original-file.png",label:"Hijra"},
  {file:"waria.png",label:"Waria"},
  {file:"mogai-pride-flag.png",label:"MOGAI"},
  {file:"lgbti-flag.svg",label:"LGBTI"},
  {file:"puppy-pride-flag.svg",label:"Puppy Pride"},
  {file:"leather-pride-flag.svg",label:"Leather Pride"},
  {file:"rubber-pride-flag.svg",label:"Rubber Pride"},
  {file:"pony-pride-flag.svg",label:"Pony Pride"},
  {file:"handler-pride-flag.svg",label:"Handler Pride"},
  {file:"disability-pride-flag.svg",label:"Disability Pride"},
  {file:"intersex-inclusive-progress-flag.svg",label:"Intersex-Inclusive Progress"},
  {file:"uranic-flag.svg",label:"Uranic"},
  {file:"neptunic-flag.svg",label:"Neptunic"},
  {file:"lithromantic-flag.svg",label:"Lithromantic"},
  {file:"quoiromantic-flag.svg",label:"Quoiromantic"},
  {file:"xenogender-flag.svg",label:"Xenogender"},
  {file:"xenogender-flag-alt.svg",label:"Xenogender (Alt)"},
  {file:"xenicflag2.webp",label:"Xenic"},
  // ── Country flags ──────────────────────────────────────────────────────
  {file:"country-ad.svg",label:"Andorra"},
  {file:"country-ae.svg",label:"United Arab Emirates"},
  {file:"country-af.svg",label:"Afghanistan"},
  {file:"country-ag.svg",label:"Antigua & Barbuda"},
  {file:"country-ai.svg",label:"Anguilla"},
  {file:"country-al.svg",label:"Albania"},
  {file:"country-am.svg",label:"Armenia"},
  {file:"country-ao.svg",label:"Angola"},
  {file:"country-aq.svg",label:"Antarctica"},
  {file:"country-ar.svg",label:"Argentina"},
  {file:"country-as.svg",label:"American Samoa"},
  {file:"country-at.svg",label:"Austria"},
  {file:"country-au.svg",label:"Australia"},
  {file:"country-aw.svg",label:"Aruba"},
  {file:"country-ax.svg",label:"Åland Islands"},
  {file:"country-az.svg",label:"Azerbaijan"},
  {file:"country-ba.svg",label:"Bosnia & Herzegovina"},
  {file:"country-bb.svg",label:"Barbados"},
  {file:"country-bd.svg",label:"Bangladesh"},
  {file:"country-be.svg",label:"Belgium"},
  {file:"country-bf.svg",label:"Burkina Faso"},
  {file:"country-bg.svg",label:"Bulgaria"},
  {file:"country-bh.svg",label:"Bahrain"},
  {file:"country-bi.svg",label:"Burundi"},
  {file:"country-bj.svg",label:"Benin"},
  {file:"country-bl.svg",label:"St. Barthélemy"},
  {file:"country-bm.svg",label:"Bermuda"},
  {file:"country-bn.svg",label:"Brunei"},
  {file:"country-bo.svg",label:"Bolivia"},
  {file:"country-bq.svg",label:"Caribbean Netherlands"},
  {file:"country-br.svg",label:"Brazil"},
  {file:"country-bs.svg",label:"Bahamas"},
  {file:"country-bt.svg",label:"Bhutan"},
  {file:"country-bv.svg",label:"Bouvet Island"},
  {file:"country-bw.svg",label:"Botswana"},
  {file:"country-by.svg",label:"Belarus"},
  {file:"country-bz.svg",label:"Belize"},
  {file:"country-ca.svg",label:"Canada"},
  {file:"country-cc.svg",label:"Cocos (Keeling) Islands"},
  {file:"country-cd.svg",label:"Congo - Kinshasa"},
  {file:"country-cf.svg",label:"Central African Republic"},
  {file:"country-cg.svg",label:"Congo - Brazzaville"},
  {file:"country-ch.svg",label:"Switzerland"},
  {file:"country-ci.svg",label:"Côte d’Ivoire"},
  {file:"country-ck.svg",label:"Cook Islands"},
  {file:"country-cl.svg",label:"Chile"},
  {file:"country-cm.svg",label:"Cameroon"},
  {file:"country-cn.svg",label:"China"},
  {file:"country-co.svg",label:"Colombia"},
  {file:"country-cp.svg",label:"Clipperton Island"},
  {file:"country-cr.svg",label:"Costa Rica"},
  {file:"country-cu.svg",label:"Cuba"},
  {file:"country-cv.svg",label:"Cape Verde"},
  {file:"country-cw.svg",label:"Curaçao"},
  {file:"country-cx.svg",label:"Christmas Island"},
  {file:"country-cy.svg",label:"Cyprus"},
  {file:"country-cz.svg",label:"Czechia"},
  {file:"country-de.svg",label:"Germany"},
  {file:"country-dg.svg",label:"Diego Garcia"},
  {file:"country-dj.svg",label:"Djibouti"},
  {file:"country-dk.svg",label:"Denmark"},
  {file:"country-dm.svg",label:"Dominica"},
  {file:"country-do.svg",label:"Dominican Republic"},
  {file:"country-dz.svg",label:"Algeria"},
  {file:"country-ec.svg",label:"Ecuador"},
  {file:"country-ee.svg",label:"Estonia"},
  {file:"country-eg.svg",label:"Egypt"},
  {file:"country-eh.svg",label:"Western Sahara"},
  {file:"country-er.svg",label:"Eritrea"},
  {file:"country-es-ct.svg",label:"Catalonia"},
  {file:"country-es-ga.svg",label:"Galicia"},
  {file:"country-es-pv.svg",label:"Basque Country"},
  {file:"country-es.svg",label:"Spain"},
  {file:"country-et.svg",label:"Ethiopia"},
  {file:"country-fi.svg",label:"Finland"},
  {file:"country-fj.svg",label:"Fiji"},
  {file:"country-fk.svg",label:"Falkland Islands"},
  {file:"country-fm.svg",label:"Micronesia"},
  {file:"country-fo.svg",label:"Faroe Islands"},
  {file:"country-fr.svg",label:"France"},
  {file:"country-ga.svg",label:"Gabon"},
  {file:"country-gb-eng.svg",label:"England"},
  {file:"country-gb-nir.svg",label:"Northern Ireland"},
  {file:"country-gb-sct.svg",label:"Scotland"},
  {file:"country-gb-wls.svg",label:"Wales"},
  {file:"country-gb.svg",label:"United Kingdom"},
  {file:"country-gd.svg",label:"Grenada"},
  {file:"country-ge.svg",label:"Georgia"},
  {file:"country-gf.svg",label:"French Guiana"},
  {file:"country-gg.svg",label:"Guernsey"},
  {file:"country-gh.svg",label:"Ghana"},
  {file:"country-gi.svg",label:"Gibraltar"},
  {file:"country-gl.svg",label:"Greenland"},
  {file:"country-gm.svg",label:"Gambia"},
  {file:"country-gn.svg",label:"Guinea"},
  {file:"country-gp.svg",label:"Guadeloupe"},
  {file:"country-gq.svg",label:"Equatorial Guinea"},
  {file:"country-gr.svg",label:"Greece"},
  {file:"country-gs.svg",label:"South Georgia & South Sandwich Islands"},
  {file:"country-gt.svg",label:"Guatemala"},
  {file:"country-gu.svg",label:"Guam"},
  {file:"country-gw.svg",label:"Guinea-Bissau"},
  {file:"country-gy.svg",label:"Guyana"},
  {file:"country-hk.svg",label:"Hong Kong SAR China"},
  {file:"country-hm.svg",label:"Heard & McDonald Islands"},
  {file:"country-hn.svg",label:"Honduras"},
  {file:"country-hr.svg",label:"Croatia"},
  {file:"country-ht.svg",label:"Haiti"},
  {file:"country-hu.svg",label:"Hungary"},
  {file:"country-ic.svg",label:"Canary Islands"},
  {file:"country-id.svg",label:"Indonesia"},
  {file:"country-ie.svg",label:"Ireland"},
  {file:"country-im.svg",label:"Isle of Man"},
  {file:"country-in.svg",label:"India"},
  {file:"country-io.svg",label:"British Indian Ocean Territory"},
  {file:"country-iq.svg",label:"Iraq"},
  {file:"country-ir.svg",label:"Iran"},
  {file:"country-is.svg",label:"Iceland"},
  {file:"country-it.svg",label:"Italy"},
  {file:"country-je.svg",label:"Jersey"},
  {file:"country-jm.svg",label:"Jamaica"},
  {file:"country-jo.svg",label:"Jordan"},
  {file:"country-jp.svg",label:"Japan"},
  {file:"country-ke.svg",label:"Kenya"},
  {file:"country-kg.svg",label:"Kyrgyzstan"},
  {file:"country-kh.svg",label:"Cambodia"},
  {file:"country-ki.svg",label:"Kiribati"},
  {file:"country-km.svg",label:"Comoros"},
  {file:"country-kn.svg",label:"St. Kitts & Nevis"},
  {file:"country-kp.svg",label:"North Korea"},
  {file:"country-kr.svg",label:"South Korea"},
  {file:"country-kw.svg",label:"Kuwait"},
  {file:"country-ky.svg",label:"Cayman Islands"},
  {file:"country-kz.svg",label:"Kazakhstan"},
  {file:"country-la.svg",label:"Laos"},
  {file:"country-lb.svg",label:"Lebanon"},
  {file:"country-lc.svg",label:"St. Lucia"},
  {file:"country-li.svg",label:"Liechtenstein"},
  {file:"country-lk.svg",label:"Sri Lanka"},
  {file:"country-lr.svg",label:"Liberia"},
  {file:"country-ls.svg",label:"Lesotho"},
  {file:"country-lt.svg",label:"Lithuania"},
  {file:"country-lu.svg",label:"Luxembourg"},
  {file:"country-lv.svg",label:"Latvia"},
  {file:"country-ly.svg",label:"Libya"},
  {file:"country-ma.svg",label:"Morocco"},
  {file:"country-mc.svg",label:"Monaco"},
  {file:"country-md.svg",label:"Moldova"},
  {file:"country-me.svg",label:"Montenegro"},
  {file:"country-mf.svg",label:"St. Martin"},
  {file:"country-mg.svg",label:"Madagascar"},
  {file:"country-mh.svg",label:"Marshall Islands"},
  {file:"country-mk.svg",label:"North Macedonia"},
  {file:"country-ml.svg",label:"Mali"},
  {file:"country-mm.svg",label:"Myanmar (Burma)"},
  {file:"country-mn.svg",label:"Mongolia"},
  {file:"country-mo.svg",label:"Macao SAR China"},
  {file:"country-mp.svg",label:"Northern Mariana Islands"},
  {file:"country-mq.svg",label:"Martinique"},
  {file:"country-mr.svg",label:"Mauritania"},
  {file:"country-ms.svg",label:"Montserrat"},
  {file:"country-mt.svg",label:"Malta"},
  {file:"country-mu.svg",label:"Mauritius"},
  {file:"country-mv.svg",label:"Maldives"},
  {file:"country-mw.svg",label:"Malawi"},
  {file:"country-mx.svg",label:"Mexico"},
  {file:"country-my.svg",label:"Malaysia"},
  {file:"country-mz.svg",label:"Mozambique"},
  {file:"country-na.svg",label:"Namibia"},
  {file:"country-nc.svg",label:"New Caledonia"},
  {file:"country-ne.svg",label:"Niger"},
  {file:"country-nf.svg",label:"Norfolk Island"},
  {file:"country-ng.svg",label:"Nigeria"},
  {file:"country-ni.svg",label:"Nicaragua"},
  {file:"country-nl.svg",label:"Netherlands"},
  {file:"country-no.svg",label:"Norway"},
  {file:"country-np.svg",label:"Nepal"},
  {file:"country-nr.svg",label:"Nauru"},
  {file:"country-nu.svg",label:"Niue"},
  {file:"country-nz.svg",label:"New Zealand"},
  {file:"country-om.svg",label:"Oman"},
  {file:"country-pa.svg",label:"Panama"},
  {file:"country-pe.svg",label:"Peru"},
  {file:"country-pf.svg",label:"French Polynesia"},
  {file:"country-pg.svg",label:"Papua New Guinea"},
  {file:"country-ph.svg",label:"Philippines"},
  {file:"country-pk.svg",label:"Pakistan"},
  {file:"country-pl.svg",label:"Poland"},
  {file:"country-pm.svg",label:"St. Pierre & Miquelon"},
  {file:"country-pn.svg",label:"Pitcairn Islands"},
  {file:"country-pr.svg",label:"Puerto Rico"},
  {file:"country-ps.svg",label:"Palestinian Territories"},
  {file:"country-pt.svg",label:"Portugal"},
  {file:"country-pw.svg",label:"Palau"},
  {file:"country-py.svg",label:"Paraguay"},
  {file:"country-qa.svg",label:"Qatar"},
  {file:"country-re.svg",label:"Réunion"},
  {file:"country-ro.svg",label:"Romania"},
  {file:"country-rs.svg",label:"Serbia"},
  {file:"country-ru.svg",label:"Russia"},
  {file:"country-rw.svg",label:"Rwanda"},
  {file:"country-sa.svg",label:"Saudi Arabia"},
  {file:"country-sb.svg",label:"Solomon Islands"},
  {file:"country-sc.svg",label:"Seychelles"},
  {file:"country-sd.svg",label:"Sudan"},
  {file:"country-se.svg",label:"Sweden"},
  {file:"country-sg.svg",label:"Singapore"},
  {file:"country-sh-ac.svg",label:"Ascension Island"},
  {file:"country-sh-hl.svg",label:"Saint Helena"},
  {file:"country-sh-ta.svg",label:"Tristan da Cunha"},
  {file:"country-sh.svg",label:"St. Helena"},
  {file:"country-si.svg",label:"Slovenia"},
  {file:"country-sj.svg",label:"Svalbard & Jan Mayen"},
  {file:"country-sk.svg",label:"Slovakia"},
  {file:"country-sl.svg",label:"Sierra Leone"},
  {file:"country-sm.svg",label:"San Marino"},
  {file:"country-sn.svg",label:"Senegal"},
  {file:"country-so.svg",label:"Somalia"},
  {file:"country-sr.svg",label:"Suriname"},
  {file:"country-ss.svg",label:"South Sudan"},
  {file:"country-st.svg",label:"São Tomé & Príncipe"},
  {file:"country-sv.svg",label:"El Salvador"},
  {file:"country-sx.svg",label:"Sint Maarten"},
  {file:"country-sy.svg",label:"Syria"},
  {file:"country-sz.svg",label:"Eswatini"},
  {file:"country-tc.svg",label:"Turks & Caicos Islands"},
  {file:"country-td.svg",label:"Chad"},
  {file:"country-tf.svg",label:"French Southern Territories"},
  {file:"country-tg.svg",label:"Togo"},
  {file:"country-th.svg",label:"Thailand"},
  {file:"country-tj.svg",label:"Tajikistan"},
  {file:"country-tk.svg",label:"Tokelau"},
  {file:"country-tl.svg",label:"Timor-Leste"},
  {file:"country-tm.svg",label:"Turkmenistan"},
  {file:"country-tn.svg",label:"Tunisia"},
  {file:"country-to.svg",label:"Tonga"},
  {file:"country-tr.svg",label:"Türkiye"},
  {file:"country-tt.svg",label:"Trinidad & Tobago"},
  {file:"country-tv.svg",label:"Tuvalu"},
  {file:"country-tw.svg",label:"Taiwan"},
  {file:"country-tz.svg",label:"Tanzania"},
  {file:"country-ua.svg",label:"Ukraine"},
  {file:"country-ug.svg",label:"Uganda"},
  {file:"country-um.svg",label:"U.S. Outlying Islands"},
  {file:"country-us.svg",label:"United States"},
  {file:"country-uy.svg",label:"Uruguay"},
  {file:"country-uz.svg",label:"Uzbekistan"},
  {file:"country-va.svg",label:"Vatican City"},
  {file:"country-vc.svg",label:"St. Vincent & Grenadines"},
  {file:"country-ve.svg",label:"Venezuela"},
  {file:"country-vg.svg",label:"British Virgin Islands"},
  {file:"country-vi.svg",label:"U.S. Virgin Islands"},
  {file:"country-vn.svg",label:"Vietnam"},
  {file:"country-vu.svg",label:"Vanuatu"},
  {file:"country-wf.svg",label:"Wallis & Futuna"},
  {file:"country-ws.svg",label:"Samoa"},
  {file:"country-xk.svg",label:"Kosovo"},
  {file:"country-ye.svg",label:"Yemen"},
  {file:"country-yt.svg",label:"Mayotte"},
  {file:"country-za.svg",label:"South Africa"},
  {file:"country-zm.svg",label:"Zambia"},
  {file:"country-zw.svg",label:"Zimbabwe"},
];

// ── Saved-accounts helpers (module-level so Navbar + HomePage can share) ──
const getSavedAccounts = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SAVED_ACCOUNTS_KEY) || '[]');
    return parsed.map(a => typeof a === 'string' ? { username: a, token: null, profile_picture: null } : a);
  } catch { return []; }
};
const upsertSavedAccount = (username, token, profile_picture) => {
  try {
    const accounts = getSavedAccounts().filter(a => a.username !== username);
    accounts.unshift({ username, token: token || null, profile_picture: profile_picture || null });
    localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts.slice(0, 8)));
  } catch {}
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const revision = sharedSession.captureAuth(), token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await getCurrentUser();
      if (!sharedSession.isAuthCurrent(revision) || token !== localStorage.getItem(AUTH_TOKEN_KEY)) return null;
      setUser(res.data);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data));
      return res.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  useEffect(() => {
    let generation = 0, cancelled = false;
    const loadSession = () => {
      const attempt = ++generation, revision = sharedSession.beginAuth();
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token && !sharedSession.mayRestore()) { setUser(null); setLoading(false); return; }
      getCurrentUser().then(res => {
        if (!cancelled && generation === attempt && sharedSession.isAuthCurrent(revision)) { setUser(res.data); localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data)); }
      }).catch(() => {
        if (!cancelled && generation === attempt && sharedSession.isAuthCurrent(revision)) { localStorage.removeItem(AUTH_TOKEN_KEY); localStorage.removeItem(AUTH_USER_KEY); }
      }).finally(() => { if (!cancelled && generation === attempt && sharedSession.isAuthCurrent(revision)) setLoading(false); });
    };
    const onSharedSession = event => { if (event.key === SHARED_SESSION_KEY) { resetSocket(); setUser(null); setLoading(true); loadSession(); } };
    loadSession();
    window.addEventListener('storage', onSharedSession);
    return () => { cancelled = true; window.removeEventListener('storage', onSharedSession); };
  }, []);

  const login = async (credentials) => {
    const revision = sharedSession.beginAuth();
    const res = await apiLogin(credentials);
    if (!sharedSession.isAuthCurrent(revision)) throw new Error('Sign-in was cancelled.');
    if (res.data.mfa_required) return res.data; // MFA step needed
    localStorage.setItem(AUTH_TOKEN_KEY, res.data.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));
    setUser(res.data.user);
    upsertSavedAccount(res.data.user.username, res.data.token, res.data.user.profile_picture);
    sharedSession.publish(res.data.token).catch(() => {});
    return res.data;
  };

  const register = async (userData) => {
    const revision = sharedSession.beginAuth();
    const res = await apiRegister(userData);
    if (!sharedSession.isAuthCurrent(revision)) throw new Error('Sign-in was cancelled.');
    localStorage.setItem(AUTH_TOKEN_KEY, res.data.token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));
    setUser(res.data.user);
    upsertSavedAccount(res.data.user.username, res.data.token, res.data.user.profile_picture);
    sharedSession.publish(res.data.token).catch(() => {});
    return res.data;
  };

  const loginWithToken = async (token) => {
    const revision = sharedSession.beginAuth();
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    try {
      const res = await getCurrentUser({ skipSharedRestore: true });
      if (!sharedSession.isAuthCurrent(revision)) throw new Error('Sign-in was cancelled.');
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data));
      setUser(res.data);
      upsertSavedAccount(res.data.username, token, res.data.profile_picture);
      sharedSession.publish(token).catch(() => {});
      return res.data;
    } catch {
      if (!sharedSession.isAuthCurrent(revision)) throw new Error('Sign-in was cancelled.');
      if (localStorage.getItem(AUTH_TOKEN_KEY) === token) localStorage.removeItem(AUTH_TOKEN_KEY);
      throw new Error('Session expired');
    }
  };

  const logout = () => {
    sharedSession.logout();
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    resetSocket();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, loginWithToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Notifications Component
const Notifications = ({ unreadCount, setUnreadCount, levelUpCount, setLevelUpCount }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;
      const response = await fetch(`${API_URL}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;
      await fetch(`${API_URL}/notifications/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUnreadCount(0);
      setLevelUpCount(0);
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (!isOpen) {
      fetchNotifications();
      markAsRead();
    }
    setIsOpen(!isOpen);
  };

  const getNotificationText = (notif) => {
    switch (notif.type) {
      case 'like': return 'liked your post';
      case 'favorite': return 'favorited your post';
      case 'comment': return 'commented on your post';
      case 'repost': return 'reposted your post';
      case 'follow': return 'followed you';
      case 'level_up': return <span>reached <strong>Level {notif.new_level}</strong> in <strong>{notif.skill_name}</strong>!</span>;
      default: return 'interacted with you';
    }
  };

  return (
    <div className="notifications-container" ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={toggleDropdown}
        style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '5px' }}
        title="Notifications"
      >
        <SocialImage src={`${BASE_PATH}/images/bell.svg`} alt="Notifications" style={{ width: '36px', height: '36px', display: 'block' }} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '0',
            right: '0',
            background: '#e0245e',
            color: 'white',
            borderRadius: '50%',
            padding: '2px 6px',
            fontSize: '10px',
            fontWeight: 'bold',
            border: '2px solid white',
            minWidth: '18px',
            textAlign: 'center'
          }}>
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notifications-dropdown" style={{
          position: 'absolute',
          top: '100%',
          right: '0',
          width: '300px',
          maxHeight: '400px',
          backgroundColor: "var(--ui-surface)",
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          borderRadius: '12px',
          zIndex: 1000,
          overflowY: 'auto',
          marginTop: '10px',
          border: "1px solid var(--ui-border)"
        }}>
          <div style={{ padding: '15px', borderBottom: "1px solid var(--ui-border)", fontWeight: 'bold' }}>
            Notifications
          </div>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: "var(--ui-muted)" }}>Loading...</div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: "var(--ui-muted)" }}>No notifications yet</div>
          ) : (
            notifications.map(notif => {
              let notifLink = `${BASE_PATH}/feed`;
              if (notif.type === 'level_up') {
                notifLink = `${BASE_PATH}/skills`;
              } else if (notif.type === 'follow' && (notif.actor_username || notif.actor_id)) {
                notifLink = `${BASE_PATH}/${notif.actor_username || notif.actor_id}`;
              } else if (notif.post_id) {
                notifLink = `${BASE_PATH}/post/${notif.post_id}`;
              }
              return (
              <Link
                key={notif.id}
                to={notifLink}
                style={{ textDecoration: 'none', color: 'inherit' }}
                onClick={() => setIsOpen(false)}
              >
                <div style={{
                  padding: '12px 15px',
                  borderBottom: "1px solid var(--ui-border)",
                  display: 'flex',
                  gap: '12px',
                  backgroundColor: notif.is_read ? "var(--ui-surface)" : "var(--ui-muted-surface)",
                  transition: 'background-color 0.2s',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '20px' }}>
                    {notif.type === 'like' && '❤️'}
                    {notif.type === 'favorite' && '⭐'}
                    {notif.type === 'comment' && '💬'}
                    {notif.type === 'repost' && '🔄'}
                    {notif.type === 'follow' && '👥'}
                    {notif.type === 'level_up' && '🎊'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                      <strong style={{ color: "var(--ui-text)" }}>@{notif.actor_username}</strong> {getNotificationText(notif)}
                    </div>
                    <div style={{ fontSize: '11px', color: "var(--ui-muted)", marginTop: '2px' }}>
                      {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {new Date(notif.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </Link>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// LevelUpBanner Component
const LevelUpBanner = ({ notification, onClear }) => {
  if (!notification) return null;

  const handleClose = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;
      await fetch(`${API_URL}/notifications/${notification.id}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onClear();
    } catch (err) {
      console.error('Error clearing level-up banner:', err);
    }
  };

  return (
    <div className="levelup-banner">
      <div className="levelup-banner-content">
        {notification.skill_name && SKILL_META[notification.skill_name.toLowerCase()] ? (
          <SocialImage src={SKILL_META[notification.skill_name.toLowerCase()].icon} alt={notification.skill_name} className="levelup-banner-emoji" style={{ width: 32, height: 32 }} />
        ) : (
          <span className="levelup-banner-emoji">🎊</span>
        )}
        <div className="levelup-banner-text">
          <strong>CONGRATULATIONS!</strong>
          <span>You reached <strong>Level {notification.new_level}</strong> in <strong>{notification.skill_name}</strong>!</span>
        </div>
        <button className="levelup-banner-close" onClick={handleClose}>×</button>
      </div>
    </div>
  );
};

// Inline search for second navbar row
const NavbarSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ users: [], posts: [], products: [] });
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults({ users: [], posts: [], products: [] }); setOpen(false); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const base = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
        const [uRes, pRes, prRes] = await Promise.all([
          fetch(`${base}/api/users/search?q=${encodeURIComponent(query)}`),
          fetch(`${base}/api/posts/search?q=${encodeURIComponent(query)}`),
          fetch(`${base}/api/products?search=${encodeURIComponent(query)}&limit=5`),
        ]);
        const users = uRes.ok ? await uRes.json() : [];
        const posts = pRes.ok ? await pRes.json() : [];
        const products = prRes.ok ? await prRes.json() : [];
        setResults({ users: users.slice(0, 5), posts: posts.slice(0, 5), products: (Array.isArray(products) ? products : []).slice(0, 5) });
        setOpen(true);
      } catch {}
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const go = (path) => { setQuery(''); setOpen(false); navigate(path); };

  return (
    <div className="navbar-search-wrap" ref={ref}>
      <input
        className="navbar-search-input"
        placeholder="Search users, posts & products…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => (results.users.length + results.posts.length + results.products.length) > 0 && setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' && query.trim()) {
            setOpen(false);
            navigate(`${BASE_PATH}/search?q=${encodeURIComponent(query.trim())}`);
          }
        }}
      />
      <button className="navbar-search-btn" onClick={() => query.length >= 2 && setOpen(true)} tabIndex={-1}>
        {searching ? '…' : '🔍'}
      </button>
      {open && (results.users.length > 0 || results.posts.length > 0 || results.products.length > 0) && (
        <div className="navbar-search-dropdown">
          {results.users.length > 0 && (
            <>
              <p className="navbar-search-section">People</p>
              {results.users.map(u => (
                <div key={u.id} className="navbar-search-result" onClick={() => go(`${BASE_PATH}/${u.username}`)}>
                  {u.profile_picture
                    ? <SocialImage src={u.profile_picture} alt="" className="navbar-search-avatar" />
                    : <div className="navbar-search-avatar-placeholder" />}
                  <span className="navbar-search-name">@{u.username}</span>
                </div>
              ))}
            </>
          )}
          {results.posts.length > 0 && (
            <>
              <p className="navbar-search-section">Posts</p>
              {results.posts.map(p => (
                <div key={p.id} className="navbar-search-result" onClick={() => go(`${BASE_PATH}/post/${p.id}`)}>
                  <div className="navbar-search-post-icon">📝</div>
                  <div className="navbar-search-post-info">
                    <span className="navbar-search-name">{p.tagline || '(untitled)'}</span>
                    <span className="navbar-search-sub">@{p.username}</span>
                  </div>
                </div>
              ))}
            </>
          )}
          {results.products.length > 0 && (
            <>
              <p className="navbar-search-section">Products</p>
              {results.products.map(pr => {
                const imgs = typeof pr.images === 'string' ? JSON.parse(pr.images) : (pr.images || []);
                return (
                  <div key={pr.id} className="navbar-search-result" onClick={() => go(`${BASE_PATH}/marketplace/product/${pr.id}`)}>
                    {imgs.length > 0
                      ? <SocialImage src={`${API_URL.replace('/api', '')}${imgs[0]}`} alt="" className="navbar-search-avatar" style={{ borderRadius: 6, objectFit: 'cover' }} />
                      : <div className="navbar-search-avatar-placeholder" style={{ borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{pr.type === 'digital' ? '📁' : pr.type === 'service' ? '🛠️' : '📦'}</div>}
                    <div className="navbar-search-post-info">
                      <span className="navbar-search-name">{pr.name}</span>
                      <span className="navbar-search-sub">${(pr.price_cents / 100).toFixed(2)} · @{pr.seller_username}</span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Switch Accounts dropdown (shown in Navbar)
const SwitchAccountsMenu = ({ currentUser, logout, login, loginWithToken }) => {
  const [open, setOpen] = useState(false);
  const [switchUser, setSwitchUser] = useState('');
  const [switchPass, setSwitchPass] = useState('');
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const accounts = getSavedAccounts().filter(a => a.username !== currentUser?.username);

  const handleAccountClick = async (account) => {
    if (!account.token) { setSwitchUser(account.username); setSwitchPass(''); return; }
    setSwitchLoading(true); setSwitchError('');
    try { await loginWithToken(account.token); setOpen(false); }
    catch { setSwitchUser(account.username); setSwitchPass(''); setSwitchError('Session expired — please sign in again.'); }
    finally { setSwitchLoading(false); }
  };

  const handleSwitchLogin = async (e) => {
    e.preventDefault();
    setSwitchLoading(true); setSwitchError('');
    try {
      await login({ username: switchUser, password: switchPass });
      setSwitchUser(''); setSwitchPass(''); setOpen(false);
    } catch (err) {
      setSwitchError(err.response?.data?.error || 'Login failed');
    } finally { setSwitchLoading(false); }
  };

  return (
    <div className="switch-accounts-wrap" ref={ref}>
      <button className="switch-accounts-icon-btn" onClick={() => setOpen(o => !o)} title="Switch Accounts">
        ⇄
      </button>
      {open && (
        <div className="switch-accounts-dropdown">
          {accounts.length > 0 && (
            <>
              <p className="switch-accounts-label">Saved accounts</p>
              {accounts.map((a, i) => (
                <button key={i} className="switch-account-row" onClick={() => handleAccountClick(a)} disabled={switchLoading}>
                  {a.profile_picture
                    ? <SocialImage src={a.profile_picture} alt="" className="switch-account-avatar" />
                    : <div className="switch-account-avatar-placeholder" />}
                  <span className="switch-account-username">@{a.username}</span>
                  {a.token && <span className="switch-account-hint">tap to switch</span>}
                </button>
              ))}
              <div className="switch-accounts-divider" />
            </>
          )}
          <p className="switch-accounts-label">Add account</p>
          {switchError && <p className="switch-accounts-error">{switchError}</p>}
          <form onSubmit={handleSwitchLogin} className="switch-accounts-form">
            <input className="switch-accounts-input" placeholder="Username" value={switchUser} onChange={e => setSwitchUser(e.target.value)} autoComplete="username" />
            <input className="switch-accounts-input" type="password" placeholder="password" value={switchPass} onChange={e => setSwitchPass(e.target.value)} autoComplete="current-password" />
            <button type="submit" className="btn switch-accounts-submit" disabled={switchLoading || !switchUser || !switchPass}>
              {switchLoading ? '…' : 'Sign in'}
            </button>
          </form>
          <div className="switch-accounts-divider" />
          <button className="switch-accounts-logout" onClick={() => { logout(); setOpen(false); }}>Log out @{currentUser?.username}</button>
        </div>
      )}
    </div>
  );
};

// Navbar Component
const Navbar = ({ onLevelUpUpdate }) => {
  const { user, logout, login, loginWithToken } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [levelUpCount, setLevelUpCount] = useState(0);

  const fetchUnreadMessages = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;
      const response = await fetch(`${API_URL}/messages/unread`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadMessages(data.unread);
      }
    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;
      const response = await fetch(`${API_URL}/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const count = Number(data.count) || 0;
        const luCount = Number(data.levelUpCount) || 0;
        console.log('Fetched unread notifications:', { count, luCount });
        setUnreadCount(count);

        if (luCount > levelUpCount) {
          // New level up detected!
          fetchLatestLevelUp();
        }
        setLevelUpCount(luCount);
      }
    } catch (err) {
      console.error('Error fetching unread notifications:', err);
    }
  };

  const fetchLatestLevelUp = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;
      const response = await fetch(`${API_URL}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const unreadLevelUps = data.filter(n => n.type === 'level_up' && !n.is_read);
        if (unreadLevelUps.length > 0) {
          onLevelUpUpdate(unreadLevelUps[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching latest level up:', err);
    }
  };

  // Request browser notification permission on login
  useEffect(() => {
    if (user && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUnreadMessages();
      fetchUnreadNotifications();
      const interval = setInterval(() => {
        fetchUnreadMessages();
        fetchUnreadNotifications();
      }, 10000);

      // Real-time notification via Socket.io
      const socket = getSocket();
      const handleNewNotif = (data) => {
        setUnreadCount((prev) => prev + 1);
        if (data.type === 'level_up') {
          setLevelUpCount((prev) => prev + 1);
          fetchLatestLevelUp();
        }
        // Play bell sound
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(830, ctx.currentTime);
          osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
          gain.gain.setValueAtTime(0.3, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.4);
        } catch (e) {}
        // Browser desktop notification — clickable to relevant page
        if ('Notification' in window && Notification.permission === 'granted') {
          const n = new Notification(data.title || 'Arawra', {
            body: data.body || 'You have a new notification',
            icon: `${window.location.origin}${BASE_PATH}/images/logo-belonging.svg`,
          });
          n.onclick = () => {
            window.focus();
            n.close();
            let path = `${BASE_PATH}/feed`;
            if (data.type === 'level_up') path = `${BASE_PATH}/skills`;
            else if (data.type === 'follow' && data.actorUsername) path = `${BASE_PATH}/${data.actorUsername}`;
            else if (data.postId) path = `${BASE_PATH}/post/${data.postId}`;
            window.location.href = path;
          };
          setTimeout(() => n.close(), 5000);
        }
      };
      socket.on('new-notification', handleNewNotif);

      return () => {
        clearInterval(interval);
        socket.off('new-notification', handleNewNotif);
      };
    }
  }, [user]);

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      {/* Row 1: Logo · · · Bell · Avatar·@username · Switch Accounts */}
      <div className="navbar-top">
        <Link to={`${BASE_PATH}/`} className="navbar-logo-link">
          <HeaderBrand />
        </Link>
        <div style={{ flex: 1 }} />
        <ThemeToggle />
        {user ? (
          <div className="navbar-top-user">
            <Notifications
              unreadCount={unreadCount}
              setUnreadCount={setUnreadCount}
              levelUpCount={levelUpCount}
              setLevelUpCount={setLevelUpCount}
            />
            {user.username === user.email ? (
              <Link to={`${BASE_PATH}/complete-profile`} style={{ color: '#e67e22', fontSize: '0.85rem' }}>Choose a username</Link>
            ) : (
              <Link to={`${BASE_PATH}/${user.username}`} className="navbar-user-link">
                <SocialImage
                  src={user.profile_picture || `${BASE_PATH}/images/profile.svg`}
                  alt=""
                  className="navbar-user-avatar"
                />
                @{user.username}
              </Link>
            )}
            <SwitchAccountsMenu currentUser={user} logout={logout} login={login} loginWithToken={loginWithToken} />
          </div>
        ) : (
          <div className="navbar-top-user">
            <Link to={`${BASE_PATH}/login`}>Login</Link>
            <Link to={`${BASE_PATH}/register`}>Register</Link>
          </div>
        )}
      </div>


      {/* Row 2: Feed · Tribes · Search fills remaining width */}
      <div className="navbar-row2">
        <Link to={`${BASE_PATH}/feed`} className="navbar-row2-link">Feed</Link>
        <Link to={`${BASE_PATH}/tribes`} className="navbar-row2-link">Tribes</Link>
        <NavbarSearch />
      </div>
    </nav>
  );
};

// Home Page
const HomePage = () => {
  const navigate = useNavigate();
  const { user, login, register, loginWithToken } = useAuth();

  // Saved accounts: [{ username, token }]
  const getSavedAccounts = () => {
    try {
      const saved = localStorage.getItem(SAVED_ACCOUNTS_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      // Support old format (plain strings) by migrating them
      return parsed.map(a => typeof a === 'string' ? { username: a, token: null } : a);
    } catch {
      return [];
    }
  };

  const savedAccounts = getSavedAccounts();
  const hasAccounts = savedAccounts.length > 0;

  const [activeTab, setActiveTab] = useState(hasAccounts ? 'login' : 'register');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const saveAccount = (username, token) => {
    try {
      const accounts = getSavedAccounts().filter(a => a.username !== username);
      accounts.unshift({ username, token });
      localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts.slice(0, 5)));
    } catch (err) {
      console.error('Failed to save account:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login({ username, password });
      saveAccount(username, result.token);
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);
      const result = await register({
        username: isEmail ? username : username.toLowerCase().replace(/[^a-z0-9_]/g, ''),
        email: isEmail ? username : null,
        password
      });
      saveAccount(username, result.token);
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      setLoading(false);
    }
  };

  const handleAccountClick = async (account) => {
    if (account.token) {
      setLoading(true);
      setError('');
      try {
        await loginWithToken(account.token);
        window.location.reload();
        return; // Auto-login succeeded
      } catch {
        // Token expired — fall through to password form
      } finally {
        setLoading(false);
      }
    }
    setUsername(account.username);
    setActiveTab('login');
    setPassword('');
  };

  if (user) {
    return (
      <div className="container">
        <div className="home-content">
          <h2>Welcome to</h2>
          <AppBrand className="arawra-home-brand" />
          <p>
            Connect with friends & queer people around the world<br /><br />
            Find Your Tribe!<br />
            Safespace!<br />
            Create Community!<br />
            Unite in Solidarity!
          </p>
          <div className="user-info">
            <h3>Your Profile</h3>
            {user.profile_picture && (
              <SocialImage
                src={user.profile_picture}
                alt="Profile"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  marginBottom: '15px'
                }}
              />
            )}
            <p><strong>Username:</strong> {user.username === user.email || !user.username ? (
              <Link to={`${BASE_PATH}/complete-profile`}>Choose a username</Link>
            ) : (
              `@${user.username}`
            )}</p>
            <p><strong>Email:</strong> {user.email ? user.email : (
              <Link to={`${BASE_PATH}/complete-profile`}>Add an email</Link>
            )}</p>
            <p><strong>Member since:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
            <Link to={`${BASE_PATH}/edit-profile`} className="btn btn-secondary" style={{ marginTop: '15px' }}>
              Edit Profile
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage-split">
      {/* Login/Register Section */}
      <div className="homepage-login">
        <div className="login-tabs">
          <button
            className={`login-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => { setActiveTab('login'); setError(''); }}
          >
            Login
          </button>
          <button
            className={`login-tab ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => { setActiveTab('register'); setError(''); }}
          >
            Register
          </button>
        </div>
        <div className="login-form-container">
          {error && <div className="error-message">{error}</div>}

          {/* Show saved accounts if logging in and accounts exist */}
          {activeTab === 'login' && hasAccounts && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: "var(--ui-muted)", marginBottom: '10px', display: 'block', fontSize: '0.9rem' }}>
                Previously logged in accounts:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {savedAccounts.map((account, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleAccountClick(account)}
                    style={{
                      padding: '10px 15px',
                      background: username === account.username ? "var(--ui-accent)" : "var(--ui-muted-surface)",
                      border: username === account.username ? "2px solid var(--ui-border)" : "1px solid var(--ui-border)",
                      textShadow: username === account.username ? '0 1px 3px rgba(0,0,0,0.4)' : 'none',
                      fontWeight: username === account.username ? 900 : 400,
                      borderRadius: '8px',
                      color: username === account.username ? "var(--ui-on-accent)" : "var(--ui-text)",
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseEnter={(e) => {
                      if (username !== account.username) e.currentTarget.style.background = "var(--ui-muted-surface)";
                    }}
                    onMouseLeave={(e) => {
                      if (username !== account.username) e.currentTarget.style.background = "var(--ui-muted-surface)";
                    }}
                  >
                    <span style={{ flex: 1 }}>{account.username}</span>
                    {account.token && <span style={{ fontSize: '0.75rem', color: username === account.username ? "var(--ui-on-accent)" : "var(--ui-muted)" }}>Tap to sign in</span>}
                  </button>
                ))}
              </div>
              <div style={{ margin: '15px 0', textAlign: 'center', color: "var(--ui-muted)" }}>or</div>
            </div>
          )}

          <form onSubmit={activeTab === 'login' ? handleLogin : handleRegister}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                required
                minLength={activeTab === 'register' ? 6 : undefined}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
              {loading ? (activeTab === 'login' ? 'Signing in...' : 'Registering...') : (activeTab === 'login' ? 'Sign in' : 'Register')}
            </button>
            <Link to={`${BASE_PATH}/tribes/create`} className="create-tribe-link">Create a Tribe!</Link>
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="homepage-left">
        <AppBrand className="arawra-home-brand" />
        {/* Safespace Section */}
        <div className="homepage-section">
          {/* Coming Soon Section */}
          <div className="homepage-section coming-soon-section">
            <SocialImage src={comingSoonImg} alt="Coming Soon" className="coming-soon-header" />
            <div className="character-grid">
              <SocialImage src={nonbianaryImg} alt="Nonbinary character" className="character-img" />
              <SocialImage src={transgirlImg} alt="Trans girl character" className="character-img" />
              <SocialImage src={lesbianImg} alt="Lesbian character" className="character-img" />
            </div>
          </div>

          <h2 className="section-title">Safespace</h2>
          <p className="section-desc">Connect with friends & queer people around the world</p>
        </div>

        {/* Find Your Tribe */}
        <div className="homepage-section">
          <h3 className="section-subtitle">Find Your Tribe!</h3>
        </div>

        {/* Create Community */}
        <div className="homepage-section">
          <h3 className="section-subtitle">Create Community!</h3>
        </div>

        {/* Unite in Solidarity */}
        <div className="homepage-section">
          <h3 className="section-subtitle">Unite in Solidarity!</h3>
        </div>

        {/* Marketplace Section */}
        <div className="homepage-section marketplace-section">
          <h2 className="section-title">Marketplace</h2>
          <p className="section-desc">A closed queer market to support LGBTQIA+ Communities, Businesses, & Creatives.</p>
          <div className="marketplace-search">
            <input type="text" placeholder="Search" className="search-input" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login({ username, password, mfaCode: mfaCode || undefined });
      if (result && result.mfa_required) {
        setMfaRequired(true);
        setLoading(false);
        return;
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="card">
        <h2>Login</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username / Email</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {mfaRequired && (
            <div className="form-group">
              <label>Two-Factor Code</label>
              <input
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="Enter 6-digit code"
                style={{ textAlign: 'center', letterSpacing: 4, fontSize: 18 }}
                autoFocus
              />
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Logging in...' : mfaRequired ? 'Verify & Login' : 'Login'}
          </button>
        </form>
        <div className="auth-links">
          <p>Don't have an account? <Link to={`${BASE_PATH}/register`}>Register</Link></p>
        </div>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);
  const { register, user } = useAuth();

  if (user && !justRegistered) {
    return <Navigate to="/" replace />;
  }

  if (justRegistered) {
    return <Navigate to={`${BASE_PATH}/complete-profile`} replace />;
  }

  const isEmail = (str) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!identifier.trim()) {
      setError('Please enter an email or username');
      return;
    }

    setLoading(true);

    try {
      const isEmailInput = isEmail(identifier);
      await register({
        username: isEmailInput ? identifier : identifier.toLowerCase().replace(/[^a-z0-9_]/g, ''),
        email: isEmailInput ? identifier : null,
        password,
        tosAccepted
      });
      setJustRegistered(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="card">
        <h2>Register</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email or Username</label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="email@example.com or username"
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                style={{ paddingRight: '50px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px',
                  color: "var(--ui-muted)",
                  fontSize: '14px'
                }}
              >
                {showPassword ? '🙈 Hide' : '👁️ Show'}
              </button>
            </div>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '16px' }}>
            <input type="checkbox" id="tos-checkbox" checked={tosAccepted} onChange={(e) => setTosAccepted(e.target.checked)} style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }} />
            <label htmlFor="tos-checkbox" style={{ cursor: 'pointer', fontSize: '13px', lineHeight: '1.4' }}>
              I agree to the <Link to={`${BASE_PATH}/docs/legal/terms-of-service`} target="_blank">Terms of Service</Link> and <Link to={`${BASE_PATH}/docs/legal/privacy-policy`} target="_blank">Privacy Policy</Link>
            </label>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || !tosAccepted} style={{ width: '100%' }}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        <div className="auth-links">
          <p>Already have an account? <Link to={`${BASE_PATH}/login`}>Login</Link></p>
        </div>
      </div>
    </div>
  );
};

// Complete Profile Page (shown after registration)
const CompleteProfilePage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [ageVerified, setAgeVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState('form'); // 'form' or 'confirmation'
  const [savedHandle, setSavedHandle] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Store the email on mount (before user object might change)
  useEffect(() => {
    if (user) {
      setUserEmail(user.email || user.username);
    }
  }, []);

  if (!user) {
    return <Navigate to={`${BASE_PATH}/login`} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Only require handle if user registered with email (username contains @)
    const needsHandle = user.username && user.username.includes('@');
    if (needsHandle && !handle.trim()) {
      setError('Please enter a handle');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName,
          lastName,
          handle: handle || undefined,
          email: email || undefined,
          ageVerified: ageVerified || undefined
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile');
      }

      // Refresh user data from server
      await refreshUser();

      // Use the handle they entered, or their username if they registered with a username (not email)
      const displayHandle = handle || (user.username && !user.username.includes('@') ? user.username : '');
      setSavedHandle(displayHandle);
      setStep('confirmation');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // If skipping, go straight to home
    window.location.href = BASE_PATH === '/' ? '/' : BASE_PATH;
  };

  // Confirmation screen after profile is saved
  if (step === 'confirmation') {
    return (
      <div className="auth-container">
        <div className="card">
          <h2>Account Created! 🎉</h2>
          <p style={{ textAlign: 'center', marginBottom: '25px', color: "var(--ui-muted)" }}>
            Your account is ready to use
          </p>
          <div style={{
            background: "var(--ui-muted-surface)",
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontSize: '12px', color: "var(--ui-muted)", textTransform: 'uppercase' }}>Username</label>
              <p style={{ margin: '5px 0 0', fontSize: '18px', fontWeight: '500' }}>@{savedHandle}</p>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: "var(--ui-muted)", textTransform: 'uppercase' }}>Email</label>
              <p style={{ margin: '5px 0 0', fontSize: '18px', fontWeight: '500' }}>{email || userEmail || 'Not set'}</p>
            </div>
          </div>
          <p style={{ textAlign: 'center', fontSize: '14px', color: "var(--ui-muted)", marginBottom: '20px' }}>
            {(email || userEmail) ? 'You can sign in with either your username or email' : 'You can sign in with your username'}
          </p>
          <Link to="/" className="btn btn-primary" style={{ width: '100%', display: 'block', textAlign: 'center' }}>
            Get Started
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="card">
        <h2>Welcome! 🎉</h2>
        <p style={{ textAlign: 'center', marginBottom: '20px', color: "var(--ui-muted)" }}>
          Tell us a bit about yourself
        </p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Your last name"
              />
            </div>
          </div>
          {user.username && user.username.includes('@') && (
            <div className="form-group">
              <label>Custom Handle</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: "var(--ui-muted)",
                  fontSize: '16px'
                }}>@</span>
                <input
                  type="text"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
                  placeholder="yourhandle"
                  style={{ paddingLeft: '28px' }}
                />
              </div>
            </div>
          )}
          {!user.email && (
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16, fontSize: 14, color: "var(--ui-text)" }}>
            <input type="checkbox" checked={ageVerified} onChange={(e) => setAgeVerified(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
            I confirm that I am 13 years of age or older
          </label>
          <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </form>
        <button
          onClick={handleSkip}
          style={{
            width: '100%',
            marginTop: '10px',
            padding: '12px 20px',
            background: 'transparent',
            border: 'none',
            color: "var(--ui-muted)",
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

// Edit Profile Page
const EditProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [bannerImage, setBannerImage] = useState('');
  const [bio, setBio] = useState('');
  const [birthday, setBirthday] = useState('');
  const [genderIdentity, setGenderIdentity] = useState('');
  const [sexualOrientation, setSexualOrientation] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [discordStatus, setDiscordStatus] = useState({ connected: false, discord_username: null, discord_avatar: null });
  const [discordLoading, setDiscordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setUsername(user.username && !user.username.includes('@') ? user.username : '');
      setEmail(user.email || '');
      setProfilePicture(user.profile_picture || '');
      setBannerImage(user.banner_image || '');
      setBio(user.bio || '');
      setBirthday(user.birthday ? user.birthday.split('T')[0] : '');
      setGenderIdentity(user.gender_identity || '');
      setSexualOrientation(user.sexual_orientation || '');

      // Check Discord connection status
      fetchDiscordStatus();
    }
  }, [user]);

  const fetchDiscordStatus = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/auth/discord/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDiscordStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch Discord status:', err);
    }
  };

  const handleDiscordConnect = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) {
        alert('Please log in first');
        return;
      }

      // Make a request to get the Discord auth URL
      const response = await fetch(`${API_URL}/auth/discord`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        alert(data.error);
      }
    } catch (error) {
      console.error('Discord connect error:', error);
      alert('Failed to connect Discord');
    }
  };

  const handleDiscordDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Discord account?')) return;

    setDiscordLoading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/auth/discord`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setDiscordStatus({ connected: false, discord_username: null, discord_avatar: null });
        setMessage('Discord account disconnected');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to disconnect Discord');
      }
    } catch (err) {
      setError('Failed to disconnect Discord');
    } finally {
      setDiscordLoading(false);
    }
  };

  if (!user) {
    return <Navigate to={`${BASE_PATH}/login`} replace />;
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          handle: username || undefined,
          email: email || undefined,
          profilePicture: profilePicture || undefined,
          bannerImage: bannerImage || undefined,
          bio: bio,
          birthday: birthday || undefined,
          genderIdentity: genderIdentity || undefined,
          sexualOrientation: sexualOrientation || undefined
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile');
      }

      await refreshUser();
      setMessage('Profile updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/auth/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change password');
      }

      setMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      setError('Image must be less than 100MB');
      return;
    }

    // Upload to server instead of base64
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      const imageUrl = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${data.url}`;
      setProfilePicture(imageUrl);
    } catch (err) {
      setError('Failed to upload image: ' + err.message);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) throw new Error('Failed to upload banner');
      const data = await response.json();
      const imageUrl = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${data.url}`;
      setBannerImage(imageUrl);
    } catch (err) {
      setError('Failed to upload banner: ' + err.message);
    }
  };

  const tabStyle = (isActive, idx = 0) => ({
    padding: '10px 20px',
    border: isActive ? "2px solid var(--ui-border)" : 'none',
    background: isActive ? "var(--ui-accent)" : 'transparent',
    color: isActive ? "var(--ui-on-accent)" : "var(--ui-muted)",
    cursor: 'pointer',
    borderRadius: '5px 5px 0 0',
    fontWeight: isActive ? 900 : '400',
    textShadow: isActive ? '0 1px 3px rgba(0,0,0,0.4)' : 'none'
  });

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h2>Edit Profile</h2>

        <div style={{ display: 'flex', gap: '5px', marginBottom: '20px', borderBottom: "1px solid var(--ui-border)" }}>
          <button style={tabStyle(activeTab === 'profile', 0)} onClick={() => setActiveTab('profile')}>
            Profile
          </button>
          <button style={tabStyle(activeTab === 'password', 1)} onClick={() => setActiveTab('password')}>
            Password
          </button>
          <button style={tabStyle(activeTab === 'discord', 2)} onClick={() => setActiveTab('discord')}>
            Discord
          </button>
        </div>

        {message && <div style={{ padding: '10px', background: '#d4edda', color: '#155724', borderRadius: '5px', marginBottom: '15px' }}>{message}</div>}
        {error && <div className="error-message">{error}</div>}

        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} noValidate>
            {/* Banner Image */}
            <div className="form-group">
              <label>Banner Image <span style={{ color: "var(--ui-muted)", fontWeight: 400, fontSize: '0.85rem' }}>(1920×1080 recommended)</span></label>
              <div className="edit-profile-banner-wrap">
                {bannerImage
                  ? <SocialImage src={bannerImage} alt="Banner" className="edit-profile-banner-preview" />
                  : <div className="edit-profile-banner-placeholder">No banner</div>
                }
                <label className="edit-profile-banner-btn">
                  {bannerImage ? 'Change Banner' : 'Upload Banner'}
                  <input type="file" accept="image/*" onChange={handleBannerUpload} style={{ display: 'none' }} />
                </label>
                {bannerImage && (
                  <button type="button" className="edit-profile-banner-remove" onClick={() => setBannerImage('')}>Remove</button>
                )}
              </div>
            </div>

            {/* Profile Picture */}
            <div className="form-group" style={{ textAlign: 'center' }}>
              <label>Profile Picture</label>
              <div style={{ marginTop: '10px' }}>
                {profilePicture ? (
                  <SocialImage
                    src={profilePicture}
                    alt="Profile"
                    style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: "3px solid var(--ui-border)"
                    }}
                  />
                ) : (
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'linear-gradient(90deg, #f15722, #f78e1e, #fed206, #8ec74e, #49b863, #3fbced, #735ca8, #995aa4, #c263a7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    fontSize: '48px',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {(firstName || username || 'U')[0].toUpperCase()}
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ marginTop: '10px' }}
                id="profile-picture-input"
              />
              {profilePicture && (
                <button
                  type="button"
                  onClick={() => setProfilePicture('')}
                  style={{ marginTop: '5px', background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                >
                  Remove Photo
                </button>
              )}
            </div>

            {/* Name Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Your first name"
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Your last name"
                />
              </div>
            </div>

            {/* Username */}
            <div className="form-group">
              <label>Username</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: "var(--ui-muted)",
                  fontSize: '16px'
                }}>@</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
                  placeholder="yourhandle"
                  style={{ paddingLeft: '28px' }}
                />
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label>Email</label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>

            {/* Bio */}
            <div className="form-group">
              <label>Bio <span style={{ color: "var(--ui-muted)", fontWeight: 400, fontSize: '0.85rem' }}>({bio.length}/4000)</span></label>
              <textarea
                value={bio}
                onChange={(e) => { if (e.target.value.length <= 4000) setBio(e.target.value); }}
                placeholder="Tell people about yourself..."
                rows={4}
                style={{ resize: 'vertical', minHeight: '80px' }}
              />
            </div>

            {/* Identity Fields */}
            <div style={{ marginTop: '10px' }}>
              <h4 style={{ margin: '0 0 10px', color: "var(--ui-text)" }}>Identity</h4>
              <div className="form-group">
                <label>Birthday</label>
                <input
                  type="date"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Gender Identity</label>
                <input
                  type="text"
                  value={genderIdentity}
                  onChange={(e) => setGenderIdentity(e.target.value)}
                  placeholder="e.g. Non-binary, Trans woman, etc."
                />
              </div>
              <div className="form-group">
                <label>Sexual Orientation</label>
                <input
                  type="text"
                  value={sexualOrientation}
                  onChange={(e) => setSexualOrientation(e.target.value)}
                  placeholder="e.g. Lesbian, Bisexual, etc."
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        )}

        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="password"
                required
              />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="password"
                required
              />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="password"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        )}

        {activeTab === 'discord' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: '20px' }}>
              <svg style={{ width: '64px', height: '64px', marginBottom: '15px' }} viewBox="0 0 127.14 96.36" fill={discordStatus.connected ? '#5865F2' : '#888'}>
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
              </svg>
              <h3 style={{ marginBottom: '10px', color: discordStatus.connected ? "var(--ui-accent)" : 'inherit' }}>
                {discordStatus.connected ? 'Discord Connected' : 'Connect Discord'}
              </h3>
            </div>

            {discordStatus.connected ? (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '20px',
                  padding: '15px',
                  background: "var(--ui-muted-surface)",
                  borderRadius: '10px'
                }}>
                  {discordStatus.discord_avatar ? (
                    <SocialImage
                      src={discordStatus.discord_avatar.startsWith('http')
                        ? discordStatus.discord_avatar
                        : `https://cdn.discordapp.com/avatars/${discordStatus.discord_id}/${discordStatus.discord_avatar}.png?size=64`}
                      alt="Discord Avatar"
                      style={{ width: '48px', height: '48px', borderRadius: '50%' }}
                    />
                  ) : (
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: "var(--ui-accent)",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: "var(--ui-on-accent)",
                      fontWeight: 'bold'
                    }}>
                      {(discordStatus.discord_username || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 'bold', color: "var(--ui-text)" }}>{discordStatus.discord_username}</div>
                    <div style={{ fontSize: '14px', color: "var(--ui-muted)" }}>Connected</div>
                  </div>
                </div>
                <p style={{ color: "var(--ui-muted)", marginBottom: '20px', fontSize: '14px' }}>
                  Your posts will sync to the Arawra Discord server, and your Discord messages (with the "connected" role) will appear on the website.
                </p>
                <button
                  onClick={handleDiscordDisconnect}
                  disabled={discordLoading}
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {discordLoading ? 'Disconnecting...' : 'Disconnect Discord'}
                </button>
              </div>
            ) : (
              <div>
                <p style={{ color: "var(--ui-muted)", marginBottom: '20px', fontSize: '14px' }}>
                  Connect your Discord account to sync your posts with the Arawra Discord server. Your posts will appear in Discord, and your Discord messages will appear on the website!
                </p>
                <button
                  onClick={handleDiscordConnect}
                  style={{
                    background: "var(--ui-accent)",
                    color: "var(--ui-on-accent)",
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 127.14 96.36" fill="white">
                    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
                  </svg>
                  Connect Discord
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Post Editor Component (WordPress-like with visual and code editor)
const WORLD_LOCATIONS = [
  // Countries
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda',
  'Argentina','Armenia','Australia','Austria','Azerbaijan',
  'Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium',
  'Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana',
  'Brazil','Brunei','Bulgaria','Burkina Faso','Burundi',
  'Cambodia','Cameroon','Canada','Cape Verde','Central African Republic',
  'Chad','Chile','China','Colombia','Comoros','Costa Rica',
  "Côte d'Ivoire",'Croatia','Cuba','Cyprus','Czech Republic',
  'Denmark','Djibouti','Dominica','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia',
  'Fiji','Finland','France',
  'Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada',
  'Guatemala','Guinea','Guinea-Bissau','Guyana',
  'Haiti','Honduras','Hungary',
  'Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan',
  'Kazakhstan','Kenya','Kiribati','Kosovo','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg',
  'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands',
  'Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar',
  'Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway',
  'Oman',
  'Pakistan','Palau','Palestine','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal',
  'Qatar',
  'Romania','Russia','Rwanda',
  'Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines',
  'Samoa','San Marino','Saudi Arabia','Senegal','Serbia','Seychelles',
  'Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands',
  'Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria',
  'Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan',
  'Vanuatu','Vatican City','Venezuela','Vietnam',
  'Yemen','Zambia','Zimbabwe',
  // North America — Cities
  'New York, USA','Los Angeles, USA','Chicago, USA','Houston, USA','Phoenix, USA',
  'Philadelphia, USA','San Antonio, USA','San Diego, USA','Dallas, USA',
  'San Jose, USA','Austin, USA','Seattle, USA','Denver, USA','Boston, USA',
  'Portland, USA','Miami, USA','Atlanta, USA','Minneapolis, USA','Las Vegas, USA',
  'San Francisco, USA','Nashville, USA','Detroit, USA','Louisville, USA',
  'Baltimore, USA','Milwaukee, USA','Sacramento, USA','Pittsburgh, USA',
  'Cincinnati, USA','Cleveland, USA','Kansas City, USA','Tampa, USA',
  'Orlando, USA','St. Louis, USA','New Orleans, USA','Indianapolis, USA',
  'Columbus, USA','Charlotte, USA','Raleigh, USA','Omaha, USA','Honolulu, USA',
  'Anchorage, USA','Albuquerque, USA','Tucson, USA','Fresno, USA',
  'Salt Lake City, USA','Richmond, USA','Baton Rouge, USA','Memphis, USA',
  'Toronto, Canada','Montreal, Canada','Vancouver, Canada','Calgary, Canada',
  'Ottawa, Canada','Edmonton, Canada','Quebec City, Canada','Winnipeg, Canada','Halifax, Canada',
  'Mexico City, Mexico','Guadalajara, Mexico','Monterrey, Mexico','Tijuana, Mexico',
  'Havana, Cuba','San José, Costa Rica','Guatemala City, Guatemala',
  'Panama City, Panama','Managua, Nicaragua','Tegucigalpa, Honduras','San Salvador, El Salvador',
  // South America — Cities
  'São Paulo, Brazil','Rio de Janeiro, Brazil','Brasília, Brazil','Salvador, Brazil',
  'Fortaleza, Brazil','Manaus, Brazil','Curitiba, Brazil','Recife, Brazil',
  'Buenos Aires, Argentina','Córdoba, Argentina','Rosario, Argentina',
  'Bogotá, Colombia','Medellín, Colombia','Cali, Colombia',
  'Lima, Peru','Santiago, Chile','Caracas, Venezuela','Quito, Ecuador',
  'Guayaquil, Ecuador','La Paz, Bolivia','Asunción, Paraguay','Montevideo, Uruguay',
  // Europe — Cities
  'London, UK','Manchester, UK','Birmingham, UK','Glasgow, UK','Liverpool, UK',
  'Leeds, UK','Bristol, UK','Edinburgh, UK','Sheffield, UK','Cardiff, UK','Belfast, UK',
  'Paris, France','Lyon, France','Marseille, France','Toulouse, France','Bordeaux, France','Nice, France','Strasbourg, France','Nantes, France',
  'Berlin, Germany','Hamburg, Germany','Munich, Germany','Cologne, Germany',
  'Frankfurt, Germany','Stuttgart, Germany','Düsseldorf, Germany','Leipzig, Germany',
  'Madrid, Spain','Barcelona, Spain','Valencia, Spain','Seville, Spain','Zaragoza, Spain','Bilbao, Spain','Málaga, Spain',
  'Rome, Italy','Milan, Italy','Naples, Italy','Turin, Italy','Florence, Italy','Venice, Italy','Bologna, Italy',
  'Amsterdam, Netherlands','Rotterdam, Netherlands','The Hague, Netherlands',
  'Brussels, Belgium','Antwerp, Belgium','Ghent, Belgium',
  'Vienna, Austria','Graz, Austria',
  'Zurich, Switzerland','Geneva, Switzerland','Basel, Switzerland','Bern, Switzerland',
  'Stockholm, Sweden','Gothenburg, Sweden','Malmö, Sweden',
  'Oslo, Norway','Bergen, Norway',
  'Copenhagen, Denmark','Aarhus, Denmark',
  'Helsinki, Finland','Reykjavik, Iceland',
  'Warsaw, Poland','Kraków, Poland','Gdańsk, Poland','Wrocław, Poland',
  'Prague, Czech Republic','Brno, Czech Republic',
  'Budapest, Hungary','Bucharest, Romania','Sofia, Bulgaria',
  'Athens, Greece','Thessaloniki, Greece',
  'Lisbon, Portugal','Porto, Portugal',
  'Dublin, Ireland','Cork, Ireland',
  'Belgrade, Serbia','Zagreb, Croatia','Split, Croatia',
  'Sarajevo, Bosnia and Herzegovina','Skopje, North Macedonia','Tirana, Albania','Podgorica, Montenegro',
  'Riga, Latvia','Vilnius, Lithuania','Tallinn, Estonia',
  'Chisinau, Moldova','Kyiv, Ukraine','Kharkiv, Ukraine','Odesa, Ukraine',
  'Moscow, Russia','St. Petersburg, Russia','Novosibirsk, Russia','Yekaterinburg, Russia',
  'Istanbul, Turkey','Ankara, Turkey','Izmir, Turkey',
  'Ljubljana, Slovenia','Luxembourg City, Luxembourg','Valletta, Malta','Nicosia, Cyprus',
  // Middle East — Cities
  'Dubai, UAE','Abu Dhabi, UAE','Doha, Qatar','Riyadh, Saudi Arabia','Jeddah, Saudi Arabia',
  'Kuwait City, Kuwait','Manama, Bahrain','Muscat, Oman',
  'Beirut, Lebanon','Tel Aviv, Israel','Jerusalem, Israel','Haifa, Israel',
  'Amman, Jordan','Damascus, Syria','Baghdad, Iraq','Tehran, Iran',
  'Kabul, Afghanistan',"Sana'a, Yemen",
  // Asia — Cities
  'Tokyo, Japan','Osaka, Japan','Kyoto, Japan','Sapporo, Japan','Fukuoka, Japan','Yokohama, Japan','Nagoya, Japan','Kobe, Japan',
  'Beijing, China','Shanghai, China','Guangzhou, China','Shenzhen, China',
  'Chengdu, China','Chongqing, China',"Xi'an, China",'Hangzhou, China','Wuhan, China','Nanjing, China','Tianjin, China',
  'Hong Kong','Taipei, Taiwan','Macau',
  'Seoul, South Korea','Busan, South Korea','Incheon, South Korea','Daegu, South Korea',
  'Pyongyang, North Korea',
  'Singapore',
  'Kuala Lumpur, Malaysia','George Town, Malaysia','Johor Bahru, Malaysia',
  'Bangkok, Thailand','Chiang Mai, Thailand',
  'Jakarta, Indonesia','Surabaya, Indonesia','Bandung, Indonesia','Bali, Indonesia',
  'Manila, Philippines','Cebu, Philippines','Quezon City, Philippines',
  'Hanoi, Vietnam','Ho Chi Minh City, Vietnam','Da Nang, Vietnam',
  'Phnom Penh, Cambodia','Siem Reap, Cambodia',
  'Yangon, Myanmar','Naypyidaw, Myanmar','Vientiane, Laos',
  'Colombo, Sri Lanka','Kathmandu, Nepal','Dhaka, Bangladesh','Chittagong, Bangladesh',
  'Karachi, Pakistan','Lahore, Pakistan','Islamabad, Pakistan','Rawalpindi, Pakistan',
  'Mumbai, India','Delhi, India','Bangalore, India','Kolkata, India',
  'Chennai, India','Hyderabad, India','Ahmedabad, India','Pune, India',
  'Jaipur, India','Surat, India','Lucknow, India','Kochi, India',
  'Ulaanbaatar, Mongolia','Almaty, Kazakhstan','Nur-Sultan, Kazakhstan',
  'Tashkent, Uzbekistan','Bishkek, Kyrgyzstan','Dushanbe, Tajikistan','Ashgabat, Turkmenistan',
  'Baku, Azerbaijan','Tbilisi, Georgia','Yerevan, Armenia',
  // Africa — Cities
  'Cairo, Egypt','Alexandria, Egypt',
  'Lagos, Nigeria','Abuja, Nigeria','Kano, Nigeria',
  'Nairobi, Kenya','Mombasa, Kenya',
  'Johannesburg, South Africa','Cape Town, South Africa','Durban, South Africa','Pretoria, South Africa',
  'Addis Ababa, Ethiopia','Dar es Salaam, Tanzania',
  'Casablanca, Morocco','Rabat, Morocco','Tunis, Tunisia','Algiers, Algeria','Oran, Algeria',
  'Accra, Ghana','Kumasi, Ghana',
  "Abidjan, Côte d'Ivoire",'Kampala, Uganda','Lusaka, Zambia',
  'Harare, Zimbabwe','Bulawayo, Zimbabwe','Dakar, Senegal',
  'Kinshasa, DR Congo','Luanda, Angola','Khartoum, Sudan','Mogadishu, Somalia',
  'Antananarivo, Madagascar','Maputo, Mozambique',
  'Douala, Cameroon','Yaoundé, Cameroon','Bamako, Mali','Ouagadougou, Burkina Faso',
  'Conakry, Guinea','Freetown, Sierra Leone','Monrovia, Liberia',
  'Lomé, Togo','Cotonou, Benin','Niamey, Niger','Ndjamena, Chad',
  'Brazzaville, Congo','Libreville, Gabon','Malabo, Equatorial Guinea',
  'Banjul, Gambia','Bissau, Guinea-Bissau','Praia, Cape Verde',
  'Djibouti City, Djibouti','Asmara, Eritrea','Juba, South Sudan',
  'Kigali, Rwanda','Bujumbura, Burundi','Lilongwe, Malawi','Windhoek, Namibia',
  'Gaborone, Botswana','Mbabane, Eswatini','Maseru, Lesotho',
  // Oceania — Cities
  'Sydney, Australia','Melbourne, Australia','Brisbane, Australia',
  'Perth, Australia','Adelaide, Australia','Canberra, Australia',
  'Gold Coast, Australia','Darwin, Australia','Hobart, Australia',
  'Auckland, New Zealand','Wellington, New Zealand','Christchurch, New Zealand',
  'Suva, Fiji','Port Moresby, Papua New Guinea','Honiara, Solomon Islands',
  'Port Vila, Vanuatu',"Nuku'alofa, Tonga",'Apia, Samoa',
  'Majuro, Marshall Islands','Palikir, Micronesia','Koror, Palau',
  'South Tarawa, Kiribati','Yaren, Nauru',
];

const COMPOSER_VIBES = [
  { emoji: '😊', label: 'happy' }, { emoji: '😢', label: 'sad' },
  { emoji: '😡', label: 'angry' }, { emoji: '😍', label: 'loving' },
  { emoji: '😂', label: 'laughing' }, { emoji: '🤔', label: 'thinking' },
  { emoji: '😴', label: 'tired' }, { emoji: '🥳', label: 'celebrating' },
  { emoji: '💪', label: 'motivated' }, { emoji: '😌', label: 'peaceful' },
  { emoji: '🔥', label: 'fired up' }, { emoji: '🌸', label: 'grateful' },
  { emoji: '😰', label: 'anxious' }, { emoji: '🤗', label: 'excited' },
];
const PostEditor = ({ onPostCreated, editPost, onCancel, initialTribe }) => {
  const { user: currentUser } = useAuth();
  const [draft, setDraft] = useState(() => {
    if (editPost) return (editPost.tagline || '') + (editPost.content ? '\n' + editPost.content : '');
    return '';
  });
  const [customCss] = useState(editPost?.custom_css || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageRows, setImageRows] = useState([]);
  const [dragSrc, setDragSrc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const pendingUploadRowId = React.useRef(null);
  const modalFileInputRef = React.useRef(null);
  const [selectedTribes, setSelectedTribes] = useState([]);
  const [allTribes, setAllTribes] = useState([]);
  const [tribeSearch, setTribeSearch] = useState('');
  const [showTribesDropdown, setShowTribesDropdown] = useState(false);
  const [location, setLocation] = useState('');
  const [locationOpen, setLocationOpen] = useState(false);
  const [vibe, setVibe] = useState('');
  const [privacy, setPrivacy] = useState('public');
  const [showStickers, setShowStickers] = useState(false);
  const [detectedLinks, setDetectedLinks] = useState([]);
  const [productSearches, setProductSearches] = useState({});
  const [productResults, setProductResults] = useState({});
  const [customStickersList, setCustomStickersList] = useState([]);
  const [contentWarning, setContentWarning] = useState(editPost?.content_warning || '');
  const [showCwInput, setShowCwInput] = useState(!!editPost?.content_warning);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const fileInputRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const linkDebounceRef = React.useRef(null);

  // Read body from contenteditable, converting sticker <img data-shortcode> back to shortcode text
  const readBodyFromEditor = () => {
    const el = bodyRef.current;
    if (!el) return draft.split('\n').slice(1).join('\n');
    let text = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeName === 'IMG' && node.dataset.shortcode) {
        text += node.dataset.shortcode;
      } else if (node.nodeName === 'BR') {
        text += '\n';
      } else {
        const isBlock = node.nodeName === 'DIV' || node.nodeName === 'P';
        if (isBlock && text.length > 0 && !text.endsWith('\n')) text += '\n';
        for (const child of node.childNodes) walk(child);
      }
    };
    for (const child of el.childNodes) walk(child);
    return text;
  };

  // Split draft: line 1 = tagline, rest = body
  const getDraftParts = () => {
    const title = draft.split('\n')[0];
    const body = bodyRef.current ? readBodyFromEditor() : draft.split('\n').slice(1).join('\n');
    return { tagline: title.trim(), body: body.trim() };
  };

  const isVideoUrl = (url) => /\.(mp4|webm|mov|avi|mkv)$/i.test(url);
  const genRowId = () => Math.random().toString(36).slice(2);

  // --- Image row management ---
  const setRowLayout = (rowId, layout) =>
    setImageRows(prev => prev.map(r => r.id === rowId ? { ...r, layout, urls: (r.urls || []).slice(0, layout), products: (r.products || []).slice(0, layout) } : r));

  const moveRow = (rowId, dir) =>
    setImageRows(prev => {
      const i = prev.findIndex(r => r.id === rowId);
      if (i + dir < 0 || i + dir >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[i + dir]] = [next[i + dir], next[i]];
      return next;
    });

  const removeRow = (rowId) =>
    setImageRows(prev => prev.filter(r => r.id !== rowId));

  const removeRowImage = (rowId, imgIdx) =>
    setImageRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, urls: r.urls.filter((_, i) => i !== imgIdx) } : r
    ).filter(r => r.type === 'text' || r.type === 'product' || r.urls.length > 0));

  const addTextRow = () =>
    setImageRows(prev => [...prev, { id: genRowId(), type: 'text', content: '' }]);

  const updateRowText = (rowId, content) =>
    setImageRows(prev => prev.map(r => r.id === rowId ? { ...r, content } : r));

  const addProductRow = () =>
    setImageRows(prev => [...prev, { id: genRowId(), type: 'product', products: [], layout: 1 }]);

  const productSearchTimer = React.useRef(null);
  const searchProductsForRow = (rowId, query) => {
    setProductSearches(prev => ({ ...prev, [rowId]: query }));
    if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
    if (!query.trim()) { setProductResults(prev => ({ ...prev, [rowId]: [] })); return; }
    productSearchTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${API_URL}/products?search=${encodeURIComponent(query.trim())}&limit=10`, {
          headers: { Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` }
        });
        const data = await resp.json();
        setProductResults(prev => ({ ...prev, [rowId]: Array.isArray(data) ? data : (data.data || []) }));
      } catch (err) { console.error('Product search error:', err); setProductResults(prev => ({ ...prev, [rowId]: [] })); }
    }, 300);
  };

  const addProductToRow = (rowId, product) => {
    setImageRows(prev => prev.map(r => {
      if (r.id !== rowId || r.type !== 'product') return r;
      if (r.products.length >= (r.layout || 1)) return r;
      if (r.products.some(p => p.id === product.id)) return r;
      const img = product.images?.[0] || '';
      const imgUrl = img.startsWith('http') ? img : img ? `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}/${img}` : '';
      return { ...r, products: [...r.products, { id: product.id, name: product.name, price_cents: product.price_cents, image: imgUrl, external_url: product.external_url || '', shop_name: product.shop_name || '' }] };
    }));
    setProductSearches(prev => ({ ...prev, [rowId]: '' }));
    setProductResults(prev => ({ ...prev, [rowId]: [] }));
  };

  const removeProductFromRow = (rowId, idx) =>
    setImageRows(prev => prev.map(r => r.id === rowId ? { ...r, products: r.products.filter((_, i) => i !== idx) } : r));

  const handleImgDragStart = (e, rowId, imgIdx) => {
    setDragSrc({ rowId, imgIdx });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImgDrop = (e, targetRowId, targetImgIdx) => {
    e.preventDefault();
    if (!dragSrc) return;
    setImageRows(prev => {
      const next = prev.map(r => ({ ...r, urls: [...(r.urls || [])] }));
      const src = next.find(r => r.id === dragSrc.rowId && r.type !== 'text');
      const tgt = next.find(r => r.id === targetRowId && r.type !== 'text');
      if (!src || !tgt) return prev;
      if (dragSrc.rowId === targetRowId && dragSrc.imgIdx === targetImgIdx) return prev;
      const srcUrl = src.urls[dragSrc.imgIdx];
      const tgtUrl = tgt.urls[targetImgIdx];
      if (tgtUrl !== undefined) {
        src.urls[dragSrc.imgIdx] = tgtUrl;
        tgt.urls[targetImgIdx] = srcUrl;
      } else {
        if (tgt.urls.length >= (tgt.layout || 3)) return prev;
        tgt.urls.push(srcUrl);
        src.urls.splice(dragSrc.imgIdx, 1);
      }
      return next.filter(r => r.type === 'text' || r.urls.length > 0);
    });
    setDragSrc(null);
  };

  const triggerUploadForRow = (rowId) => {
    pendingUploadRowId.current = rowId;
    setUploadProgress([]);
    setUploadModal(true);
  };

  const addNewRowAndUpload = () => {
    const newId = genRowId();
    setImageRows(prev => [...prev, { id: newId, type: 'image', urls: [], layout: 1 }]);
  };

  const uploadFileWithProgress = (file, token, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('image', file);
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
        else reject(new Error('Upload failed'));
      });
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.open('POST', `${API_URL}/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });

  const handleModalUpload = async (files) => {
    const fileArray = Array.from(files);
    if (!fileArray.length) return;
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    setUploadProgress(fileArray.map(f => ({ name: f.name, progress: 0, done: false, error: false })));
    setUploading(true);
    const newUrls = [];
    for (let i = 0; i < fileArray.length; i++) {
      try {
        const data = await uploadFileWithProgress(fileArray[i], token, pct => {
          setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, progress: pct } : p));
        });
        setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, progress: 100, done: true } : p));
        newUrls.push(`${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${data.url}`);
      } catch {
        setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, error: true } : p));
      }
    }
    setUploading(false);
    const targetId = pendingUploadRowId.current;
    if (newUrls.length > 0) {
      if (targetId) {
        setImageRows(prev => prev.map(r => {
          if (r.id !== targetId) return r;
          const max = r.layout || 3;
          const merged = [...r.urls, ...newUrls].slice(0, max);
          return { ...r, urls: merged };
        }));
      } else {
        const layout = Math.min(newUrls.length, 3);
        setImageRows(prev => [...prev, { id: genRowId(), type: 'image', urls: newUrls.slice(0, layout), layout }]);
      }
      pendingUploadRowId.current = null;
    }
    setTimeout(() => { setUploadModal(false); setUploadProgress([]); }, 600);
  };

  // Build HTML content from body + image rows
  const stickerShortcodesToHtml = (text) => {
    const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
    const allStickers = [...STICKERS, ...customStickersList];
    return text.replace(/(<[^>]*>)|:(\w+):/g, (match, tag, name) => {
      if (tag) return tag;
      const sticker = allStickers.find(s => s.name === name);
      if (!sticker) return match;
      if (sticker.file_url) return `<img src="${apiBase}${sticker.file_url}" alt="sticker" class="sticker-inline-img" />`;
      return sticker.emoji || match;
    });
  };

  const buildContent = () => {
    const { body } = getDraftParts();
    if (editPost) return body;
    let html = stickerShortcodesToHtml(body)
      .replace(/\*\*(.*?)\*\*/g, '<h1>$1</h1>')
      .split('\n').join('<br/>');
    if (location.trim()) html += `<p class="post-location">📍 ${location}</p>`;
    if (vibe) html += `<p class="post-vibe">${vibe}</p>`;
    for (const row of imageRows) {
      if (row.type === 'text') {
        if (row.content && row.content.trim()) {
          html += '<p>' + row.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split('\n').join('<br/>') + '</p>';
        }
      } else if (row.type === 'product' && row.products?.length > 0) {
        const cards = row.products.map(p => {
          const img = p.image ? `<img src="${p.image}" alt="" />` : '';
          return `<div class="post-product-card" data-product-id="${p.id}" data-external-url="${p.external_url || ''}" data-name="${p.name.replace(/"/g, '&quot;')}" data-price="$${(p.price_cents / 100).toFixed(2)}" data-shop="${(p.shop_name || '').replace(/"/g, '&quot;')}" data-img="${p.image || ''}">${img}<span class="post-product-name">${p.name.replace(/</g, '&lt;')}</span><span class="post-product-price">$${(p.price_cents / 100).toFixed(2)}</span>${p.shop_name ? `<span class="post-product-shop">${p.shop_name.replace(/</g, '&lt;')}</span>` : ''}${p.external_url ? '<span class="post-product-external">Open in a new tab</span>' : ''}</div>`;
        }).join('');
        html += `<div class="post-product-grid" data-cols="${row.layout}">${cards}</div>`;
      } else if (row.urls && row.urls.length > 0) {
        const mediaHtml = row.urls.map(url =>
          isVideoUrl(url)
            ? `<video src="${url}" controls></video>`
            : `<img src="${url}" alt="" />`
        ).join('');
        html += `<div class="post-image-grid" data-cols="${row.layout}">${mediaHtml}</div>`;
      }
    }
    for (const l of detectedLinks.filter(x => !x.dismissed)) {
      if (l.type === 'image') {
        html += `<img src="${l.url}" alt="" />`;
      } else if (l.type === 'video') {
        html += `<video src="${l.url}" controls></video>`;
      } else if (l.type === 'youtube') {
        html += `<div class="link-embed-video"><iframe src="https://www.youtube.com/embed/${l.youtubeId}" width="560" height="315" frameborder="0" allowfullscreen></iframe></div>`;
      } else if (l.type === 'vimeo') {
        html += `<div class="link-embed-video"><iframe src="https://player.vimeo.com/video/${l.vimeoId}" width="560" height="315" frameborder="0" allowfullscreen></iframe></div>`;
      } else if (l.type === 'link' && l.ogData) {
        html += `<a href="${l.url}" class="link-embed-card" target="_blank" rel="noreferrer">`;
        if (l.ogData.image) html += `<img src="${l.ogData.image}" alt="" class="link-embed-image" />`;
        html += `<div class="link-embed-body">`;
        if (l.ogData.site_name) html += `<div class="link-embed-site">${l.ogData.site_name}</div>`;
        if (l.ogData.title) html += `<div class="link-embed-title">${l.ogData.title}</div>`;
        if (l.ogData.description) html += `<div class="link-embed-desc">${l.ogData.description.slice(0, 120)}${l.ogData.description.length > 120 ? '…' : ''}</div>`;
        html += `</div></a>`;
      } else if (l.type === 'link') {
        html += `<a href="${l.url}" target="_blank" rel="noreferrer">${l.url}</a>`;
      }
    }
    return html;
  };

  // Replace :shortcode: text nodes in the contenteditable with visual sticker elements.
  // Returns true if any replacements were made.
  const renderShortcodesInEditor = () => {
    const el = bodyRef.current;
    if (!el) return false;
    const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
    const allStickers = [...STICKERS, ...customStickersList];
    const regex = /:(\w+):/g;
    let replaced = false;

    // Collect text nodes first (avoid modifying DOM while iterating)
    const textNodes = [];
    const walkCollect = (node) => {
      if (node.nodeType === Node.TEXT_NODE) textNodes.push(node);
      else for (const child of node.childNodes) walkCollect(child);
    };
    walkCollect(el);

    for (const tn of textNodes) {
      let match;
      regex.lastIndex = 0;
      while ((match = regex.exec(tn.textContent)) !== null) {
        const sticker = allStickers.find(s => s.name === match[1]);
        if (!sticker) continue;
        const before = tn.textContent.slice(0, match.index);
        const after = tn.textContent.slice(match.index + match[0].length);
        let visual;
        if (sticker.file_url) {
          visual = document.createElement('img');
          visual.src = sticker.file_url.startsWith('http') ? sticker.file_url : `${apiBase}${sticker.file_url}`;
          visual.alt = sticker.name;
          visual.className = 'sticker-inline-img';
          visual.setAttribute('data-shortcode', `:${sticker.name}:`);
        } else if (sticker.emoji) {
          visual = document.createTextNode(sticker.emoji);
        }
        if (!visual) continue;
        const parent = tn.parentNode;
        if (before) parent.insertBefore(document.createTextNode(before), tn);
        parent.insertBefore(visual, tn);
        tn.textContent = after;
        // Place cursor after the inserted element
        const sel = window.getSelection();
        if (sel) {
          const r = document.createRange();
          if (after) { r.setStart(tn, 0); r.setEnd(tn, 0); }
          else { r.setStartAfter(visual); r.setEndAfter(visual); }
          sel.removeAllRanges();
          sel.addRange(r);
        }
        replaced = true;
        regex.lastIndex = 0; // restart on the (now shorter) remaining text
      }
    }
    return replaced;
  };

  // Insert :shortcode: text at the cursor, then render it as a visual preview.
  const insertStickerInEditor = (shortcode) => {
    const el = bodyRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    let range;
    if (sel && sel.rangeCount > 0) {
      range = sel.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
    }
    range.deleteContents();
    const textNode = document.createTextNode(shortcode);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    if (sel) { sel.removeAllRanges(); sel.addRange(range); }
    renderShortcodesInEditor();
    setDraft(prev => prev.split('\n')[0] + '\n' + readBodyFromEditor());
  };

  // Handle image/video upload (supports multiple files)
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const newUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);
        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (!response.ok) throw new Error('Failed to upload');
        const data = await response.json();
        newUrls.push(`${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${data.url}`);
      }
      const targetId = pendingUploadRowId.current;
      if (targetId) {
        setImageRows(prev => prev.map(r => {
          if (r.id !== targetId) return r;
          const max = r.layout || 3;
          const merged = [...r.urls, ...newUrls].slice(0, max);
          return { ...r, urls: merged };
        }));
      } else {
        const layout = Math.min(newUrls.length, 3);
        setImageRows(prev => [...prev, { id: genRowId(), type: 'image', urls: newUrls.slice(0, layout), layout }]);
      }
      pendingUploadRowId.current = null;
    } catch (err) {
      setError('Failed to upload: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Fetch custom stickers on mount for buildContent()
  useEffect(() => {
    const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
    fetch(`${apiBase}/api/stickers`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setCustomStickersList(Array.isArray(data) ? data.map(s => ({ name: s.shortcode, file_url: s.file_url })) : []))
      .catch(() => {});
  }, []);

  // Initialize contenteditable body with existing content (e.g. when editing a post)
  useEffect(() => {
    if (bodyRef.current) {
      const body = draft.split('\n').slice(1).join('\n');
      if (body && !bodyRef.current.textContent) bodyRef.current.textContent = body;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all tribes on mount (sorted by popularity)
  useEffect(() => {
    fetch(`${API_URL}/tribes`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setAllTribes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Pre-select initialTribe on mount if provided
  useEffect(() => {
    if (initialTribe && !editPost) {
      setSelectedTribes([initialTribe]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect URLs in body and build link previews
  const isImageUrl = (url) => /\.(jpe?g|png|gif|webp|svg)(\?.*)?$/i.test(url);
  useEffect(() => {
    if (editPost) return;
    clearTimeout(linkDebounceRef.current);
    linkDebounceRef.current = setTimeout(() => {
      const { body } = getDraftParts();
      const urlRegex = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
      const found = [...new Set(body.match(urlRegex) || [])];
      setDetectedLinks(prev => {
        const kept = prev.filter(l => found.includes(l.url));
        const existing = new Set(kept.map(l => l.url));
        const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
        const newLinks = found.filter(url => !existing.has(url)).map(url => {
          const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
          const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
          const type = isImageUrl(url) ? 'image' : isVideoUrl(url) ? 'video'
            : ytMatch ? 'youtube' : vimeoMatch ? 'vimeo' : 'link';
          return { url, type, youtubeId: ytMatch?.[1] || null, vimeoId: vimeoMatch?.[1] || null, ogData: null, dismissed: false };
        });
        newLinks.filter(l => l.type === 'link').forEach(l => {
          fetch(`${apiBase}/api/embed/preview?url=${encodeURIComponent(l.url)}`)
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setDetectedLinks(prev2 => prev2.map(x => x.url === l.url ? { ...x, ogData: data } : x)); })
            .catch(() => {});
        });
        return [...kept, ...newLinks];
      });
    }, 600);
    return () => clearTimeout(linkDebounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const addTribe = (tribe) => {
    if (!selectedTribes.find(t => t.id === tribe.id)) setSelectedTribes(prev => [...prev, tribe]);
    setTribeSearch('');
    // Keep dropdown open so user can keep adding tribes
  };

  const removeTribe = (tribeId) => setSelectedTribes(selectedTribes.filter(t => t.id !== tribeId));

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    const { tagline } = getDraftParts();
    if (!tagline) { setError('Title is required'); return; }
    const content = buildContent();
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const url = editPost
        ? `${API_URL}/posts/${editPost.id}`
        : `${API_URL}/posts`;
      const response = await fetch(url, {
        method: editPost ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          tagline,
          content,
          customCss,
          tribeIds: selectedTribes.map(t => t.id),
          content_warning: contentWarning.trim() || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save post');
      }

      setDraft('');
      setSelectedTribes([]);
      setLocation('');
      setVibe('');
      setPrivacy('public');
      setImageRows([]);
      setDetectedLinks([]);
      setContentWarning('');
      setShowCwInput(false);
      if (onPostCreated) onPostCreated();
      if (onCancel) onCancel();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const { tagline: draftTagline, body: draftBody } = getDraftParts();
  const hasExtra = imageRows.some(r => r.type === 'text' ? r.content?.trim() : r.urls?.length > 0);
  const canPost = draftTagline && !currentUser?.is_banned && !currentUser?.is_muted;

  const filteredTribes = allTribes.filter(t =>
    !selectedTribes.find(s => s.id === t.id) &&
    (tribeSearch === '' || t.name.toLowerCase().includes(tribeSearch.toLowerCase()) ||
      (t.tag && t.tag.toLowerCase().includes(tribeSearch.toLowerCase())))
  );

  const filteredLocations = location.length >= 2
    ? WORLD_LOCATIONS.filter(l => l.toLowerCase().includes(location.toLowerCase())).slice(0, 20)
    : [];

  return (
    <div className="post-composer">
      {error && <div className="composer-error">{error}</div>}

      {/* Header: Write a post! + vibe */}
      <div className="composer-topbar">
        <span className="composer-heading">Write a post!</span>
        <div style={{ flex: 1 }} />
        <select className="composer-vibe-select" value={vibe} onChange={e => setVibe(e.target.value)}>
          <option value="">vibe…</option>
          {COMPOSER_VIBES.map(v => (
            <option key={v.emoji} value={`${v.emoji} ${v.label}`}>{v.emoji} {v.label}</option>
          ))}
        </select>
      </div>

      {/* Avatar + split title/body inputs */}
      <div className="composer-input-row">
        <div className="composer-avatar-wrap">
          <SocialImage src={currentUser?.profile_picture || `${BASE_PATH}/images/profile.svg`} alt="" className="composer-avatar" />
        </div>
        <div className="composer-fields">
          <input
            ref={titleRef}
            className="composer-title-input"
            placeholder="Title…"
            value={draft.split('\n')[0]}
            onChange={e => setDraft(e.target.value + '\n' + draft.split('\n').slice(1).join('\n'))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); bodyRef.current?.focus(); } }}
          />
          <div
            ref={bodyRef}
            contentEditable="true"
            suppressContentEditableWarning={true}
            className="composer-draft"
            data-placeholder="What's on your mind?"
            onInput={() => { renderShortcodesInEditor(); setDraft(prev => prev.split('\n')[0] + '\n' + readBodyFromEditor()); }}
            onClick={() => { if (!draft.split('\n')[0]) titleRef.current?.focus(); }}
          />
        </div>
      </div>

      {/* Extra content rows (image/video grids + text blocks) */}
      {imageRows.length > 0 && (
        <div className="composer-image-rows">
          {imageRows.map((row, rowIdx) => (
            <div key={row.id} className="composer-row">
              {/* Row header — controls differ by type */}
              <div className="composer-row-header">
                {row.type === 'text' && (
                  <span className="row-type-label">¶ Text</span>
                )}
                {row.type !== 'text' && (
                  <div className="row-layout-btns">
                    {[1, 2, 3].map(n => (
                      <button key={n} type="button"
                        className={`row-layout-btn${row.layout === n ? ' active' : ''}`}
                        onClick={() => setRowLayout(row.id, n)}
                        title={n === 1 ? '1 column (100%)' : n === 2 ? '2 columns (50/50)' : '3 columns (33/33/33)'}
                      >{n}</button>
                    ))}
                  </div>
                )}
                <div style={{ flex: 1 }} />
                {rowIdx > 0 && <button type="button" className="row-ctrl-btn" onClick={() => moveRow(row.id, -1)}>↑</button>}
                {rowIdx < imageRows.length - 1 && <button type="button" className="row-ctrl-btn" onClick={() => moveRow(row.id, 1)}>↓</button>}
                <button type="button" className="row-ctrl-btn row-ctrl-remove" onClick={() => removeRow(row.id)}>×</button>
              </div>

              {/* Row body */}
              {row.type === 'text' ? (
                <textarea
                  className="composer-draft row-text-area"
                  placeholder="Add text…"
                  value={row.content || ''}
                  onChange={e => updateRowText(row.id, e.target.value)}
                  rows={3}
                />
              ) : row.type === 'product' ? (
                <div className="composer-product-row">
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearches[row.id] || ''}
                      onChange={e => searchProductsForRow(row.id, e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: "1px solid var(--ui-border)", borderRadius: 8, fontSize: 13 }}
                    />
                    {(productResults[row.id] || []).length > 0 && (
                      <div className="product-search-dropdown">
                        {productResults[row.id].map(p => (
                          <div key={p.id} className="product-search-item" onClick={() => addProductToRow(row.id, p)}>
                            {p.images?.[0] && <SocialImage src={p.images[0].startsWith('http') ? p.images[0] : `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}/${p.images[0]}`} alt="" />}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                              <div style={{ fontSize: 12, color: "var(--ui-accent)" }}>${(p.price_cents / 100).toFixed(2)}{p.shop_name ? ` · ${p.shop_name}` : ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {row.products.length > 0 && (
                    <div className={`composer-image-grid cols-${row.layout}`}>
                      {row.products.map((p, pIdx) => (
                        <div key={p.id} className="composer-product-card">
                          {p.image && <SocialImage src={p.image} alt="" />}
                          <div className="composer-product-info">
                            <span className="composer-product-name">{p.name}</span>
                            <span className="composer-product-price">${(p.price_cents / 100).toFixed(2)}</span>
                          </div>
                          <button type="button" className="composer-image-remove" onClick={() => removeProductFromRow(row.id, pIdx)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className={`composer-image-grid cols-${row.layout}`}>
                  {(row.urls || []).map((url, imgIdx) => (
                    <div key={imgIdx}
                      className={`composer-image-wrap${dragSrc?.rowId === row.id && dragSrc?.imgIdx === imgIdx ? ' dragging' : ''}`}
                      draggable
                      onDragStart={e => handleImgDragStart(e, row.id, imgIdx)}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => handleImgDrop(e, row.id, imgIdx)}
                    >
                      {isVideoUrl(url)
                        ? <SocialVideo src={url} className="composer-image-preview" />
                        : <SocialImage src={url} alt="" className="composer-image-preview" />}
                      <button type="button" className="composer-image-remove" onClick={() => removeRowImage(row.id, imgIdx)}>×</button>
                    </div>
                  ))}
                  {(row.urls || []).length < (row.layout || 3) && (
                    <div className="composer-image-wrap composer-add-slot"
                      onClick={() => { pendingUploadRowId.current = row.id; fileInputRef.current?.click(); }}
                      onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                      onDragLeave={e => e.currentTarget.classList.remove('dragover')}
                      onDrop={e => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('dragover');
                        if (e.dataTransfer.files?.length) {
                          pendingUploadRowId.current = row.id;
                          handleImageUpload({ target: { files: e.dataTransfer.files } });
                        } else {
                          handleImgDrop(e, row.id, (row.urls || []).length);
                        }
                      }}
                    >+</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detected link previews */}
      {detectedLinks.filter(l => !l.dismissed).length > 0 && (
        <div className="detected-links">
          {detectedLinks.filter(l => !l.dismissed).map(l => (
            <div key={l.url} className="detected-link-item">
              <button type="button" className="detected-link-dismiss" onClick={() => setDetectedLinks(prev => prev.map(x => x.url === l.url ? { ...x, dismissed: true } : x))}>×</button>
              {l.type === 'image' && <SocialImage src={l.url} alt="" className="detected-link-img" />}
              {l.type === 'video' && <SocialVideo src={l.url} controls className="detected-link-img" />}
              {l.type === 'youtube' && (
                <div className="link-embed-video">
                  <iframe src={`https://www.youtube.com/embed/${l.youtubeId}`} width="100%" height="202" frameBorder="0" allowFullScreen title="YouTube" />
                </div>
              )}
              {l.type === 'vimeo' && (
                <div className="link-embed-video">
                  <iframe src={`https://player.vimeo.com/video/${l.vimeoId}`} width="100%" height="202" frameBorder="0" allowFullScreen title="Vimeo" />
                </div>
              )}
              {l.type === 'link' && l.ogData && (
                <a href={l.url} target="_blank" rel="noreferrer" className="link-embed-card">
                  {l.ogData.image && <SocialImage src={l.ogData.image} alt="" className="link-embed-image" />}
                  <div className="link-embed-body">
                    {l.ogData.site_name && <div className="link-embed-site">{l.ogData.site_name}</div>}
                    {l.ogData.title && <div className="link-embed-title">{l.ogData.title}</div>}
                    {l.ogData.description && <div className="link-embed-desc">{l.ogData.description.slice(0, 120)}{l.ogData.description.length > 120 ? '…' : ''}</div>}
                  </div>
                </a>
              )}
              {l.type === 'link' && !l.ogData && (
                <div className="detected-link-loading">Fetching preview…</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Content warning */}
      {showCwInput ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0', padding: '8px 12px', background: '#fff3cd', borderRadius: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#856404' }}>CW:</span>
          <input
            type="text"
            value={contentWarning}
            onChange={e => setContentWarning(e.target.value)}
            placeholder="Describe the content warning..."
            style={{ flex: 1, border: "1px solid var(--ui-border)", borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
          />
          <button type="button" onClick={() => { setShowCwInput(false); setContentWarning(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#856404', fontWeight: 700, fontSize: 16 }}>✕</button>
        </div>
      ) : null}

      {/* Add more rows + post button */}
      <div className="composer-add-row-btns">
        <button type="button" className="row-add-btn" onClick={addTextRow}>+ Text</button>
        <button type="button" className="row-add-btn" onClick={addNewRowAndUpload}>+ Media</button>
        <button type="button" className="row-add-btn" onClick={() => setShowStickers(s => !s)}>+ Stickers</button>
        <button type="button" className="row-add-btn more-menu-wrap" onClick={() => setShowMoreMenu(v => !v)}>•••
          {showMoreMenu && (
            <div className="more-menu-dropdown">
              {!showCwInput && <button type="button" className="more-menu-item" onClick={(e) => { e.stopPropagation(); setShowCwInput(true); setShowMoreMenu(false); }}>+ Content Warning</button>}
              <button type="button" className="more-menu-item" onClick={(e) => { e.stopPropagation(); addProductRow(); setShowMoreMenu(false); }}>+ Products</button>
            </div>
          )}
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="composer-btn composer-post-btn"
          onClick={handleSubmit}
          disabled={!canPost || loading}
          title="Post"
        >
          {loading ? '⌛' : <SocialImage src={`${BASE_PATH}/images/arrow-post.svg`} alt="Post" className="arrow-post-icon" />}
        </button>
      </div>

      {/* Upload modal */}
      {uploadModal && (
        <div className="upload-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !uploading) { setUploadModal(false); pendingUploadRowId.current = null; } }}>
          <div className="upload-modal">
            <div className="upload-modal-header">
              <span>Upload Media</span>
              {!uploading && <button type="button" className="upload-modal-close" onClick={() => { setUploadModal(false); pendingUploadRowId.current = null; }}>×</button>}
            </div>
            {uploadProgress.length === 0 ? (
              <div
                className="upload-dropzone"
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                onDragLeave={e => e.currentTarget.classList.remove('dragover')}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('dragover'); handleModalUpload(e.dataTransfer.files); }}
                onClick={() => modalFileInputRef.current?.click()}
              >
                <div className="upload-dropzone-icon">🖼️</div>
                <p className="upload-dropzone-text">Drag &amp; drop images or videos here</p>
                <p className="upload-dropzone-sub">or click to browse files</p>
                <input type="file" ref={modalFileInputRef} multiple accept="image/*,video/*" style={{ display: 'none' }} onChange={e => handleModalUpload(e.target.files)} />
              </div>
            ) : (
              <div className="upload-progress-list">
                {uploadProgress.map((p, i) => (
                  <div key={i} className="upload-progress-item">
                    <div className="upload-progress-name" title={p.name}>{p.name}</div>
                    <div className="upload-progress-bar-wrap">
                      <div className="upload-progress-bar" style={{ width: `${p.progress}%`, background: p.error ? '#e53e3e' : p.done ? '#38a169' : "var(--ui-accent)" }} />
                    </div>
                    <div className="upload-progress-pct">{p.error ? 'Failed' : p.done ? '✓' : `${p.progress}%`}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*,video/*" multiple style={{ display: 'none' }} />

      {/* Sticker panel */}
      {showStickers && (
        <StickerPicker
          onSelect={(sc) => {
            insertStickerInEditor(sc);
            setShowStickers(false);
          }}
          onClose={() => setShowStickers(false)}
          onStickerUploaded={(s) => setCustomStickersList(prev => [...prev, { name: s.shortcode, file_url: s.file_url }])}
        />
      )}

      {/* Metadata row: tribes | location */}
      <div className="composer-meta">
        {/* Tribe searchable dropdown */}
        <div className="composer-tribes-wrap" onClick={e => e.currentTarget.querySelector('input')?.focus()}>
          <span className="composer-meta-icon">#</span>
          <input
            className="composer-meta-input"
            placeholder="tribe"
            value={tribeSearch}
            onChange={e => { setTribeSearch(e.target.value); setShowTribesDropdown(true); }}
            onFocus={() => setShowTribesDropdown(true)}
            onBlur={() => setTimeout(() => setShowTribesDropdown(false), 150)}
          />
          {showTribesDropdown && filteredTribes.length > 0 && (
            <div className="composer-tribe-dropdown">
              {filteredTribes.map(tribe => (
                <div key={tribe.id} className="composer-tribe-option" onMouseDown={() => addTribe(tribe)}>
                  <TribeIcon icon={tribe.icon} size={18} />
                  <span>{tribe.name}</span>
                  {tribe.member_count > 0 && <span className="composer-tribe-count">{tribe.member_count}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location searchable dropdown */}
        <div className="composer-location-wrap" onClick={e => e.currentTarget.querySelector('input')?.focus()}>
          <span className="composer-meta-icon">📍</span>
          <input
            className="composer-meta-input"
            placeholder="location"
            value={location}
            onChange={e => { setLocation(e.target.value); setLocationOpen(true); }}
            onFocus={() => setLocationOpen(true)}
            onBlur={() => setTimeout(() => setLocationOpen(false), 150)}
          />
          {locationOpen && filteredLocations.length > 0 && (
            <div className="composer-location-dropdown">
              {filteredLocations.map(loc => (
                <div key={loc} className="composer-location-option" onMouseDown={() => { setLocation(loc); setLocationOpen(false); }}>
                  {loc}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Privacy row — full width with label */}
      <div className="composer-privacy-row" onClick={e => e.currentTarget.querySelector('select')?.focus()}>
        <label className="composer-privacy-label">Privacy</label>
        <select className="composer-privacy-select" value={privacy} onChange={e => setPrivacy(e.target.value)}>
          <option value="public">🌍 public</option>
          <option value="mutuals">👥 mutuals</option>
          <option value="private">🔒 private</option>
        </select>
      </div>

      {/* Selected tribe chips */}
      {selectedTribes.length > 0 && (
        <div className="composer-selected-tribes">
          {selectedTribes.map(tribe => (
            <span key={tribe.id} className="composer-tribe-chip" style={{ background: tribe.color || "var(--ui-accent)", color: tribe.color ? '#fff' : 'var(--ui-on-accent)' }}>
              <TribeIcon icon={tribe.icon} size={12} />
              {tribe.name}
              <button type="button" onClick={() => removeTribe(tribe.id)}>×</button>
            </span>
          ))}
        </div>
      )}

    </div>
  );
};

// Module-level sticker cache for PostCard (avoids per-card fetches)
let _globalCustomStickers = null;
const _stickerFetchPromise = { current: null };
function useGlobalCustomStickers() {
  const [stickers, setStickers] = useState(_globalCustomStickers || []);
  useEffect(() => {
    if (_globalCustomStickers !== null) return;
    if (!_stickerFetchPromise.current) {
      const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
      _stickerFetchPromise.current = fetch(`${apiBase}/api/stickers`)
        .then(r => r.ok ? r.json() : [])
        .then(data => {
          _globalCustomStickers = Array.isArray(data) ? data.map(s => ({ name: s.shortcode, file_url: s.file_url })) : [];
          return _globalCustomStickers;
        })
        .catch(() => { _globalCustomStickers = []; return []; });
    }
    _stickerFetchPromise.current.then(setStickers);
  }, []);
  return stickers;
}

// Post Display Component
const PostCard = ({ post, onEdit, onDelete, isOwner, disablePostNav }) => {
  const { user: currentUser } = useAuth();
  const globalCustomStickers = useGlobalCustomStickers();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [showPostMenu, setShowPostMenu] = useState(false);
  const [commentMenuId, setCommentMenuId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [cwRevealed, setCwRevealed] = useState(!post.content_warning);
  const [showCommentStickerPicker, setShowCommentStickerPicker] = useState(false);
  const commentFileInputRef = useRef(null);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [showReplyStickerPicker, setShowReplyStickerPicker] = useState(false);
  const replyFileInputRef = useRef(null);
  const [showAlbumPicker, setShowAlbumPicker] = useState(false);
  const [cardAlbums, setCardAlbums] = useState([]);
  const [cardNewAlbumName, setCardNewAlbumName] = useState('');
  const replyInputRef = useRef(null);
  const profileUrl = post.username !== post.email ? `/${post.username}` : `/${post.user_id}`;

  // Extract location/vibe from content HTML, leave clean content for rendering
  const [cleanContent, postLocation, postVibe] = React.useMemo(() => {
    const div = document.createElement('div');
    div.innerHTML = post.content || '';
    const locEl = div.querySelector('.post-location');
    const vibeEl = div.querySelector('.post-vibe');
    const loc = locEl ? locEl.textContent.trim() : '';
    const vib = vibeEl ? vibeEl.textContent.trim() : '';
    if (locEl) locEl.remove();
    if (vibeEl) vibeEl.remove();
    const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
    const allStickers = [...STICKERS, ...globalCustomStickers];
    // Tag-aware replacement: skip shortcodes inside HTML tags/attributes
    const withStickers = div.innerHTML.replace(/(<[^>]*>)|:(\w+):/g, (match, tag, name) => {
      if (tag) return tag;
      const sticker = allStickers.find(s => s.name === name);
      if (!sticker) return match;
      if (sticker.file_url) return `<img src="${apiBase}${sticker.file_url}" alt="sticker" class="sticker-inline-img" />`;
      return sticker.emoji || match;
    });
    return [withStickers, loc, vib];
  }, [post.content, globalCustomStickers]);

  // Interaction state
  const [liked, setLiked] = useState(!!post.user_liked);
  const [favorited, setFavorited] = useState(!!post.user_favorited);
  const [reposted, setReposted] = useState(!!post.user_reposted);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [favoriteCount, setFavoriteCount] = useState(post.favorite_count || 0);
  const [repostCount, setRepostCount] = useState(post.repost_count || 0);

  const handleToggleLike = async () => {
    if (!currentUser) return;
    // Optimistic update
    setLiked(!liked);
    setLikeCount(prev => liked ? prev - 1 : prev + 1);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${post.id}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLiked(data.liked);
        setLikeCount(data.like_count);
      }
    } catch (err) {
      // Revert on error
      setLiked(liked);
      setLikeCount(post.like_count || 0);
    }
  };

  const handleToggleFavorite = async () => {
    if (!currentUser) return;
    setFavorited(!favorited);
    setFavoriteCount(prev => favorited ? prev - 1 : prev + 1);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${post.id}/favorite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFavorited(data.favorited);
        setFavoriteCount(data.favorite_count);
      }
    } catch (err) {
      setFavorited(favorited);
      setFavoriteCount(post.favorite_count || 0);
    }
  };

  const handleToggleRepost = async () => {
    if (!currentUser) return;
    setReposted(!reposted);
    setRepostCount(prev => reposted ? prev - 1 : prev + 1);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${post.id}/repost`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReposted(data.reposted);
        setRepostCount(data.repost_count);
      }
    } catch (err) {
      setReposted(reposted);
      setRepostCount(post.repost_count || 0);
    }
  };



  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await fetch(`${API_URL}/posts/${post.id}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  // On mount: fetch comments and auto-open section if none exist
  useEffect(() => {
    const loadAndMaybeOpen = async () => {
      setLoadingComments(true);
      try {
        const response = await fetch(`${API_URL}/posts/${post.id}/comments`);
        if (response.ok) {
          const data = await response.json();
          setComments(data);
          if (data.length === 0) {
            setShowComments(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch comments:', err);
      } finally {
        setLoadingComments(false);
      }
    };
    loadAndMaybeOpen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  const handleToggleComments = () => {
    if (!showComments) {
      fetchComments();
    }
    setShowComments(prev => !prev);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${post.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newComment })
      });

      if (response.ok) {
        const data = await response.json();
        setComments([...comments, data.comment]);
        setNewComment('');
      } else {
        const data = await response.json().catch(() => ({}));
        if (data.code === 'BANNED') alert('Your account is restricted. You cannot post.');
        else if (data.code === 'MUTED') alert('Your account is muted. You cannot post.');
      }
    } catch (err) {
      console.error('Failed to submit comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSubmitReply = async (e, parentCommentId) => {
    e.preventDefault();
    if (!replyText.trim() || submittingReply) return;

    setSubmittingReply(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${post.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: replyText, parentCommentId })
      });

      if (response.ok) {
        const data = await response.json();
        // Update comments to include the new reply
        const updatedComments = comments.map(c => {
          if (c.id === parentCommentId) {
            return { ...c, replies: [...(c.replies || []), data.comment] };
          }
          return c;
        });
        setComments(updatedComments);
        setReplyText('');
        setReplyingToCommentId(null);
      }
    } catch (err) {
      console.error('Failed to submit reply:', err);
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/comments/${commentId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editCommentText.trim() })
      });
      if (response.ok) {
        const data = await response.json();
        const updateComments = (list) => list.map(c => c.id === commentId ? { ...c, content: data.comment.content, updated_at: data.comment.updated_at } : { ...c, replies: c.replies ? updateComments(c.replies) : [] });
        setComments(updateComments(comments));
        setEditingCommentId(null);
        setEditCommentText('');
      }
    } catch (err) {
      console.error('Failed to edit comment:', err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setComments(comments.filter(c => c.id !== commentId));
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '20px', overflow: 'hidden', padding: 0 }}>
      {post.reposter_username && (
        <div style={{ padding: '8px 15px', background: "var(--ui-muted-surface)", borderBottom: "1px solid var(--ui-border)", fontSize: '12px', color: "var(--ui-muted)", display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ fontSize: '14px' }}>🔄</span>
          <span>Reposted by <strong>@{post.reposter_username}</strong></span>
        </div>
      )}
      <div style={{ padding: '20px' }}>
        {/* Post Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
          <Link to={profileUrl} style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: 'inherit' }}>
            {post.profile_picture ? (
              <SocialImage
                src={post.profile_picture}
                alt="Profile"
                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(90deg, #f15722, #f78e1e, #fed206, #8ec74e, #49b863, #3fbced, #735ca8, #995aa4, #c263a7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {(post.first_name || post.username || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <p style={{ margin: 0, fontWeight: '500' }}>
                {post.first_name && post.last_name ? `${post.first_name} ${post.last_name}` : post.username}
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: "var(--ui-muted)" }}>
                @{post.username} · {new Date(post.created_at).toLocaleDateString()}
              </p>
            </div>
          </Link>

          {currentUser && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowPostMenu(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: "var(--ui-muted)", fontSize: '20px', padding: '4px', fontWeight: 900, letterSpacing: 2 }} title="More">•••</button>
              {showPostMenu && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: "var(--ui-surface)", borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', border: "1px solid var(--ui-border)", zIndex: 20, minWidth: 140, padding: '4px 0' }}>
                  {isOwner && onDelete && (
                    <button onClick={() => { setShowPostMenu(false); onDelete(post.id); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#e0245e', textAlign: 'left' }}>Delete</button>
                  )}
                  <button onClick={() => {
                    setShowPostMenu(false);
                    const token = localStorage.getItem(AUTH_TOKEN_KEY);
                    fetch(`${API_URL}/users/${currentUser.id}/albums`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    }).then(r => r.json()).then(data => setCardAlbums(Array.isArray(data) ? data : [])).catch(() => setCardAlbums([]));
                    setShowAlbumPicker(true);
                  }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: "var(--ui-text)", textAlign: 'left' }}>Add to Album</button>
                  <button onClick={() => { setShowPostMenu(false); setReportTarget({ type: 'post', id: post.id }); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: "var(--ui-text)", textAlign: 'left' }}>Report</button>
                  {currentUser && currentUser.id !== post.user_id && (
                    <button onClick={() => { setShowPostMenu(false); if (window.confirm(`Block @${post.username}? You won't see their posts.`)) { const token = localStorage.getItem(AUTH_TOKEN_KEY); fetch(`${API_URL}/users/${post.user_id}/block`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }).then(() => alert('User blocked')).catch(() => alert('Failed to block')); } }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#e0245e', textAlign: 'left' }}>Block @{post.username}</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tribe Tags + location + vibe */}
        {(post.tribes?.length > 0 || postLocation || postVibe) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px', marginTop: '5px', alignItems: 'center' }}>
            {post.tribes?.map(tribe => (
              <Link
                key={tribe.id}
                to={`${BASE_PATH}/tribe/${tribe.tag}`}
                style={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: tribe.color ? `${tribe.color}15` : "var(--ui-muted-surface)",
                  color: tribe.color || "var(--ui-accent)",
                  textDecoration: 'none',
                  fontWeight: '600',
                  border: tribe.color ? `1px solid ${tribe.color}30` : '1px solid var(--ui-border)'
                }}
              >
                #{tribe.tag}
              </Link>
            ))}
            {postLocation && (
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '12px', background: "var(--ui-muted-surface)", color: "var(--ui-muted)", fontWeight: '500' }}>
                {postLocation}
              </span>
            )}
            {postVibe && (
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '12px', background: '#fff8f0', color: '#b06000', fontWeight: '500' }}>
                {postVibe}
              </span>
            )}
          </div>
        )}

        {/* Post Tagline */}
        {post.tagline && (
          <h3
            className="post-tagline"
            style={{ margin: '15px 0 10px 0', fontSize: '1rem', fontWeight: 700, color: "var(--ui-text)", cursor: disablePostNav ? 'default' : 'pointer' }}
            onClick={() => { if (!disablePostNav) navigate(`${BASE_PATH}/post/${post.id}`); }}
          >
            {post.tagline}
          </h3>
        )}

        {/* Content Warning */}
        {post.content_warning && !cwRevealed && (
          <div style={{ background: '#fff3cd', borderRadius: 10, padding: '16px 20px', margin: '10px 0', textAlign: 'center' }}>
            <p style={{ margin: 0, fontWeight: 600, color: '#856404', fontSize: 14 }}>Content Warning: {post.content_warning}</p>
            <button onClick={() => setCwRevealed(true)} style={{ marginTop: 10, padding: '6px 20px', borderRadius: 6, border: '1px solid #856404', background: '#fff3cd', color: '#856404', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Show Content</button>
          </div>
        )}

        {/* Post Content with Custom CSS */}
        {cwRevealed && (
        <div className="post-content-wrapper">
          {post.custom_css && <style>{`.post-${post.id} { } ${post.custom_css}`}</style>}
          <div
            className={`post-${post.id}`}
            dangerouslySetInnerHTML={{ __html: richMediaHtml(cleanContent) }}
            style={{ lineHeight: '1.5', fontSize: '0.95rem', color: "var(--ui-text)", cursor: disablePostNav ? 'default' : 'pointer' }}
            onClick={e => {
              const card = e.target.closest('.post-product-card');
              if (card) {
                e.preventDefault();
                e.stopPropagation();
                const extUrl = card.dataset.externalUrl;
                const pid = card.dataset.productId;
                if (extUrl) window.open(extUrl, '_blank');
                else window.location.href = `${BASE_PATH}/marketplace/product/${pid}`;
                return;
              }
              if (!disablePostNav && e.target.tagName !== 'A' && e.target.tagName !== 'BUTTON' && e.target.tagName !== 'VIDEO' && !e.target.closest('video')) {
                navigate(`${BASE_PATH}/post/${post.id}`);
              }
            }}
          />
        </div>
        )}


        {/* Post Actions */}
        <div className="post-actions">
          <button
            onClick={handleToggleLike}
            title={liked ? "Unlike" : "Like"}
            className={`interaction-btn ${liked ? 'liked' : ''}`}
          >
            <SocialImage src={`${BASE_PATH}/Skills/${liked ? 'Love' : 'love-unselected'}.svg`} alt="Like" className="love-icon" /> {likeCount || 0}
          </button>

          <button
            onClick={handleToggleFavorite}
            title={favorited ? "Unsave" : "Save"}
            className={`interaction-btn ${favorited ? 'favorited' : ''}`}
          >
            <SocialImage src={`${BASE_PATH}/images/${favorited ? 'star' : 'star-unselected'}.svg`} alt="Save" className="love-icon" /> {favoriteCount || 0}
          </button>

          <button
            onClick={handleToggleComments}
            className="interaction-btn"
            style={{ color: "var(--ui-accent)" }}
          >
            <SocialImage src={`${BASE_PATH}/images/comment.svg`} alt="Comment" className="love-icon" /> {post.comment_count || 0}
          </button>

          <button
            onClick={handleToggleRepost}
            title={reposted ? "Undo Repost" : "Repost"}
            className={`interaction-btn ${reposted ? 'reposted' : ''}`}
          >
            <SocialImage src={`${BASE_PATH}/images/repost.svg`} alt="Repost" className="love-icon" /> {repostCount || 0}
          </button>
          <button
            onClick={() => {
              const postUrl = `${window.location.origin}${BASE_PATH}/post/${post.id}`;
              if (navigator.share) {
                navigator.share({
                  title: `Post by @${post.username}`,
                  text: post.content.replace(/<[^>]*>/g, '').substring(0, 100) + '...',
                  url: postUrl
                }).catch(() => { });
              } else {
                navigator.clipboard.writeText(postUrl);
                alert('Link copied to clipboard!');
              }
            }}
            className="interaction-btn"
            style={{ color: "var(--ui-accent)" }}
          >
            🔗
          </button>
        </div>
        {reportTarget && <ReportModal type={reportTarget.type} id={reportTarget.id} onClose={() => setReportTarget(null)} />}

        {showAlbumPicker && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowAlbumPicker(false)}>
            <div style={{ background: "var(--ui-surface)", borderRadius: 16, padding: 20, width: 340, maxHeight: '70vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>Add to Album</h3>
              {cardAlbums.map(a => (
                <button key={a.id} onClick={async () => {
                  try {
                    const token = localStorage.getItem(AUTH_TOKEN_KEY);
                    await fetch(`${API_URL}/albums/${a.id}/posts`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({ postId: post.id })
                    });
                    alert('Post added to album');
                  } catch { alert('Failed'); }
                  setShowAlbumPicker(false);
                }} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '12px 0', border: 'none', borderBottom: "1px solid var(--ui-border)", background: 'none', cursor: 'pointer', fontSize: 15 }}>
                  <span>{a.name}</span><span style={{ color: "var(--ui-muted)", fontSize: 13 }}>{a.post_count} posts</span>
                </button>
              ))}
              {cardAlbums.length === 0 && <p style={{ color: "var(--ui-muted)", textAlign: 'center' }}>No albums yet</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--ui-border)" }}>
                <input value={cardNewAlbumName} onChange={e => setCardNewAlbumName(e.target.value)} placeholder="New album name..." style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: "1px solid var(--ui-border)", fontSize: 14 }} />
                <button onClick={async () => {
                  if (!cardNewAlbumName.trim()) return;
                  try {
                    const token = localStorage.getItem(AUTH_TOKEN_KEY);
                    const res = await fetch(`${API_URL}/albums`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({ name: cardNewAlbumName.trim() })
                    });
                    const album = await res.json();
                    await fetch(`${API_URL}/albums/${album.id}/posts`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({ postId: post.id })
                    });
                    alert(`Post added to "${cardNewAlbumName.trim()}"`);
                    setCardNewAlbumName('');
                    setShowAlbumPicker(false);
                  } catch { alert('Failed'); }
                }} style={{ padding: '8px 14px', borderRadius: 8, background: "var(--ui-accent)", color: "var(--ui-on-accent)", border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Create & Add</button>
              </div>
              <button onClick={() => setShowAlbumPicker(false)} style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: "var(--ui-muted)", cursor: 'pointer', fontSize: 14 }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Top Comment Preview */}
        {!showComments && comments.length > 0 && (() => {
          const c = comments[0];
          return (
            <div style={{ marginTop: 12, padding: '10px 12px', background: "var(--ui-muted-surface)", borderRadius: 8, cursor: 'pointer' }} onClick={() => setShowComments(true)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {c.profile_picture ? (
                  <SocialImage src={c.profile_picture} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(90deg, #f15722, #f78e1e, #fed206, #8ec74e, #49b863, #3fbced, #735ca8, #995aa4, #c263a7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 'bold' }}>{c.username[0].toUpperCase()}</div>
                )}
                <span style={{ fontWeight: 600, fontSize: 13 }}>@{c.username}</span>
                <span style={{ flex: 1, fontSize: 13, color: "var(--ui-muted)", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.content}</span>
                <button onClick={(e) => { e.stopPropagation(); setShowComments(true); setReplyingToCommentId(c.id); }} style={{ background: "var(--ui-accent)", color: "var(--ui-on-accent)", border: "2px solid var(--ui-border)", borderRadius: 14, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 900, textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>Reply</button>
              </div>
              {(post.comment_count || 0) > 1 && <p style={{ margin: '6px 0 0', fontSize: 12, color: "var(--ui-accent)", fontWeight: 500 }}>View all {post.comment_count} comments</p>}
            </div>
          );
        })()}

        {/* Comments Section */}
        {showComments && (
          <div style={{ marginTop: '15px' }}>
            {loadingComments ? (
              <p style={{ color: "var(--ui-muted)", fontSize: '14px' }}>Loading comments...</p>
            ) : (
              <>
                {/* Comment input — always at top when expanded */}
                {currentUser && (
                  <>
                    {showCommentStickerPicker && (
                      <StickerPicker
                        onSelect={(shortcode) => setNewComment(prev => prev + shortcode)}
                        onClose={() => setShowCommentStickerPicker(false)}
                        customStickers={globalCustomStickers}
                        onStickerUploaded={() => {}}
                      />
                    )}
                    <input type="file" ref={commentFileInputRef} style={{ display: 'none' }} accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('image', file);
                        const token = localStorage.getItem(AUTH_TOKEN_KEY);
                        const res = await fetch(`${API_URL}/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
                        if (res.ok) { const data = await res.json(); setNewComment(prev => prev + ` [image:${data.url}]`); }
                      }} />
                    <form onSubmit={handleSubmitComment} style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                      <button type="button" onClick={() => commentFileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '4px' }} title="Attach image">📎</button>
                      <button type="button" onClick={() => setShowCommentStickerPicker(prev => !prev)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '4px' }} title="Stickers">🎨</button>
                      <input type="text" value={newComment}
                        onChange={(e) => { if (!currentUser?.is_banned && !currentUser?.is_muted) setNewComment(e.target.value); }}
                        placeholder={currentUser?.is_banned ? 'Your account is restricted.' : currentUser?.is_muted ? 'You are muted.' : 'Write a comment...'}
                        disabled={currentUser?.is_banned || currentUser?.is_muted}
                        style={{ flex: 1, padding: '10px 15px', border: "1px solid var(--ui-border)", borderRadius: '20px', fontSize: '14px', outline: 'none' }}
                      />
                      <button type="submit" disabled={submittingComment || !newComment.trim() || currentUser?.is_banned || currentUser?.is_muted}
                        style={{ padding: '10px 20px', background: "var(--ui-accent)", color: "var(--ui-on-accent)", border: "2px solid var(--ui-border)", borderRadius: '20px', cursor: submittingComment ? 'not-allowed' : 'pointer', opacity: submittingComment || !newComment.trim() ? 0.6 : 1, fontWeight: 900, textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                        {submittingComment ? '...' : 'Post'}
                      </button>
                    </form>
                  </>
                )}
                {/* Nested Comment Renderer */}
                {(() => {
                  const renderComments = (commentsList, level = 0) => (
                    <div style={{ marginBottom: '15px' }}>
                      {commentsList.length > 0 ? (
                        commentsList.map(comment => {
                          const btnColor = 'var(--ui-accent)';
                          return (
                          <div key={comment.id}>
                            <div className="comment-item" style={{
                              padding: '12px',
                              background: "var(--ui-muted-surface)",
                              borderRadius: '8px',
                              marginBottom: '10px',
                              marginLeft: `${level * 20}px`,
                              borderLeft: level > 0 ? "3px solid var(--ui-border)" : 'none'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <Link
                                  to={`/${comment.username}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    textDecoration: 'none',
                                    color: 'inherit'
                                  }}
                                >
                                  {comment.profile_picture ? (
                                    <SocialImage
                                      src={comment.profile_picture}
                                      alt=""
                                      style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '28px',
                                      height: '28px',
                                      borderRadius: '50%',
                                      background: 'linear-gradient(90deg, #f15722, #f78e1e, #fed206, #8ec74e, #49b863, #3fbced, #735ca8, #995aa4, #c263a7)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}>
                                      {comment.username[0].toUpperCase()}
                                    </div>
                                  )}
                                  <span style={{ fontWeight: '500', fontSize: '14px' }}>@{comment.username}</span>
                                </Link>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <button
                                    onClick={() => setReplyingToCommentId(replyingToCommentId === comment.id ? null : comment.id)}
                                    style={{
                                      background: btnColor,
                                      border: "2px solid var(--ui-border)",
                                      color: "var(--ui-on-accent)",
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      borderRadius: 14,
                                      padding: '4px 12px',
                                      fontWeight: 900,
                                      textShadow: '0 1px 3px rgba(0,0,0,0.4)'
                                    }}
                                  >
                                    Reply
                                  </button>
                                  {currentUser && (
                                    <div style={{ position: 'relative' }}>
                                      <button onClick={() => setCommentMenuId(commentMenuId === comment.id ? null : comment.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: "var(--ui-muted)", fontSize: '16px', padding: '2px', fontWeight: 900, letterSpacing: 2 }} title="More">•••</button>
                                      {commentMenuId === comment.id && (
                                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: "var(--ui-surface)", borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', border: "1px solid var(--ui-border)", zIndex: 20, minWidth: 140, padding: '4px 0' }}>
                                          {currentUser.id === comment.user_id && (
                                            <button onClick={() => { setCommentMenuId(null); setEditingCommentId(comment.id); setEditCommentText(comment.content); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: "var(--ui-text)", textAlign: 'left' }}>Edit</button>
                                          )}
                                          {(currentUser.id === comment.user_id || currentUser.role === 'admin') && (
                                            <button onClick={() => { setCommentMenuId(null); handleDeleteComment(comment.id); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#e0245e', textAlign: 'left' }}>Delete</button>
                                          )}
                                          <button onClick={() => { setCommentMenuId(null); setReportTarget({ type: 'comment', id: comment.id }); }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: "var(--ui-text)", textAlign: 'left' }}>Report</button>
                                          {currentUser.id !== comment.user_id && (
                                            <button onClick={() => { setCommentMenuId(null); if (window.confirm(`Block @${comment.username}? You won't see their posts.`)) { const token = localStorage.getItem(AUTH_TOKEN_KEY); fetch(`${API_URL}/users/${comment.user_id}/block`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }).then(() => alert('User blocked')).catch(() => alert('Failed to block')); } }} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#e0245e', textAlign: 'left' }}>Block @{comment.username}</button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {editingCommentId === comment.id ? (
                                <form onSubmit={(e) => { e.preventDefault(); handleEditComment(comment.id); }} style={{ margin: '8px 0 5px 0' }}>
                                  <textarea value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: 8, border: "1px solid var(--ui-border)", fontSize: 14, resize: 'vertical', minHeight: 60, boxSizing: 'border-box' }} />
                                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                    <button type="submit" style={{ padding: '4px 12px', background: "var(--ui-accent)", color: "var(--ui-on-accent)", border: "2px solid var(--ui-border)", borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 900, textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>Save</button>
                                    <button type="button" onClick={() => { setEditingCommentId(null); setEditCommentText(''); }} style={{ padding: '4px 12px', background: "var(--ui-muted-surface)", color: "var(--ui-text)", border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                                  </div>
                                </form>
                              ) : (
                                <div style={{ margin: '8px 0 5px 0', fontSize: '14px', color: "var(--ui-text)" }} dangerouslySetInnerHTML={{ __html: richMediaHtml((() => {
                                  const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
                                  const allStickers = [...STICKERS, ...globalCustomStickers];
                                  return (comment.content || '')
                                    .replace(/\[image:([^\]]+)\]/g, (_, url) => `<img src="${apiBase}${url.startsWith('/') ? url : '/uploads/' + url}" alt="" style="max-width:200px;max-height:200px;border-radius:8px;display:block;margin:4px 0;" />`)
                                    .replace(/(<[^>]*>)|:(\w+):/g, (match, tag, name) => {
                                      if (tag) return tag;
                                      const sticker = allStickers.find(s => s.name === name);
                                      if (!sticker) return match;
                                      if (sticker.file_url) return `<img src="${apiBase}${sticker.file_url}" alt="sticker" class="sticker-inline-img" />`;
                                      return sticker.emoji || match;
                                    });
                                })()) }} />
                              )}
                              <p style={{ margin: '0', fontSize: '11px', color: "var(--ui-muted)" }}>
                                {new Date(comment.created_at).toLocaleString()}
                                {comment.updated_at && comment.updated_at !== comment.created_at && (
                                  <span style={{ marginLeft: 6, fontStyle: 'italic' }}>· edited {new Date(comment.updated_at).toLocaleString()}</span>
                                )}
                              </p>

                              {/* Reply Form */}
                              {replyingToCommentId === comment.id && currentUser && (
                                <div style={{ marginTop: '10px' }}>
                                  {showReplyStickerPicker && (
                                    <StickerPicker
                                      onSelect={(shortcode) => setReplyText(prev => prev + shortcode)}
                                      onClose={() => setShowReplyStickerPicker(false)}
                                      customStickers={globalCustomStickers}
                                      onStickerUploaded={() => {}}
                                    />
                                  )}
                                  <input type="file" ref={replyFileInputRef} style={{ display: 'none' }} accept="image/*"
                                    onChange={async (e) => {
                                      const file = e.target.files[0];
                                      if (!file) return;
                                      const formData = new FormData();
                                      formData.append('file', file);
                                      const token = localStorage.getItem(AUTH_TOKEN_KEY);
                                      const res = await fetch(`${API_URL}/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
                                      if (res.ok) { const data = await res.json(); setReplyText(prev => prev + ` [image:${data.url}]`); }
                                    }} />
                                  <form onSubmit={(e) => handleSubmitReply(e, comment.id)} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <button type="button" onClick={() => replyFileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '2px' }} title="Attach image">📎</button>
                                    <button type="button" onClick={() => setShowReplyStickerPicker(prev => !prev)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '2px' }} title="Stickers">🎨</button>
                                    <input
                                      type="text"
                                      value={replyText}
                                      onChange={(e) => setReplyText(e.target.value)}
                                      placeholder="Write a reply..."
                                      style={{ flex: 1, padding: '8px 12px', border: "1px solid var(--ui-border)", borderRadius: '15px', fontSize: '13px' }}
                                    />
                                    <button type="submit" disabled={submittingReply}
                                      style={{ padding: '8px 16px', background: btnColor, color: "var(--ui-on-accent)", border: "2px solid var(--ui-border)", borderRadius: '15px', cursor: 'pointer', fontSize: '13px', fontWeight: 900, textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                                      {submittingReply ? '...' : 'Post Reply'}
                                    </button>
                                  </form>
                                </div>
                              )}
                            </div>

                            {/* Render nested replies */}
                            {comment.replies && comment.replies.length > 0 && renderComments(comment.replies, level + 1)}
                          </div>
                        );})
                      ) : null}
                    </div>
                  );
                  return renderComments(comments);
                })()}

                {!currentUser && comments.length > 0 && (
                  <p style={{ color: "var(--ui-muted)", fontSize: '14px' }}>
                    <Link to={`${BASE_PATH}/login`} style={{ color: "var(--ui-accent)" }}>Log in</Link> to comment
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// User Card Component (reusable)
const UserCard = ({ user }) => {
  const profileUrl = user.username !== user.email ? `/${user.username}` : `/${user.id}`;

  return (
    <Link to={profileUrl} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="card" style={{ padding: '15px', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
        onMouseOut={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {user.profile_picture ? (
            <SocialImage
              src={user.profile_picture}
              alt="Profile"
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'linear-gradient(90deg, #f15722, #f78e1e, #fed206, #8ec74e, #49b863, #3fbced, #735ca8, #995aa4, #c263a7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '20px'
            }}>
              {(user.first_name || user.username || '?')[0].toUpperCase()}
            </div>
          )}
          <div>
            <p style={{ margin: 0, fontWeight: '500' }}>
              {user.first_name && user.last_name
                ? `${user.first_name} ${user.last_name}`
                : user.username !== user.email ? user.username : 'Anonymous'}
            </p>
            {user.username !== user.email && (
              <p style={{ margin: '5px 0 0', color: "var(--ui-muted)", fontSize: '14px' }}>@{user.username}</p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

// User Profile Page
const UserProfilePage = () => {
  const { identifier } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPost, setEditingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followCounts, setFollowCounts] = useState({ followingCount: 0, followersCount: 0 });
  const [showFollowModal, setShowFollowModal] = useState(null);
  const [followList, setFollowList] = useState([]);
  const [followListLoading, setFollowListLoading] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [profileTab, setProfileTab] = useState('posts');
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState(null);
  const [albumPosts, setAlbumPosts] = useState([]);
  const [albumMenuOpen, setAlbumMenuOpen] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const navigate = React.useCallback(() => { }, []);

  const startConversation = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId: profile.id })
      });

      if (response.ok) {
        window.location.href = `${BASE_PATH}/messages`;
      }
    } catch (err) {
      console.error('Failed to start conversation:', err);
    }
  };

  const fetchPosts = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/posts/user/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
  };

  const fetchSavedPosts = async (userId) => {
    setSavedLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/${userId}/saved-posts`);
      if (response.ok) {
        const data = await response.json();
        setSavedPosts(data);
      }
    } catch (err) {
      console.error('Failed to fetch saved posts:', err);
    } finally {
      setSavedLoading(false);
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await fetch(`${API_URL}/users/${identifier}`);
        if (!response.ok) {
          if (response.status === 404) throw new Error('User not found');
          throw new Error('Failed to load profile');
        }
        const data = await response.json();
        setProfile(data);
        setIsBlocked(!!data.is_blocked);
        setIsMuted(!!data.is_muted_by_you);
        fetchPosts(data.id);
        fetchFollowStatus(data.id);
        fetchFollowCounts(data.id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchFollowStatus = async (userId) => {
      try {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) return;
        const response = await fetch(`${API_URL}/users/${userId}/follow-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setFollowing(data.following);
        }
      } catch (err) {
        console.error('Failed to fetch follow status:', err);
      }
    };

    const fetchFollowCounts = async (userId) => {
      try {
        const response = await fetch(`${API_URL}/users/${userId}/follow-counts`);
        if (response.ok) {
          const data = await response.json();
          setFollowCounts(data);
        }
      } catch (err) {
        console.error('Failed to fetch follow counts:', err);
      }
    };

    fetchProfileData();
    setProfileTab('posts');
  }, [identifier]);

  useEffect(() => {
    if (profileTab === 'saved' && profile) {
      fetchSavedPosts(profile.id);
    }
    if (profileTab === 'albums' && profile) {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      fetch(`${API_URL}/users/${profile.id}/albums`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        const list = Array.isArray(data) ? data : [];
        setAlbums(list);
        if (list.length > 0 && !selectedAlbumId) setSelectedAlbumId(list[0].id);
      }).catch(() => setAlbums([]));
    }
  }, [profileTab, profile]);

  useEffect(() => {
    if (!selectedAlbumId) { setAlbumPosts([]); return; }
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    fetch(`${API_URL}/albums/${selectedAlbumId}/posts`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(data => setAlbumPosts(Array.isArray(data) ? data : [])).catch(() => setAlbumPosts([]));
  }, [selectedAlbumId]);

  useEffect(() => {
    if (profile) {
      const displayName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.username;
      document.title = `${displayName} (@${profile.username}) — Arawra`;
    }
    return () => { document.title = BASE_PATH === '/social' ? 'Arawra | Social' : 'Arawra'; };
  }, [profile]);

  const handleFollow = async () => {
    if (!currentUser) {
      window.location.href = `${BASE_PATH}/login`;
      return;
    }

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/users/${profile.id}/follow`, {
        method: following ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setFollowing(!following);
        setFollowCounts(prev => ({
          ...prev,
          followersCount: prev.followersCount + (following ? -1 : 1)
        }));
      }
    } catch (err) {
      console.error('Follow toggle failed:', err);
    }
  };

  useEffect(() => {
    if (!showFollowModal || !profile) return;
    setFollowListLoading(true);
    const endpoint = showFollowModal === 'followers'
      ? `/api/users/${profile.id}/followers`
      : `/api/users/${profile.id}/following`;
    fetch(`${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${endpoint}`)
      .then(r => r.json())
      .then(data => {
        if (!currentUser) { setFollowList(data); setFollowListLoading(false); return; }
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        Promise.all(data.map(async (u) => {
          if (u.id === currentUser.id) return { ...u, _following: null };
          try {
            const r2 = await fetch(`${API_URL}/users/${u.id}/follow-status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const d = await r2.json();
            return { ...u, _following: d.following };
          } catch { return { ...u, _following: false }; }
        })).then(enriched => { setFollowList(enriched); setFollowListLoading(false); });
      })
      .catch(() => { setFollowList([]); setFollowListLoading(false); });
  }, [showFollowModal, profile]);

  const handleFollowListToggle = async (userId) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const user = followList.find(u => u.id === userId);
      const isCurrentlyFollowing = user?._following;
      await fetch(`${API_URL}/users/${userId}/follow`, {
        method: isCurrentlyFollowing ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFollowList(prev => prev.map(u => u.id === userId ? { ...u, _following: !isCurrentlyFollowing } : u));
    } catch (err) {
      console.error('Follow toggle failed:', err);
    }
  };

  const handlePostCreated = () => {
    setEditingPost(null);
    setShowEditor(false);
    if (profile) fetchPosts(profile.id);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setPosts(posts.filter(p => p.id !== postId));
      }
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  if (loading) return <div className="container"><p>Loading profile...</p></div>;
  if (error) return <div className="container"><div className="error-message">{error}</div></div>;
  if (!profile) return <div className="container"><p>User not found</p></div>;

  const displayName = profile.first_name && profile.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile.username !== profile.email ? profile.username : 'Anonymous';

  const isOwner = currentUser && currentUser.id === profile.id;
  const canManageMembership = currentUser && (
    currentUser.role === 'admin' ||
    ['eve', 'evepanzarino', 'belonging'].includes(currentUser.username) ||
    ['eve', 'evepanzarino', 'belonging'].includes(currentUser.handle)
  );

  const handleBlock = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const method = isBlocked ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/users/${profile.id}/block`, {
        method, headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIsBlocked(!isBlocked);
        if (!isBlocked) { setFollowing(false); }
      }
    } catch (e) { console.error('Block toggle error:', e); }
  };

  const handleMute = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const method = isMuted ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/users/${profile.id}/user-mute`, {
        method, headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { setIsMuted(!isMuted); }
    } catch (e) { console.error('Mute toggle error:', e); }
  };

  const handleToggleMembership = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const newStatus = !profile.is_member;
      const res = await fetch(`${API_URL}/users/${profile.id}/member`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_member: newStatus })
      });
      if (res.ok) {
        setProfile(prev => ({ ...prev, is_member: newStatus }));
      }
    } catch (e) {
      console.error('Toggle membership error:', e);
    }
  };

  return (
    <div className="container">
      {/* Profile Header */}
      <div className="card profile-header-card" style={{ marginBottom: '20px', overflow: 'hidden' }}>
        {/* Banner */}
        <div className="profile-banner-wrap">
          {profile.banner_image
            ? <SocialImage src={profile.banner_image} alt="" className="profile-banner-img" />
            : <div className="profile-banner-placeholder" />
          }
        </div>

        {/* Avatar + info */}
        <div className="profile-header-body">
          <div className="profile-header-avatar-row">
            {profile.profile_picture
              ? <SocialImage src={profile.profile_picture} alt="Profile" className="profile-header-avatar" />
              : <div className="profile-header-avatar profile-header-avatar-placeholder">
                  {(profile.first_name || profile.username || '?')[0].toUpperCase()}
                </div>
            }
            <div className="profile-header-actions">
              {isOwner ? (
                <>
                  <Link to={`${BASE_PATH}/edit-profile`} className="btn btn-secondary">Edit Profile</Link>
                  <Link to={`${BASE_PATH}/settings`} className="btn btn-secondary">Settings</Link>
                </>
              ) : (
                <>
                  {currentUser && (
                    <button onClick={handleFollow} className={`btn ${following ? 'btn-secondary' : 'btn-primary'}`}>
                      {following ? '✓ Following' : '+ Follow'}
                    </button>
                  )}
                  {currentUser && (
                    <button onClick={startConversation} className="btn btn-secondary">💬 Message</button>
                  )}
                  {currentUser && (
                    <button onClick={handleBlock} className="btn" style={{
                      background: isBlocked ? '#e74c3c' : 'transparent',
                      color: isBlocked ? '#fff' : '#e74c3c',
                      border: isBlocked ? 'none' : '1px solid #e74c3c',
                      borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13
                    }}>
                      {isBlocked ? 'Unblock' : 'Block'}
                    </button>
                  )}
                  {currentUser && (
                    <button onClick={handleMute} className="btn" style={{
                      background: isMuted ? '#e67e22' : 'transparent',
                      color: isMuted ? '#fff' : '#e67e22',
                      border: isMuted ? 'none' : '1px solid #e67e22',
                      borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13
                    }}>
                      {isMuted ? 'Unmute' : 'Mute'}
                    </button>
                  )}
                  {currentUser && (
                    <button onClick={() => setReportTarget({ type: 'user', id: profile.id })} className="btn" style={{
                      background: 'transparent', color: "var(--ui-muted)", border: "1px solid var(--ui-border)",
                      borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontSize: 13
                    }} title="Report user">🚩</button>
                  )}
                  {canManageMembership && !isOwner && (
                    <button onClick={handleToggleMembership} style={{
                      background: profile.is_member ? '#ef4444' : '#22c55e',
                      color: '#fff', border: 'none', borderRadius: 6,
                      padding: '8px 16px', cursor: 'pointer', fontWeight: 600
                    }}>
                      {profile.is_member ? 'Revoke Member' : 'Grant Member'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <h2 className="profile-header-name">{displayName}</h2>
          {profile.username !== profile.email && (
            <p className="profile-header-username">@{profile.username}</p>
          )}
          {profile.is_member && (
            <span className="profile-member-badge">⭐ Member</span>
          )}

          {profile.bio && (
            <p style={{ color: "var(--ui-text)", fontSize: '14px', lineHeight: '1.5', marginTop: '10px', marginBottom: '4px', maxWidth: '500px', whiteSpace: 'pre-wrap' }}>{profile.bio}</p>
          )}

          <div className="profile-header-counts">
            <div style={{ cursor: 'pointer' }} onClick={() => setShowFollowModal('followers')}><span className="profile-count-num">{followCounts.followersCount || 0}</span><span className="profile-count-label">Followers</span></div>
            <div style={{ cursor: 'pointer' }} onClick={() => setShowFollowModal('following')}><span className="profile-count-num">{followCounts.followingCount || 0}</span><span className="profile-count-label">Following</span></div>
          </div>

          <TribeTags userId={profile.id} username={profile.username !== profile.email ? profile.username : null} />

          <p style={{ color: "var(--ui-muted)", fontSize: '13px', marginTop: '12px', marginBottom: 0 }}>
            Member since {new Date(profile.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Create Post Button (for owner) */}
      {isOwner && !showEditor && (
        <button
          onClick={() => setShowEditor(true)}
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: '20px' }}
        >
          + Create New Post
        </button>
      )}

      {/* Post Editor */}
      {showEditor && (
        <PostEditor
          onPostCreated={handlePostCreated}
          editPost={editingPost}
          onCancel={() => { setShowEditor(false); setEditingPost(null); }}
        />
      )}

      {/* Profile Tabs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
        {['posts', 'media', 'albums', 'saved'].map(tab => {
          const mediaPosts = posts.filter(p => p.content && (/<img\s(?!.*class="sticker-img")/i.test(p.content) || /<video\s/i.test(p.content)));
          const label = tab === 'posts' ? `Posts (${posts.length})` : tab === 'media' ? `Media (${mediaPosts.length})` : tab === 'albums' ? `Albums (${albums.length})` : `Saved (${savedPosts.length})`;
          return (
            <button key={tab} onClick={() => setProfileTab(tab)} style={{
              padding: '10px 20px', border: profileTab === tab ? "2px solid var(--ui-border)" : 'none',
              background: profileTab === tab ? "var(--ui-accent)" : "var(--ui-muted-surface)",
              color: profileTab === tab ? "var(--ui-on-accent)" : "var(--ui-muted)", cursor: 'pointer', borderRadius: 12,
              fontWeight: profileTab === tab ? 900 : 600, fontSize: 14,
              textShadow: profileTab === tab ? '0 1px 3px rgba(0,0,0,0.4)' : 'none',
            }}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Posts Tab */}
      {profileTab === 'posts' && (
        <div>
          {posts.length > 0 ? (
            <div className="profile-posts-grid">
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  isOwner={isOwner}
                  onEdit={handleEditPost}
                  onDelete={handleDeletePost}
                />
              ))}
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>
              {isOwner ? 'You haven\'t posted anything yet. Create your first post!' : 'No posts yet.'}
            </div>
          )}
        </div>
      )}

      {/* Media Tab */}
      {profileTab === 'media' && (() => {
        const mediaPosts = posts.filter(p => p.content && (/<img\s(?!.*class="sticker-img")/i.test(p.content) || /<video\s/i.test(p.content)));
        return (
          <div>
            {mediaPosts.length > 0 ? (
              <div className="profile-posts-grid">
                {mediaPosts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    isOwner={isOwner}
                    onEdit={handleEditPost}
                    onDelete={handleDeletePost}
                  />
                ))}
              </div>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>
                {isOwner ? 'No media yet. Upload images or videos in a post!' : 'No media.'}
              </div>
            )}
          </div>
        );
      })()}

      {/* Albums Tab */}
      {profileTab === 'albums' && (() => {
        const RAINBOW_COLORS = ['#f15722','#f78e1e','#fed306','#8ec74e','#49b863','#3fbced','#735ca8','#995aa4','#c263a7'];
        const selectedAlbum = albums.find(a => a.id === selectedAlbumId);
        const handleCreateAlbum = async () => {
          if (!newAlbumName.trim()) return;
          try {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            const res = await fetch(`${API_URL}/albums`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ name: newAlbumName.trim() })
            });
            if (res.ok) { const album = await res.json(); setAlbums(prev => [album, ...prev]); setSelectedAlbumId(album.id); setNewAlbumName(''); }
          } catch {}
        };
        const handleRenameAlbum = async () => {
          if (!selectedAlbum) return;
          const name = prompt('Rename album:', selectedAlbum.name);
          if (!name?.trim()) return;
          try {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            await fetch(`${API_URL}/albums/${selectedAlbum.id}`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ name: name.trim() })
            });
            setAlbums(prev => prev.map(a => a.id === selectedAlbum.id ? { ...a, name: name.trim() } : a));
          } catch {}
          setAlbumMenuOpen(false);
        };
        const handleDeleteAlbum = async () => {
          if (!selectedAlbum || !window.confirm(`Delete "${selectedAlbum.name}"?`)) return;
          try {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            await fetch(`${API_URL}/albums/${selectedAlbum.id}`, {
              method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
            });
            setAlbums(prev => prev.filter(a => a.id !== selectedAlbum.id));
            setSelectedAlbumId(null);
          } catch {}
          setAlbumMenuOpen(false);
        };
        const handleAlbumDescription = async () => {
          if (!selectedAlbum) return;
          const desc = prompt('Album description:', selectedAlbum.description || '');
          if (desc === null) return;
          try {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            await fetch(`${API_URL}/albums/${selectedAlbum.id}`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ description: desc.trim() })
            });
            setAlbums(prev => prev.map(a => a.id === selectedAlbum.id ? { ...a, description: desc.trim() } : a));
          } catch {}
          setAlbumMenuOpen(false);
        };
        return (
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {albums.map((a, i) => (
                <button key={a.id} onClick={() => setSelectedAlbumId(a.id)} style={{
                  padding: '10px 16px', borderRadius: 6, border: selectedAlbumId === a.id ? "2px solid var(--ui-border)" : '2px solid transparent',
                  background: RAINBOW_COLORS[i % RAINBOW_COLORS.length], color: '#fff', fontWeight: 900, fontSize: 14, cursor: 'pointer'
                }}>{a.name}</button>
              ))}
              {isOwner && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input value={newAlbumName} onChange={e => setNewAlbumName(e.target.value)} placeholder="New album..." style={{ padding: '8px 12px', borderRadius: 6, border: "1px solid var(--ui-border)", fontSize: 14 }} />
                  <button onClick={handleCreateAlbum} style={{ padding: '8px 16px', borderRadius: 6, background: "var(--ui-accent)", color: "var(--ui-on-accent)", border: 'none', fontWeight: 600, cursor: 'pointer' }}>Create</button>
                </div>
              )}
            </div>
            {selectedAlbum && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{selectedAlbum.name}</h3>
                {isOwner && (
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setAlbumMenuOpen(!albumMenuOpen)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: "var(--ui-muted)", fontWeight: 700 }}>···</button>
                    {albumMenuOpen && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, background: "var(--ui-surface)", border: "1px solid var(--ui-border)", borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: 160 }}>
                        <button onClick={handleRenameAlbum} style={{ display: 'block', width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 14 }}>Rename</button>
                        <button onClick={handleAlbumDescription} style={{ display: 'block', width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 14 }}>Description</button>
                        <button onClick={handleDeleteAlbum} style={{ display: 'block', width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 14, color: '#e0245e' }}>Delete</button>
                      </div>
                    )}
                  </div>
                )}
                {selectedAlbum.description && <span style={{ color: "var(--ui-muted)", fontSize: 14 }}>{selectedAlbum.description}</span>}
              </div>
            )}
            {selectedAlbumId && albumPosts.length > 0 ? (
              <div className="profile-posts-grid">
                {albumPosts.map(post => (
                  <PostCard key={post.id} post={post} isOwner={isOwner} onEdit={handleEditPost} onDelete={handleDeletePost} />
                ))}
              </div>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>
                {albums.length === 0 ? (isOwner ? 'No albums yet. Create one above!' : 'No albums.') : (selectedAlbumId ? 'No posts in this album.' : 'Select an album.')}
              </div>
            )}
          </div>
        );
      })()}

      {/* Saved Tab */}
      {profileTab === 'saved' && (
        <div>
          {savedLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>Loading saved posts...</div>
          ) : savedPosts.length > 0 ? (
            <div className="profile-posts-grid">
              {savedPosts.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>
              {isOwner ? 'No saved posts yet. Star a post to save it!' : 'No saved posts.'}
            </div>
          )}
        </div>
      )}

      {/* Followers/Following Modal */}
      {showFollowModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowFollowModal(null)}>
          <div style={{ background: "var(--ui-surface)", borderRadius: 16, width: '90%', maxWidth: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: "1px solid var(--ui-border)", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{showFollowModal === 'followers' ? 'Followers' : 'Following'}</h3>
              <button onClick={() => setShowFollowModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: "var(--ui-muted)" }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '8px 0' }}>
              {followListLoading ? (
                <p style={{ textAlign: 'center', padding: 20, color: "var(--ui-muted)" }}>Loading...</p>
              ) : followList.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 20, color: "var(--ui-muted)" }}>No {showFollowModal} yet</p>
              ) : followList.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 20px', gap: 12 }}>
                  <Link to={`${BASE_PATH}/${u.username}`} onClick={() => setShowFollowModal(null)}>
                    {u.profile_picture
                      ? <SocialImage src={u.profile_picture} alt="" style={{ width: 40, height: 40, borderRadius: 20, objectFit: 'cover' }} />
                      : <div style={{ width: 40, height: 40, borderRadius: 20, background: "var(--ui-accent)", display: 'flex', alignItems: 'center', justifyContent: 'center', color: "var(--ui-on-accent)", fontWeight: 700 }}>{(u.first_name || u.username || '?')[0].toUpperCase()}</div>
                    }
                  </Link>
                  <Link to={`${BASE_PATH}/${u.username}`} onClick={() => setShowFollowModal(null)} style={{ flex: 1, textDecoration: 'none' }}>
                    <div style={{ fontWeight: 600, color: "var(--ui-text)", fontSize: 14 }}>{u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.username}</div>
                    <div style={{ color: "var(--ui-muted)", fontSize: 13 }}>@{u.username}</div>
                  </Link>
                  {currentUser && u._following !== null && u.id !== currentUser.id && (
                    <button onClick={() => handleFollowListToggle(u.id)} className={`btn ${u._following ? 'btn-secondary' : 'btn-primary'}`} style={{ padding: '6px 14px', fontSize: 13 }}>
                      {u._following ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {reportTarget && <ReportModal type={reportTarget.type} id={reportTarget.id} onClose={() => setReportTarget(null)} />}
    </div>
  );
};

// Create Feed Modal
const CreateFeedModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('Custom Feed');
  const [tribes, setTribes] = useState([]);
  const [friends, setFriends] = useState([]);
  const [selectedTribes, setSelectedTribes] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [tribeSearch, setTribeSearch] = useState('');
  const [friendSearch, setFriendSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const h = { 'Authorization': `Bearer ${token}` };
    fetch(`${window.location.origin}${BASE_PATH}/api/tribes`, { headers: h })
      .then(r => r.json()).then(setTribes).catch(() => {});
    fetch(`${window.location.origin}${BASE_PATH}/api/users/me/following`, { headers: h })
      .then(r => r.json()).then(setFriends).catch(() => {});
  }, []);

  const addTribe = (id) => setSelectedTribes(prev => prev.includes(id) ? prev : [...prev, id]);
  const removeTribe = (id) => setSelectedTribes(prev => prev.filter(x => x !== id));
  const addFriend = (id) => setSelectedFriends(prev => prev.includes(id) ? prev : [...prev, id]);
  const removeFriend = (id) => setSelectedFriends(prev => prev.filter(x => x !== id));

  const filteredTribes = tribeSearch.trim()
    ? tribes.filter(t => t.tag.toLowerCase().includes(tribeSearch.toLowerCase()) && !selectedTribes.includes(t.id))
    : tribes.filter(t => !selectedTribes.includes(t.id)).slice(0, 3);

  const filteredFriends = friendSearch.trim()
    ? friends.filter(f => (f.username || '').toLowerCase().includes(friendSearch.toLowerCase()) && !selectedFriends.includes(f.id))
    : friends.filter(f => !selectedFriends.includes(f.id)).slice(0, 3);

  const handleSave = async () => {
    if (!selectedTribes.length && !selectedFriends.length) { setModalError('Pick at least one tribe or friend'); return; }
    setSaving(true);
    setModalError('');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${window.location.origin}${BASE_PATH}/api/feeds`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'Custom Feed', tribe_ids: selectedTribes, friend_ids: selectedFriends })
      });
      if (!res.ok) { const d = await res.json(); setModalError(d.error || 'Failed to create'); return; }
      onCreated(await res.json());
      onClose();
    } catch { setModalError('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-feed-modal" onClick={e => e.stopPropagation()}>
        <h3>Feed Title</h3>
        <input
          className="create-feed-name-input"
          type="text"
          placeholder="Custom Feed"
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
        />

        {/* Tribes */}
        <div className="create-feed-section">
          <p className="create-feed-section-label">Tribes</p>
          {selectedTribes.length > 0 && (
            <div className="create-feed-chips">
              {selectedTribes.map(id => {
                const t = tribes.find(x => x.id === id);
                if (!t) return null;
                return (
                  <span key={id} className="create-feed-chip">
                    <TribeIcon icon={t.icon} size={14} />
                    #{t.tag}
                    <button className="create-feed-chip-remove" onClick={() => removeTribe(id)}>×</button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="create-feed-search-wrap">
            <input
              className="create-feed-search-input"
              type="text"
              placeholder="Search tribes…"
              value={tribeSearch}
              onChange={e => setTribeSearch(e.target.value)}
            />
            {filteredTribes.length > 0 && (
              <div className="create-feed-suggestions">
                {filteredTribes.map(t => (
                  <button key={t.id} className="create-feed-suggestion" onClick={() => { addTribe(t.id); setTribeSearch(''); }}>
                    <TribeIcon icon={t.icon} size={16} />
                    <span>#{t.tag}</span>
                  </button>
                ))}
              </div>
            )}
            {tribeSearch.trim() && filteredTribes.length === 0 && (
              <p className="create-feed-no-results">No tribes match "{tribeSearch}"</p>
            )}
          </div>
        </div>

        {/* Friends */}
        <div className="create-feed-section">
          <p className="create-feed-section-label">Friends</p>
          {selectedFriends.length > 0 && (
            <div className="create-feed-chips">
              {selectedFriends.map(id => {
                const f = friends.find(x => x.id === id);
                if (!f) return null;
                return (
                  <span key={id} className="create-feed-chip">
                    {f.profile_picture
                      ? <SocialImage src={f.profile_picture} alt="" className="create-feed-chip-avatar" />
                      : <span className="create-feed-chip-avatar-placeholder">{(f.first_name || f.username || '?')[0].toUpperCase()}</span>
                    }
                    @{f.username}
                    <button className="create-feed-chip-remove" onClick={() => removeFriend(id)}>×</button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="create-feed-search-wrap">
            <input
              className="create-feed-search-input"
              type="text"
              placeholder={friends.length === 0 ? 'Follow people to add them…' : 'Search friends…'}
              value={friendSearch}
              onChange={e => setFriendSearch(e.target.value)}
              disabled={friends.length === 0}
            />
            {filteredFriends.length > 0 && (
              <div className="create-feed-suggestions">
                {filteredFriends.map(f => (
                  <button key={f.id} className="create-feed-suggestion" onClick={() => { addFriend(f.id); setFriendSearch(''); }}>
                    {f.profile_picture
                      ? <SocialImage src={f.profile_picture} alt="" className="create-feed-avatar" />
                      : <span className="create-feed-avatar-placeholder">{(f.first_name || f.username || '?')[0].toUpperCase()}</span>
                    }
                    <span>@{f.username}</span>
                  </button>
                ))}
              </div>
            )}
            {friendSearch.trim() && filteredFriends.length === 0 && (
              <p className="create-feed-no-results">No friends match "{friendSearch}"</p>
            )}
          </div>
        </div>

        {modalError && <p className="create-feed-error">{modalError}</p>}

        <div className="create-feed-actions">
          <button className="create-feed-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create Feed'}
          </button>
          <button className="create-feed-btn-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Feed Page (all posts)
const FeedPage = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('global');
  const [editingPost, setEditingPost] = useState(null);
  const [customFeeds, setCustomFeeds] = useState([]);
  const [showCreateFeed, setShowCreateFeed] = useState(false);
  const [trendsData, setTrendsData] = useState(null);
  const [trendsDays, setTrendsDays] = useState(7);
  const [myTribes, setMyTribes] = useState([]);
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0, mutuals: 0 });
  const [mutuals, setMutuals] = useState([]);
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] });
  const [friendName, setFriendName] = useState('');
  const [friendStatus, setFriendStatus] = useState('');
  const friendActorToken = localStorage.getItem(AUTH_TOKEN_KEY);
  const [friendRowsToken, setFriendRowsToken] = useState(null);
  const visibleFriends = friendRowsToken === friendActorToken ? mutuals : [];
  const visibleFriendRequests = friendRowsToken === friendActorToken ? friendRequests : { incoming: [], outgoing: [] };
  const [onlineStatus, setOnlineStatus] = useState({});
  const [showFollowModal, setShowFollowModal] = useState(null); // 'followers' | 'following' | 'mutuals'
  const [followModalUsers, setFollowModalUsers] = useState([]);
  const feedTabsScrollRef = useRef(null);
  const feedTabsTrackRef = useRef(null);
  const feedTabsThumbRef = useRef(null);
  const [tabsOverflow, setTabsOverflow] = useState(false);
  const tabsMetaRef = useRef({ thumbWidth: 0 });

  const updateThumbDOM = useCallback(() => {
    const el = feedTabsScrollRef.current;
    const thumb = feedTabsThumbRef.current;
    if (!el || !thumb) return;
    const tw = Math.max(30, (el.clientWidth / el.scrollWidth) * 100);
    const ratio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    tabsMetaRef.current.thumbWidth = tw;
    thumb.style.width = tw + '%';
    thumb.style.left = (ratio * (100 - tw)) + '%';
  }, []);

  useEffect(() => {
    const el = feedTabsScrollRef.current;
    if (!el) return;
    const onScroll = () => { updateThumbDOM(); };
    const preventWheel = (e) => { e.preventDefault(); };
    el.addEventListener('scroll', onScroll);
    el.addEventListener('wheel', preventWheel, { passive: false });
    const ro = new ResizeObserver(() => {
      const overflow = el.scrollWidth > el.clientWidth + 1;
      setTabsOverflow(overflow);
      updateThumbDOM();
    });
    ro.observe(el);
    setTabsOverflow(el.scrollWidth > el.clientWidth + 1);
    updateThumbDOM();
    return () => { el.removeEventListener('scroll', onScroll); el.removeEventListener('wheel', preventWheel); ro.disconnect(); };
  }, [updateThumbDOM, customFeeds]);

  const scrollTabsBy = (dir) => {
    const el = feedTabsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 150, behavior: 'smooth' });
  };

  const handleTrackMouseDown = (e) => {
    const track = feedTabsTrackRef.current;
    const el = feedTabsScrollRef.current;
    if (!track || !el) return;
    e.preventDefault();
    const rect = track.getBoundingClientRect();
    const tw = tabsMetaRef.current.thumbWidth;
    const thumbW = (tw / 100) * rect.width;
    const startScroll = el.scrollLeft;
    let startX = e.clientX;
    // If clicked outside thumb, jump to position
    const ratio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    const thumbLeft = ratio * (rect.width - thumbW);
    const clickX = e.clientX - rect.left;
    if (clickX < thumbLeft || clickX > thumbLeft + thumbW) {
      const jumpRatio = Math.max(0, Math.min(1, (clickX - thumbW / 2) / (rect.width - thumbW)));
      el.scrollLeft = jumpRatio * (el.scrollWidth - el.clientWidth);
      startX = e.clientX;
    }
    const dragStartScroll = el.scrollLeft;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const scrollRange = el.scrollWidth - el.clientWidth;
      const trackRange = rect.width - thumbW;
      el.scrollLeft = dragStartScroll + (dx / trackRange) * scrollRange;
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const fetchCustomFeeds = async () => {
    if (!currentUser) return;
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${window.location.origin}${BASE_PATH}/api/feeds`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setCustomFeeds(await res.json());
    } catch { /* ignore */ }
  };

  const fetchPosts = async () => {
    try {
      if (activeTab === 'trends') {
        const res = await fetch(`${BASE_PATH}/api/trends?days=${trendsDays}`, { credentials: 'include' });
        const d = await res.json();
        setTrendsData(d);
        setPosts(d.posts || []);
      } else {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const response = await fetch(`${API_URL}/posts?tab=${activeTab}`, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        });
        if (!response.ok) throw new Error('Failed to fetch posts');
        setPosts(await response.json());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch sidebar data
  const fetchSidebarData = async () => {
    if (!currentUser) return;
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const [tribesRes, countsRes, mutualsCountRes, mutualsRes, friendsRes] = await Promise.all([
        fetch(`${API_URL}/my-tribes`, { headers }),
        fetch(`${API_URL}/users/${currentUser.id}/follow-counts`),
        fetch(`${API_URL}/users/${currentUser.id}/mutuals-count`),
        fetch(`${API_URL}/users/${currentUser.id}/mutuals`),
        fetch(`${API_URL}/friends`, { headers })
      ]);
      if (localStorage.getItem(AUTH_TOKEN_KEY) !== token) return;
      if (tribesRes.ok) setMyTribes(await tribesRes.json());
      if (countsRes.ok) {
        const c = await countsRes.json();
        const mc = mutualsCountRes.ok ? await mutualsCountRes.json() : { count: 0 };
        setFollowCounts({ followers: c.followers, following: c.following, mutuals: mc.count });
      }
      if (mutualsRes.ok) { const data = await mutualsRes.json(); if (localStorage.getItem(AUTH_TOKEN_KEY) === token) { setFriendRowsToken(token); setMutuals(data); } }
      if (friendsRes.ok) {
        const data = await friendsRes.json();
        if (localStorage.getItem(AUTH_TOKEN_KEY) !== token) return;
        setFriendRowsToken(token); setMutuals(data.friends || []); setFriendRequests({ incoming: data.incoming || [], outgoing: data.outgoing || [] });
      }
    } catch { /* ignore */ }
  };

  const changeFriend = async (action, body) => {
    const token = friendActorToken;
    if (localStorage.getItem(AUTH_TOKEN_KEY) !== token) return;
    try {
      const response = await fetch(`${API_URL}/friends/${action}`, {
        method: body === null ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        ...(body === null ? {} : { body: JSON.stringify(body) }),
      });
      const result = await response.json();
      if (localStorage.getItem(AUTH_TOKEN_KEY) !== token) return;
      if (!response.ok) throw new Error(result.error || 'Could not update friends');
      setFriendStatus(result.status === 'sent' ? 'Friend request sent.' : 'Friends updated.');
      setFriendName(''); await fetchSidebarData();
    } catch (error) { if (localStorage.getItem(AUTH_TOKEN_KEY) === token) setFriendStatus(error.message || 'Could not update friends'); }
  };

  // Poll online status for mutuals
  const fetchOnlineStatus = async () => {
    if (!mutuals.length) return;
    try {
      const res = await fetch(`${API_URL}/users/online-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: mutuals.map(u => u.id) })
      });
      if (res.ok) setOnlineStatus(await res.json());
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchCustomFeeds(); }, [currentUser]);
  useEffect(() => { setFriendRowsToken(null); setMutuals([]); setFriendRequests({ incoming: [], outgoing: [] }); setFriendStatus(''); fetchSidebarData(); }, [currentUser]);
  useEffect(() => { fetchOnlineStatus(); const iv = setInterval(fetchOnlineStatus, 30000); return () => clearInterval(iv); }, [mutuals]);
  useEffect(() => { fetchPosts(); }, [activeTab, trendsDays]);

  const openFollowModal = async (type) => {
    setShowFollowModal(type);
    try {
      let url;
      if (type === 'followers') url = `${API_URL}/users/${currentUser.id}/followers`;
      else if (type === 'following') url = `${API_URL}/users/${currentUser.id}/following`;
      else url = `${API_URL}/users/${currentUser.id}/mutuals`;
      const res = await fetch(url);
      if (res.ok) {
        const users = await res.json();
        // Check follow status for each user
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const statusChecks = await Promise.all(users.map(u =>
          fetch(`${API_URL}/users/${u.id}/follow-status`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : { following: false })
            .catch(() => ({ following: false }))
        ));
        setFollowModalUsers(users.map((u, i) => ({ ...u, _following: statusChecks[i].following })));
      }
    } catch { /* ignore */ }
  };

  const handleFollowModalToggle = async (userId) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const user = followModalUsers.find(u => u.id === userId);
      const isFollowing = user?._following;
      await fetch(`${API_URL}/users/${userId}/follow`, {
        method: isFollowing ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFollowModalUsers(prev => prev.map(u => u.id === userId ? { ...u, _following: !isFollowing } : u));
    } catch { /* ignore */ }
  };

  const handlePostCreated = () => { setEditingPost(null); fetchPosts(); };

  const handleEditPost = (post) => {
    setEditingPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setPosts(posts.filter(p => p.id !== postId));
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  const handleDeleteFeed = async (feedId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this custom feed?')) return;
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    await fetch(`${window.location.origin}${BASE_PATH}/api/feeds/${feedId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setCustomFeeds(prev => prev.filter(f => f.id !== feedId));
    if (activeTab === `feed_${feedId}`) setActiveTab('global');
  };

  const handleFeedCreated = (feed) => {
    setCustomFeeds(prev => [...prev, feed]);
    setActiveTab(`feed_${feed.id}`);
  };

  if (loading) return <div className="container"><p>Loading feed...</p></div>;
  if (error) return <div className="container"><div className="error-message">{error}</div></div>;

  const isCustomTab = activeTab.startsWith('feed_');

  return (
    <div className="feed-layout">
      {/* Follow List Modal */}
      {showFollowModal && (
        <div className="modal-overlay" onClick={() => setShowFollowModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div style={{ padding: '16px 20px', borderBottom: "1px solid var(--ui-border)", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{showFollowModal === 'followers' ? 'Followers' : showFollowModal === 'following' ? 'Following' : 'Mutuals'}</h3>
              <button onClick={() => setShowFollowModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: "var(--ui-muted)" }}>×</button>
            </div>
            <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
              {followModalUsers.length === 0 ? (
                <p style={{ padding: 20, textAlign: 'center', color: "var(--ui-muted)" }}>No {showFollowModal} yet.</p>
              ) : followModalUsers.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 20px', gap: 12 }}>
                  <Link to={`${BASE_PATH}/${u.username}`} onClick={() => setShowFollowModal(null)}>
                    {u.profile_picture
                      ? <SocialImage src={u.profile_picture} alt="" style={{ width: 40, height: 40, borderRadius: 20, objectFit: 'cover' }} />
                      : <div style={{ width: 40, height: 40, borderRadius: 20, background: "var(--ui-accent)", display: 'flex', alignItems: 'center', justifyContent: 'center', color: "var(--ui-on-accent)", fontWeight: 700 }}>{(u.first_name || u.username || '?')[0].toUpperCase()}</div>
                    }
                  </Link>
                  <Link to={`${BASE_PATH}/${u.username}`} onClick={() => setShowFollowModal(null)} style={{ flex: 1, textDecoration: 'none' }}>
                    <div style={{ fontWeight: 600, color: "var(--ui-text)", fontSize: 14 }}>{u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.username}</div>
                    <div style={{ color: "var(--ui-muted)", fontSize: 13 }}>@{u.username}</div>
                  </Link>
                  {u.id !== currentUser.id && (
                    <button onClick={() => handleFollowModalToggle(u.id)} className={`btn ${u._following ? 'btn-secondary' : 'btn-primary'}`} style={{ padding: '6px 14px', fontSize: 13 }}>
                      {u._following ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showCreateFeed && (
        <CreateFeedModal onClose={() => setShowCreateFeed(false)} onCreated={handleFeedCreated} />
      )}

      {/* Left Sidebar */}
      {currentUser && (
        <aside className="feed-sidebar feed-sidebar-left">
          {/* Profile Card */}
          <div className="sidebar-card sidebar-profile-card" onClick={() => navigate(`${BASE_PATH}/${currentUser.username}`)} style={{ cursor: 'pointer' }}>
            <SocialImage
              src={currentUser.profile_picture || `${BASE_PATH}/images/profile.svg`}
              alt=""
              className="sidebar-profile-avatar"
            />
            <div className="sidebar-profile-info">
              <span className="sidebar-profile-name">{currentUser.first_name} {currentUser.last_name}</span>
              <span className="sidebar-profile-username">@{currentUser.username}</span>
            </div>
          </div>

          {/* Follow Counts */}
          <div className="sidebar-card sidebar-follow-counts">
            <button className="sidebar-count-btn" onClick={(e) => { e.stopPropagation(); openFollowModal('followers'); }}>
              <span className="sidebar-count-num">{followCounts.followers}</span>
              <span className="sidebar-count-label">Followers</span>
            </button>
            <button className="sidebar-count-btn" onClick={(e) => { e.stopPropagation(); openFollowModal('following'); }}>
              <span className="sidebar-count-num">{followCounts.following}</span>
              <span className="sidebar-count-label">Following</span>
            </button>
            <button className="sidebar-count-btn" onClick={(e) => { e.stopPropagation(); openFollowModal('mutuals'); }}>
              <span className="sidebar-count-num">{followCounts.mutuals}</span>
              <span className="sidebar-count-label">Mutuals</span>
            </button>
          </div>

          {/* Tribes */}
          {myTribes.length > 0 && (
            <div className="sidebar-card">
              <h4 className="sidebar-section-title">Tribes</h4>
              <div className="sidebar-tribes-list">
                {myTribes.map(tribe => (
                  <div key={tribe.id} className="sidebar-tribe-item" onClick={() => navigate(`${BASE_PATH}/tribe/${tribe.tag}`)}>
                    <TribeIcon icon={tribe.icon} size={24} />
                    <span className="sidebar-tribe-name">@{tribe.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      )}

      {/* Main Feed */}
      <main className="feed-main">
        {/* Feed Tabs */}
        <div className="feed-tabs-wrapper">
          {tabsOverflow && <button className="feed-tabs-arrow feed-tabs-arrow-left" onClick={() => scrollTabsBy(-1)}>&lsaquo;</button>}
          <div className="feed-tabs-row" ref={feedTabsScrollRef}>
            {(() => {
              const rainbowColors = ['#f15722','#f78e1e','#fed306','#8ec74e','#49b863','#3fbced','#735ca8','#995aa4','#c263a7'];
              const tabs = [
                { key: 'global', label: 'Global', active: activeTab === 'global', onClick: () => setActiveTab('global') },
                ...(currentUser ? [{ key: 'personal', label: 'Mutuals', active: activeTab === 'personal', onClick: () => setActiveTab('personal') }] : []),
                { key: 'trends', label: 'Trends', active: activeTab === 'trends', onClick: () => setActiveTab('trends') },
                { key: 'updates', label: 'Updates', active: activeTab === 'updates', onClick: () => setActiveTab('updates') },
                ...customFeeds.map(feed => ({ key: `feed_${feed.id}`, label: feed.name, active: activeTab === `feed_${feed.id}`, onClick: () => setActiveTab(`feed_${feed.id}`), feedId: feed.id }))
              ];
              return tabs.map((tab, i) => (
                <button
                  key={tab.key}
                  className={`feed-tab${tab.active ? ' active' : ''}${tab.feedId ? ' feed-tab-custom' : ''}`}
                  onClick={tab.onClick}
                  style={{ backgroundColor: rainbowColors[i % rainbowColors.length], color: '#fff', borderColor: tab.active ? "var(--ui-border)" : 'transparent' }}
                >
                  {tab.label}
                  {tab.feedId && <span className="feed-tab-delete" onClick={(e) => handleDeleteFeed(tab.feedId, e)} title="Delete feed">×</span>}
                </button>
              ));
            })()}
            {currentUser && (
              <button className="feed-tab feed-tab-plus" onClick={() => setShowCreateFeed(true)} title="Create custom feed">+</button>
            )}
          </div>
          {tabsOverflow && <button className="feed-tabs-arrow feed-tabs-arrow-right" onClick={() => scrollTabsBy(1)}>&rsaquo;</button>}
          {tabsOverflow && (
            <div className="feed-tabs-scrollbar" ref={feedTabsTrackRef} onMouseDown={handleTrackMouseDown}>
              <div
                className="feed-tabs-scrollbar-thumb"
                ref={feedTabsThumbRef}
              />
            </div>
          )}
        </div>

        {/* Post Editor */}
        {currentUser && !isCustomTab && activeTab !== 'trends' && (activeTab !== 'updates' || currentUser.username === 'belonging') && (
          <div className="feed-compose-section">
            <PostEditor
              onPostCreated={handlePostCreated}
              editPost={editingPost}
              onCancel={() => { setEditingPost(null); }}
            />
          </div>
        )}

        {/* Trends period selector */}
        {activeTab === 'trends' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[[1,'Today'],[7,'Week'],[30,'Month']].map(([d, label]) => (
              <button key={d} className={`btn${trendsDays === d ? ' active' : ''}`} style={{ fontSize: 13, padding: '6px 16px', borderRadius: 20 }} onClick={() => setTrendsDays(d)}>{label}</button>
            ))}
          </div>
        )}

        {/* Trends word cloud */}
        {activeTab === 'trends' && trendsData?.words?.length > 0 && (
          <div className="trends-word-cloud" style={{ marginBottom: 16 }}>
            {trendsData.words.map(({ word, score, count }) => {
              const maxScore = trendsData.words[0]?.score || 1;
              return (
                <span key={word} className="trend-word" style={{ fontSize: `${(1 + (score / maxScore) * 2).toFixed(2)}rem` }} title={`${count} posts`}>{word}</span>
              );
            })}
          </div>
        )}

        {/* Posts grid */}
        <div className="feed-grid">
          {posts.length > 0 ? (
            posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                isOwner={currentUser && (currentUser.id === post.user_id || currentUser.role === 'admin')}
                onEdit={handleEditPost}
                onDelete={handleDeletePost}
              />
            ))
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>
              {activeTab === 'updates' ? 'No updates yet.' : activeTab === 'trends' ? 'No trending posts yet.' : 'No posts yet. Be the first to post!'}
            </div>
          )}
        </div>
      </main>

      {/* Right Sidebar - Friends Online */}
      {currentUser && (
        <aside className="feed-sidebar feed-sidebar-right">
          <div className="sidebar-card">
            <h4 className="sidebar-section-title">Friends</h4>
            <form onSubmit={event => { event.preventDefault(); if (friendName.trim()) changeFriend('request', { username: friendName.trim() }); }} style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input aria-label="Friend username" value={friendName} onChange={event => setFriendName(event.target.value)} placeholder="Username" style={{ minWidth: 0, flex: 1 }} />
              <button type="submit">Add</button>
            </form>
            {friendStatus && <p role="status" style={{ fontSize: 12 }}>{friendStatus}</p>}
            {visibleFriendRequests.incoming.map(request => (
              <div key={request.requestID} style={{ marginBottom: 10 }}>
                <span>@{request.username}</span>
                <button type="button" onClick={() => changeFriend('respond', { requestID: request.requestID, accept: true })}>Accept</button>
                <button type="button" onClick={() => changeFriend('respond', { requestID: request.requestID, accept: false })}>Decline</button>
              </div>
            ))}
            {visibleFriendRequests.outgoing.map(request => <p key={request.requestID} style={{ fontSize: 12 }}>Request sent to @{request.username}</p>)}
            <div className="sidebar-friends-list">
              {visibleFriends.length === 0 ? (
                <p style={{ color: "var(--ui-muted)", fontSize: 13, padding: '8px 0' }}>No friends yet.</p>
              ) : visibleFriends.map(u => (
                <div key={u.id} className="sidebar-friend-item" onClick={() => navigate(`${BASE_PATH}/${u.username}`)}>
                  <div className="sidebar-avatar-wrap">
                    <SocialImage src={u.profile_picture || `${BASE_PATH}/images/profile.svg`} alt="" className="sidebar-avatar" />
                    <span className={`sidebar-status-dot ${onlineStatus[u.id] ? 'online' : 'offline'}`} />
                  </div>
                  <span className="sidebar-friend-name">{u.first_name || u.username}</span>
                  <button type="button" aria-label={`Remove ${u.username} from friends`} onClick={event => { event.stopPropagation(); changeFriend(String(u.id), null); }}>Remove</button>
                </div>
              ))}
            </div>
          </div>
        </aside>
      )}
    </div>
  );
};

// Users Page
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_URL}/users`);
        if (!response.ok) throw new Error('Failed to fetch users');
        const data = await response.json();
        setUsers(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  if (loading) return <div className="container"><p>Loading users...</p></div>;
  if (error) return <div className="container"><p className="error-message">{error}</p></div>;

  return (
    <div className="container">
      <h2>Users</h2>
      <div style={{ display: 'grid', gap: '15px', marginTop: '20px' }}>
        {users.map(user => (
          <UserCard key={user.id} user={user} />
        ))}
        {users.length === 0 && <p>No users found.</p>}
      </div>
    </div>
  );
};

// Search Page
const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [tab, setTab] = useState('users');
  const [loading, setLoading] = useState(false);

  // Results
  const [users, setUsers] = useState([]);
  const [tribes, setTribes] = useState([]);
  const [posts, setPosts] = useState([]);
  const [shops, setShops] = useState([]);

  // User filters
  const [showFilters, setShowFilters] = useState(false);
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [identity, setIdentity] = useState('');
  const [orientation, setOrientation] = useState('');
  const [tribeQuery, setTribeQuery] = useState('');
  const [tribeSuggestions, setTribeSuggestions] = useState([]);
  const [selectedTribes, setSelectedTribes] = useState([]);

  useEffect(() => {
    if (searchParams.get('q')) {
      doSearch(searchParams.get('q'), 'users');
    }
  }, []);

  const doSearch = async (q, currentTab) => {
    const activeTab = currentTab || tab;
    setLoading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      if (activeTab === 'users') {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (minAge) params.set('min_age', minAge);
        if (maxAge) params.set('max_age', maxAge);
        if (identity) params.set('identity', identity);
        if (orientation) params.set('orientation', orientation);
        if (selectedTribes.length > 0) params.set('tribe_ids', selectedTribes.map(t => t.id).join(','));
        const res = await fetch(`${API_URL}/users/search?${params}`, { headers });
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : []);
      } else if (activeTab === 'tribes') {
        const res = await fetch(`${API_URL}/tribes/search?q=${encodeURIComponent(q || '')}`, { headers });
        const data = await res.json();
        setTribes(Array.isArray(data) ? data : []);
      } else if (activeTab === 'posts') {
        const res = await fetch(`${API_URL}/posts/search?q=${encodeURIComponent(q || '')}`, { headers });
        const data = await res.json();
        setPosts(Array.isArray(data) ? data : []);
      } else if (activeTab === 'shop') {
        const res = await fetch(`${API_URL}/shops?q=${encodeURIComponent(q || '')}`, { headers });
        const data = await res.json();
        setShops(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTribeAutocomplete = async (text) => {
    setTribeQuery(text);
    if (text.length < 1) { setTribeSuggestions([]); return; }
    try {
      const res = await fetch(`${API_URL}/tribes/search/autocomplete?q=${encodeURIComponent(text)}`);
      const data = await res.json();
      const selectedIds = new Set(selectedTribes.map(t => t.id));
      setTribeSuggestions((data || []).filter(t => !selectedIds.has(t.id)));
    } catch {
      setTribeSuggestions([]);
    }
  };

  const addTribeFilter = (tribe) => {
    setSelectedTribes(prev => [...prev, tribe]);
    setTribeQuery('');
    setTribeSuggestions([]);
  };

  const removeTribeFilter = (tribeId) => {
    setSelectedTribes(prev => prev.filter(t => t.id !== tribeId));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSearch(query);
  };

  const switchTab = (newTab) => {
    setTab(newTab);
    if (query) doSearch(query, newTab);
  };

  const tabStyle = (isActive, idx = 0) => ({
    padding: '8px 16px',
    border: isActive ? "2px solid var(--ui-border)" : 'none',
    background: isActive ? "var(--ui-accent)" : "var(--ui-muted-surface)",
    color: isActive ? "var(--ui-on-accent)" : "var(--ui-muted)",
    cursor: 'pointer',
    borderRadius: '8px',
    fontWeight: isActive ? 900 : '600',
    fontSize: '13px',
    textShadow: isActive ? '0 1px 3px rgba(0,0,0,0.4)' : 'none',
  });

  return (
    <div className="container">
      <h2>Search</h2>
      <form onSubmit={handleSubmit} style={{ marginTop: '16px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            style={{ flex: 1 }}
            autoFocus
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', marginBottom: '16px' }}>
        {['users', 'tribes', 'posts', 'shop'].map((t, i) => (
          <button key={t} style={tabStyle(tab === t, i)} onClick={() => switchTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* User filters */}
      {tab === 'users' && (
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{ background: 'none', border: 'none', color: "var(--ui-accent)", cursor: 'pointer', fontWeight: '600', fontSize: '13px', padding: '4px 0' }}
          >
            {showFilters ? 'Hide Filters ▲' : 'Filters ▼'}
          </button>

          {showFilters && (
            <div style={{ background: "var(--ui-surface)", borderRadius: '12px', padding: '16px', border: "1px solid var(--ui-border)", marginTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <input type="number" value={minAge} onChange={e => setMinAge(e.target.value)} placeholder="Min age" />
                <input type="number" value={maxAge} onChange={e => setMaxAge(e.target.value)} placeholder="Max age" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <input type="text" value={identity} onChange={e => setIdentity(e.target.value)} placeholder="Gender identity" />
                <input type="text" value={orientation} onChange={e => setOrientation(e.target.value)} placeholder="Sexual orientation" />
              </div>

              {/* Tribe multi-select */}
              <div style={{ position: 'relative', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={tribeQuery}
                  onChange={e => handleTribeAutocomplete(e.target.value)}
                  placeholder="Search tribes to filter by..."
                />
                {tribeSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: "var(--ui-surface)", border: "1px solid var(--ui-border)", borderRadius: '8px', zIndex: 10, maxHeight: '200px', overflow: 'auto' }}>
                    {tribeSuggestions.map(t => (
                      <div
                        key={t.id}
                        onClick={() => addTribeFilter(t)}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: "1px solid var(--ui-border)", fontSize: '13px' }}
                        onMouseEnter={e => e.target.style.background = "var(--ui-muted-surface)"}
                        onMouseLeave={e => e.target.style.background = "var(--ui-surface)"}
                      >
                        {t.name} <span style={{ color: "var(--ui-muted)" }}>/{t.tag}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedTribes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {selectedTribes.map(t => (
                    <span
                      key={t.id}
                      onClick={() => removeTribeFilter(t.id)}
                      style={{
                        background: t.color || "var(--ui-accent)", color: t.color ? '#fff' : 'var(--ui-on-accent)', padding: '3px 10px',
                        borderRadius: '12px', fontSize: '12px', cursor: 'pointer',
                      }}
                    >
                      {t.name} ×
                    </span>
                  ))}
                </div>
              )}

              <button className="btn btn-primary" onClick={() => doSearch(query)} style={{ width: '100%' }}>
                Apply Filters
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>Searching...</div>
      ) : tab === 'users' ? (
        users.length > 0 ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {users.map(user => (
              <div key={user.id} className="card" style={{ padding: '12px', cursor: 'pointer' }} onClick={() => navigate(`${BASE_PATH}/user/${user.username || user.id}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <SocialImage
                    src={user.profile_picture ? `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${user.profile_picture}` : `${API_URL}/avatar/${user.username}`}
                    alt=""
                    style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '14px' }}>@{user.username}</div>
                    {(user.first_name || user.last_name) && (
                      <div style={{ fontSize: '13px', color: "var(--ui-muted)" }}>{user.first_name} {user.last_name}</div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                      {user.age != null && <span style={{ fontSize: '12px', color: "var(--ui-muted)" }}>Age {user.age}</span>}
                      {user.gender_identity && <span style={{ fontSize: '12px', color: "var(--ui-muted)" }}>{user.gender_identity}</span>}
                      {user.sexual_orientation && <span style={{ fontSize: '12px', color: "var(--ui-muted)" }}>{user.sexual_orientation}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : query ? <p style={{ color: "var(--ui-muted)", textAlign: 'center' }}>No users found</p> : null
      ) : tab === 'tribes' ? (
        tribes.length > 0 ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {tribes.map(tribe => (
              <div key={tribe.id} className="card" style={{ padding: '12px', cursor: 'pointer' }} onClick={() => navigate(`${BASE_PATH}/tribe/${tribe.tag}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '12px',
                    background: tribe.color || "var(--ui-accent)", display: 'flex',
                    alignItems: 'center', justifyContent: 'center', color: tribe.color ? '#fff' : 'var(--ui-on-accent)', fontSize: '18px',
                  }}>
                    {tribe.icon || tribe.name[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '14px' }}>{tribe.name}</div>
                    <div style={{ fontSize: '13px', color: "var(--ui-muted)" }}>/{tribe.tag} · {tribe.member_count} members</div>
                    {tribe.description && <div style={{ fontSize: '12px', color: "var(--ui-muted)", marginTop: '2px' }}>{tribe.description.substring(0, 100)}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : query ? <p style={{ color: "var(--ui-muted)", textAlign: 'center' }}>No tribes found</p> : null
      ) : tab === 'posts' ? (
        posts.length > 0 ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {posts.map(post => (
              <PostCard key={post.id} post={post} currentUser={null} />
            ))}
          </div>
        ) : query ? <p style={{ color: "var(--ui-muted)", textAlign: 'center' }}>No posts found</p> : null
      ) : tab === 'shop' ? (
        shops.length > 0 ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {shops.map(shop => (
              <div key={shop.id} className="card" style={{ padding: '12px', cursor: 'pointer' }} onClick={() => navigate(`${BASE_PATH}/shop/${shop.slug}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {shop.icon ? (
                    <SocialImage src={shop.icon} alt="" style={{ width: '44px', height: '44px', borderRadius: '12px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '12px',
                      background: "var(--ui-accent)", display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: "var(--ui-on-accent)", fontWeight: '700', fontSize: '18px',
                    }}>
                      {shop.name[0]}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '14px' }}>{shop.name}</div>
                    {shop.description && <div style={{ fontSize: '13px', color: "var(--ui-muted)" }}>{shop.description.substring(0, 100)}</div>}
                    {shop.username && <div style={{ fontSize: '12px', color: "var(--ui-muted)" }}>by @{shop.username}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : query ? <p style={{ color: "var(--ui-muted)", textAlign: 'center' }}>No shops found</p> : null
      ) : null}
    </div>
  );
};

// Skills Page (RuneScape-style)
const SKILL_META = {
  create:    { name: 'Create',    icon: '/Skills/Create.svg',    color: '#f15722', desc: 'Create posts' },
  engage:    { name: 'Engage',    icon: '/Skills/engage.svg',    color: '#49b663', desc: 'Comment & reply' },
  love:      { name: 'Love',      icon: '/Skills/Love.svg',      color: '#f15722', desc: 'Heart & star posts' },
  community: { name: 'Community', icon: '/Skills/Community.svg', color: '#3fbced', desc: 'Follow & gain followers' },
  message:   { name: 'Message',   icon: '/Skills/Message.svg',   color: '#735ca8', desc: 'Send messages' },
  shop:      { name: 'Shop',      icon: '/Skills/shop.svg',      color: '#fcd006', desc: 'Buy & sell' },
};

const SkillsPage = () => {
  const { user } = useAuth();
  const [skills, setSkills] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user) {
          const skillsRes = await getSkills(user.id);
          setSkills(skillsRes.data);
        }
        const leaderboardRes = await getLeaderboard(selectedSkill);
        setLeaderboard(leaderboardRes.data);
      } catch (error) {
        console.error('Error fetching skills:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, selectedSkill]);

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
          <p>Loading skills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="skills-page">
      <div className="skills-container">
        <h2 className="skills-title">Skills</h2>

        {user && skills ? (
          <div className="skills-panel">
            <div className="skills-grid">
              {Object.entries(SKILL_META).map(([key, meta]) => {
                const skill = skills.skills[key] || { level: 1, xp: 0, progress: 0, xpToNextLevel: 0 };
                return (
                  <div
                    key={key}
                    className={`skill-tile ${selectedSkill === key ? 'selected' : ''}`}
                    onClick={() => setSelectedSkill(selectedSkill === key ? null : key)}
                    style={{ '--skill-color': meta.color }}
                  >
                    <div className="skill-tile-icon">
                      <SocialImage src={meta.icon} alt={meta.name} />
                    </div>
                    <div className="skill-tile-level">Lvl: {skill.level}</div>
                    <div className="skill-tile-name">{meta.name}</div>
                    <div className="skill-tile-xpbar">
                      <div className="skill-tile-xpfill" style={{ width: `${skill.progress}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="total-level">
              <span className="total-label">Total Level</span>
              <span className="total-value">{skills.totalLevel}</span>
              <span className="total-xp">{skills.totalXp.toLocaleString()} XP</span>
            </div>

            {selectedSkill && skills.skills[selectedSkill] && (
              <div className="skill-detail">
                <div className="skill-detail-header">
                  <SocialImage src={SKILL_META[selectedSkill].icon} alt="" className="skill-detail-icon" />
                  <div>
                    <div className="skill-detail-name">{SKILL_META[selectedSkill].name}</div>
                    <div className="skill-detail-desc">{SKILL_META[selectedSkill].desc}</div>
                  </div>
                  <div className="skill-detail-lvl">Lvl: {skills.skills[selectedSkill].level}</div>
                </div>
                <div className="skill-detail-bar">
                  <div className="skill-detail-fill" style={{ width: `${skills.skills[selectedSkill].progress}%`, background: 'linear-gradient(90deg, #f15722, #f78e1e, #fed206, #8ec74e, #49b863, #3fbced, #735ca8, #995aa4, #c263a7)' }} />
                </div>
                <div className="skill-detail-xp">
                  {skills.skills[selectedSkill].xp.toLocaleString()} XP
                  {skills.skills[selectedSkill].level < 99 && (
                    <span> &middot; {skills.skills[selectedSkill].xpToNextLevel.toLocaleString()} to next level</span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="skills-panel">
            <p style={{ textAlign: 'center', color: "var(--ui-muted)" }}>
              <Link to={`${BASE_PATH}/login`}>Log in</Link> to track your skills!
            </p>
          </div>
        )}

        <div className="leaderboard-panel">
          <h3 className="leaderboard-title">
            {selectedSkill ? `${SKILL_META[selectedSkill]?.name || selectedSkill} Leaderboard` : 'Total Level Leaderboard'}
          </h3>
          <div className="leaderboard-tabs">
            <button className={`lb-tab ${!selectedSkill ? 'active' : ''}`} onClick={() => setSelectedSkill(null)}>Total</button>
            {Object.entries(SKILL_META).map(([key, meta]) => (
              <button key={key} className={`lb-tab ${selectedSkill === key ? 'active' : ''}`} onClick={() => setSelectedSkill(key)}>
                <SocialImage src={meta.icon} alt="" style={{ width: 18, height: 18, verticalAlign: 'middle', marginRight: 4 }} />
                {meta.name}
              </button>
            ))}
          </div>
          <div className="leaderboard-list">
            {leaderboard.length > 0 ? (
              leaderboard.map((entry) => (
                <div key={entry.userId} className={`lb-entry ${user && entry.userId === user.id ? 'current-user' : ''}`}>
                  <span className={`lb-rank rank-${entry.rank}`}>#{entry.rank}</span>
                  <Link to={`${BASE_PATH}/${entry.username}`} className="lb-user">
                    {entry.profilePicture && (
                      <SocialImage src={entry.profilePicture} alt="" className="lb-avatar" />
                    )}
                    <span className="lb-username">@{entry.username}</span>
                  </Link>
                  <span className="lb-level">Lvl: {entry.level}</span>
                  <span className="lb-xp">{entry.xp.toLocaleString()} XP</span>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: "var(--ui-muted)", padding: '20px' }}>
                No entries yet. Start using belonging to earn XP!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Single Post Page
const SinglePostPage = () => {
  const { postId } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API_URL}/posts/${postId}`);
      if (!response.ok) {
        throw new Error('Post not found');
      }
      const data = await response.json();
      setPost(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [postId]);

  useEffect(() => {
    if (post) {
      const author = [post.first_name, post.last_name].filter(Boolean).join(' ') || post.username;
      document.title = post.tagline ? `${post.tagline} — Arawra` : `Post by ${author} — Arawra`;
    }
    return () => { document.title = BASE_PATH === '/social' ? 'Arawra | Social' : 'Arawra'; };
  }, [post]);

  const handlePostCreated = () => {
    setShowEditor(false);
    setEditingPost(null);
    fetchPost();
  };

  const handleEditPost = (postToEdit) => {
    setEditingPost(postToEdit);
    setShowEditor(true);
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/posts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        window.location.href = `${BASE_PATH}/feed`;
      }
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <p>Loading post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <h2>Post not found</h2>
        <Link to={`${BASE_PATH}/feed`} className="btn btn-primary" style={{ marginTop: '20px' }}>
          Back to Feed
        </Link>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', maxWidth: '700px' }}>
      <Link to={`${BASE_PATH}/feed`} style={{ color: "var(--ui-accent)", marginBottom: '20px', display: 'inline-block' }}>
        ← Back to Feed
      </Link>
      {showEditor && (
        <div style={{ marginBottom: '20px' }}>
          <PostEditor
            onPostCreated={handlePostCreated}
            editPost={editingPost}
            onCancel={() => { setShowEditor(false); setEditingPost(null); }}
          />
        </div>
      )}
      <PostCard
        post={post}
        isOwner={user && (user.id === post.user_id || user.role === 'admin')}
        onEdit={handleEditPost}
        onDelete={handleDeletePost}
        disablePostNav
      />
    </div>
  );
};

// Tribes List Page
const TribesPage = () => {
  const { user } = useAuth();
  const [tribes, setTribes] = useState([]);
  const [myTribes, setMyTribes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('discover');

  useEffect(() => {
    const fetchTribes = async () => {
      try {
        const response = await fetch(`${API_URL}/tribes`);
        const data = await response.json();
        setTribes(data);

        if (user) {
          const token = localStorage.getItem(AUTH_TOKEN_KEY);
          const myRes = await fetch(`${API_URL}/my-tribes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const myData = await myRes.json();
          setMyTribes(myData);
        }
      } catch (err) {
        console.error('Failed to fetch tribes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTribes();
  }, [user]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <p>Loading tribes...</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>🏳️‍🌈 Tribes</h1>
        {user && (
          <Link to={`${BASE_PATH}/tribes/create`} className="btn btn-primary">
            + Create a Tribe
          </Link>
        )}
      </div>

      {user && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={() => setActiveTab('discover')}
            className={activeTab === 'discover' ? 'btn btn-primary' : 'btn btn-secondary'}
          >
            Discover
          </button>
          <button
            onClick={() => setActiveTab('my-tribes')}
            className={activeTab === 'my-tribes' ? 'btn btn-primary' : 'btn btn-secondary'}
          >
            My Tribes ({myTribes.length})
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
        {(activeTab === 'discover' ? tribes : myTribes).map(tribe => (
          <Link
            key={tribe.id}
            to={`${BASE_PATH}/tribe/${tribe.tag}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="card tribe-card" style={{
              padding: '20px',
              borderLeft: `4px solid ${tribe.color || 'var(--ui-border)'}`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'pointer'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', position: 'relative' }}>
                <TribeIcon icon={tribe.icon} size={48} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0 }}>{tribe.name}</h3>
                    <TribeFlagStrip icon={tribe.icon} height={14} style={{ marginLeft: 8, flexShrink: 0 }} />
                  </div>
                  <span className="tribe-tag" style={{
                    background: tribe.color,
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px'
                  }}>
                    #{tribe.tag}
                  </span>
                </div>
              </div>
              <p style={{ color: "var(--ui-muted)", fontSize: '14px', margin: '10px 0' }}>
                {tribe.description || 'No description'}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: "var(--ui-muted)" }}>
                <span>👥 {tribe.member_count} members</span>
                {activeTab === 'my-tribes' && (
                  <span style={{
                    background: tribe.role === 'owner' ? '#f39c12' : tribe.role === 'admin' ? '#e74c3c' : "var(--ui-accent)",
                    color: tribe.role === 'owner' || tribe.role === 'admin' ? '#fff' : 'var(--ui-on-accent)',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    textTransform: 'capitalize'
                  }}>
                    {tribe.role}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {(activeTab === 'discover' ? tribes : myTribes).length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>
          {activeTab === 'discover'
            ? 'No tribes yet. Be the first to create one!'
            : 'You haven\'t joined any tribes yet.'}
        </div>
      )}
    </div>
  );
};

// Create Tribe Page
const DEFAULT_TRIBE_ICONS = [
  'people', 'heart', 'star', 'chat', 'game', 'book',
  'music', 'art', 'globe', 'flower', 'shield', 'home',
  'flame', 'crown', 'sparkle', 'rainbow', 'moon', 'butterfly',
];

const CreateTribePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3fbced');
  const [selectedIcon, setSelectedIcon] = useState('tribe-icons/people.svg');
  const [iconUploading, setIconUploading] = useState(false);
  const [selectedFlags, setSelectedFlags] = useState([]);
  const [flagSearch, setFlagSearch] = useState('');
  const [showFlagDropdown, setShowFlagDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filteredFlags = PRIDE_FLAGS.filter(f =>
    f.label.toLowerCase().includes(flagSearch.toLowerCase())
  );

  const toggleFlag = (file) => {
    const val = `flags/${file}`;
    setSelectedFlags(prev =>
      prev.includes(val) ? prev.filter(f => f !== val) : [...prev, val]
    );
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconUploading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setSelectedIcon(data.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setIconUploading(false);
    }
  };

  if (!user) {
    return <Navigate to={`${BASE_PATH}/login`} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/tribes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, tag, description, color, icon: JSON.stringify({ img: selectedIcon, flags: selectedFlags }) })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create tribe');
      }

      navigate(`${BASE_PATH}/tribe/${data.tribe.tag}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: '40px', maxWidth: '600px' }}>
      <h1>Create a Tribe</h1>
      <p style={{ color: "var(--ui-muted)", marginBottom: '30px' }}>
        Tribes are communities where you can share content, make announcements, and connect with like-minded people.
      </p>

      {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Tribe Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Trans Gamers United"
            required
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <label>Tag *</label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: "var(--ui-muted)"
            }}>#</span>
            <input
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
              placeholder="transgamers"
              required
              maxLength={20}
              style={{ paddingLeft: '28px' }}
            />
          </div>
          <small style={{ color: "var(--ui-muted)" }}>2-20 characters, letters and numbers only. This will be your tribe's unique identifier.</small>
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this tribe about?"
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="form-group">
          <label>Icon</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            {selectedIcon && (
              <div style={{
                width: 52, height: 52, padding: 4, border: `2px solid ${color}`,
                borderRadius: 10, background: `${color}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SocialImage src={selectedIcon.startsWith('http') ? selectedIcon : `${BASE_PATH}/${selectedIcon}`} alt="icon" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4 }} />
              </div>
            )}
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              cursor: iconUploading ? 'wait' : 'pointer',
              background: "var(--ui-muted-surface)", border: "1px solid var(--ui-border)",
              borderRadius: 8, padding: '8px 16px', fontSize: 13,
            }}>
              {iconUploading ? 'Uploading...' : (selectedIcon ? 'Change Icon' : 'Upload Icon')}
              <input type="file" accept="image/*" onChange={handleIconUpload} style={{ display: 'none' }} disabled={iconUploading} />
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Flags</label>

          {/* Selected flags chips */}
          {selectedFlags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {selectedFlags.map(val => {
                const file = val.replace('flags/', '');
                const meta = PRIDE_FLAGS.find(f => f.file === file);
                return (
                  <span key={val} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: color, color: '#fff',
                    borderRadius: '20px', padding: '3px 8px 3px 4px',
                    fontSize: '12px', fontWeight: 500,
                  }}>
                    <SocialImage src={`${BASE_PATH}/${val}`} alt="" style={{ width: 24, height: 16, objectFit: 'cover', borderRadius: 2 }} />
                    {meta ? meta.label : file}
                    <button
                      type="button"
                      onClick={() => toggleFlag(file)}
                      style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 0 0 2px', fontSize: '14px', lineHeight: 1 }}
                    >×</button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Search input */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={flagSearch}
              onChange={e => { setFlagSearch(e.target.value); setShowFlagDropdown(true); }}
              onFocus={() => setShowFlagDropdown(true)}
              onBlur={() => setTimeout(() => setShowFlagDropdown(false), 150)}
              placeholder="Search flags (e.g. Bisexual, Trans, Non-Binary...)"
              style={{ width: '100%', boxSizing: 'border-box' }}
            />

            {/* Dropdown */}
            {showFlagDropdown && filteredFlags.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                background: "var(--ui-surface)", border: "1px solid var(--ui-border)", borderRadius: '6px',
                maxHeight: '280px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}>
                {filteredFlags.map(({ file, label }) => {
                  const val = `flags/${file}`;
                  const selected = selectedFlags.includes(val);
                  return (
                    <button
                      key={file}
                      type="button"
                      onMouseDown={e => { e.preventDefault(); toggleFlag(file); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        width: '100%', padding: '8px 12px', border: 'none',
                        background: selected ? `${color}18` : "var(--ui-surface)",
                        cursor: 'pointer', textAlign: 'left',
                        borderBottom: "1px solid var(--ui-border)",
                      }}
                    >
                      <SocialImage src={`${BASE_PATH}/${val}`} alt={label} style={{ width: 36, height: 24, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: '14px' }}>{label}</span>
                      {selected && <span style={{ color, fontWeight: 'bold', fontSize: '16px' }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <small style={{ color: "var(--ui-muted)" }}>Select as many flags as you'd like to represent your tribe.</small>
        </div>

        <div className="form-group">
          <label>Color</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: '60px', height: '40px', border: 'none', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', gap: '5px' }}>
              {['#3fbced', '#e91e63', '#00bcd4', '#4caf50', '#ff9800', '#9c27b0', '#f44336'].map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  style={{
                    width: '30px',
                    height: '30px',
                    background: c,
                    border: color === c ? "3px solid var(--ui-border)" : 'none',
                    borderRadius: '50%',
                    cursor: 'pointer'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: '20px', marginBottom: '20px', borderLeft: `4px solid ${color}` }}>
          <p style={{ margin: '0 0 10px', color: "var(--ui-muted)", fontSize: '14px' }}>Preview:</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
            <TribeIcon icon={JSON.stringify({ img: selectedIcon, flags: selectedFlags })} size={48} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>{name || 'Tribe Name'}</h3>
                <TribeFlagStrip icon={JSON.stringify({ img: selectedIcon, flags: selectedFlags })} height={14} style={{ marginLeft: 8, flexShrink: 0 }} />
              </div>
              <span style={{
                background: color,
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px'
              }}>
                #{tag || 'tag'}
              </span>
            </div>
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
          {loading ? 'Creating...' : 'Create Tribe'}
        </button>
      </form>
    </div>
  );
};

// Single Tribe Page
const EditTribeModal = ({ tribe, tag, onClose, onSaved }) => {
  // Parse existing icon JSON to get img + flags
  const parsed = (() => {
    try {
      if (typeof tribe.icon === 'string' && tribe.icon.startsWith('{')) return JSON.parse(tribe.icon);
    } catch {}
    return { img: '', flags: [] };
  })();

  const [name, setName] = useState(tribe.name);
  const [description, setDescription] = useState(tribe.description || '');
  const [color, setColor] = useState(tribe.color || '#3fbced');
  const [selectedIcon, setSelectedIcon] = useState(parsed.img || '');
  const [iconUploading, setIconUploading] = useState(false);
  const [selectedFlags, setSelectedFlags] = useState(parsed.flags || []);
  const [flagSearch, setFlagSearch] = useState('');
  const [showFlagDropdown, setShowFlagDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filteredFlags = PRIDE_FLAGS.filter(f =>
    f.label.toLowerCase().includes(flagSearch.toLowerCase())
  );

  const toggleFlag = (file) => {
    const val = `flags/${file}`;
    setSelectedFlags(prev =>
      prev.includes(val) ? prev.filter(f => f !== val) : [...prev, val]
    );
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconUploading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setSelectedIcon(data.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setIconUploading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const iconJson = JSON.stringify({ img: selectedIcon, flags: selectedFlags });
      const res = await fetch(`${API_URL}/tribes/${tag}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), description: description.trim(), color, icon: iconJson })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to save'); }
      onSaved({ name: name.trim(), description: description.trim(), color, icon: iconJson });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="edit-tribe-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>Edit Tribe</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: "var(--ui-muted)" }}>×</button>
        </div>
        {error && <div className="error-message" style={{ marginBottom: 8 }}>{error}</div>}

        <div className="form-group">
          <label>Tribe Name *</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} maxLength={100} />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} maxLength={500} placeholder="What is this tribe about?" />
        </div>

        {/* Icon + Color on one row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, display: 'block' }}>Icon</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {selectedIcon && (
                <div style={{ width: 40, height: 40, padding: 3, border: `2px solid ${color}`, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SocialImage src={selectedIcon.startsWith('http') ? selectedIcon : `${BASE_PATH}/${selectedIcon}`} alt="icon" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 3 }} />
                </div>
              )}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: iconUploading ? 'wait' : 'pointer', background: "var(--ui-muted-surface)", border: "1px solid var(--ui-border)", borderRadius: 6, padding: '6px 12px', fontSize: 12 }}>
                {iconUploading ? 'Uploading...' : (selectedIcon ? 'Change' : 'Upload Icon')}
                <input type="file" accept="image/*" onChange={handleIconUpload} style={{ display: 'none' }} disabled={iconUploading} />
              </label>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, display: 'block' }}>Color</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', padding: 0 }} />
              {['#3fbced', '#e91e63', '#00bcd4', '#4caf50', '#ff9800', '#9c27b0', '#f44336'].map(c => (
                <button key={c} type="button" onClick={() => setColor(c)} style={{ width: 24, height: 24, background: c, border: color === c ? "2px solid var(--ui-border)" : 'none', borderRadius: '50%', cursor: 'pointer' }} />
              ))}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Flags</label>
          {selectedFlags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
              {selectedFlags.map(val => {
                const file = val.replace('flags/', '');
                const meta = PRIDE_FLAGS.find(f => f.file === file);
                return (
                  <span key={val} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: color, color: '#fff', borderRadius: 16, padding: '2px 6px 2px 3px', fontSize: 11, fontWeight: 500 }}>
                    <SocialImage src={`${BASE_PATH}/${val}`} alt="" style={{ width: 20, height: 13, objectFit: 'cover', borderRadius: 2 }} />
                    {meta ? meta.label : file}
                    <button type="button" onClick={() => toggleFlag(file)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 0 0 2px', fontSize: 12, lineHeight: 1 }}>×</button>
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ position: 'relative' }}>
            <input type="text" value={flagSearch} onChange={e => { setFlagSearch(e.target.value); setShowFlagDropdown(true); }} onFocus={() => setShowFlagDropdown(true)} onBlur={() => setTimeout(() => setShowFlagDropdown(false), 150)} placeholder="Search flags..." style={{ width: '100%', boxSizing: 'border-box' }} />
            {showFlagDropdown && filteredFlags.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: "var(--ui-surface)", border: "1px solid var(--ui-border)", borderRadius: 6, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {filteredFlags.map(({ file, label }) => {
                  const val = `flags/${file}`;
                  const sel = selectedFlags.includes(val);
                  return (
                    <button key={file} type="button" onMouseDown={e => { e.preventDefault(); toggleFlag(file); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 10px', border: 'none', background: sel ? `${color}18` : "var(--ui-surface)", cursor: 'pointer', textAlign: 'left', borderBottom: "1px solid var(--ui-border)" }}>
                      <SocialImage src={`${BASE_PATH}/${val}`} alt={label} style={{ width: 30, height: 20, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 13 }}>{label}</span>
                      {sel && <span style={{ color, fontWeight: 'bold', fontSize: 14 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Compact preview */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 12, background: "var(--ui-muted-surface)", borderRadius: 8, borderLeft: `3px solid ${color}` }}>
          <TribeIcon icon={JSON.stringify({ img: selectedIcon, flags: selectedFlags })} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{name || 'Tribe Name'}</span>
              <TribeFlagStrip icon={JSON.stringify({ img: selectedIcon, flags: selectedFlags })} height={12} style={{ flexShrink: 0 }} />
            </div>
            <span style={{ background: color, color: '#fff', padding: '1px 6px', borderRadius: 8, fontSize: 11 }}>#{tag}</span>
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ width: '100%' }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

const TribePage = () => {
  const { tag } = useParams();
  const { user } = useAuth();
  const [tribe, setTribe] = useState(null);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newPost, setNewPost] = useState('');
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [posting, setPosting] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [showPostEditor, setShowPostEditor] = useState(false);
  const [editTribeOpen, setEditTribeOpen] = useState(false);

  const fetchTribeData = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const authHeader = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [tribeRes, membersRes, postsRes, feedRes] = await Promise.all([
        fetch(`${API_URL}/tribes/${tag}`),
        fetch(`${API_URL}/tribes/${tag}/members`),
        fetch(`${API_URL}/tribes/${tag}/posts`),
        fetch(`${API_URL}/tribes/${tag}/feed`, { headers: authHeader })
      ]);

      if (!tribeRes.ok) throw new Error('Tribe not found');

      const tribeData = await tribeRes.json();
      const membersData = await membersRes.json();
      const postsData = await postsRes.json();
      const feedData = feedRes.ok ? await feedRes.json() : [];

      setTribe(tribeData);
      setMembers(membersData);
      setPosts(postsData);
      setFeedPosts(Array.isArray(feedData) ? feedData : []);

      if (user) {
        const userMembership = membersData.find(m => m.user_id === user.id);
        setMembership(userMembership || null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTribeData();
  }, [tag, user]);

  const handleJoin = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/tribes/${tag}/join`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchTribeData();
      } else {
        const data = await response.json();
        alert(data.error);
      }
    } catch (err) {
      console.error('Failed to join:', err);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Are you sure you want to leave this tribe?')) return;

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/tribes/${tag}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMembership(null);
        fetchTribeData();
      } else {
        const data = await response.json();
        alert(data.error);
      }
    } catch (err) {
      console.error('Failed to leave:', err);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    setPosting(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/tribes/${tag}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newPost, isAnnouncement })
      });

      if (response.ok) {
        setNewPost('');
        setIsAnnouncement(false);
        fetchTribeData();
      }
    } catch (err) {
      console.error('Failed to post:', err);
    } finally {
      setPosting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await fetch(`${API_URL}/tribes/${tag}/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTribeData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleFeedPostCreated = () => {
    setShowPostEditor(false);
    fetchTribeData();
  };

  const handleUpdateMemberRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/tribes/${tag}/members/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: newRole } : m));
      }
    } catch (err) {
      console.error('Failed to update role:', err);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the tribe?')) return;
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/tribes/${tag}/members/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setMembers(prev => prev.filter(m => m.user_id !== userId));
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <p>Loading tribe...</p>
      </div>
    );
  }

  if (error || !tribe) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <h2>Tribe not found</h2>
        <Link to={`${BASE_PATH}/tribes`} className="btn btn-primary" style={{ marginTop: '20px' }}>
          Browse Tribes
        </Link>
      </div>
    );
  }

  const canPost = membership !== null;
  const canAnnounce = membership && ['owner', 'admin', 'moderator'].includes(membership.role);
  const canManageMembers = (membership && ['owner', 'admin'].includes(membership.role)) || (user && user.role === 'admin');
  const isOwner = user && tribe && user.id === tribe.owner_id;
  const isSiteAdmin = user && user.role === 'admin';

  const roleBg = (role) =>
    role === 'owner' ? '#f39c12' : role === 'admin' ? '#e74c3c' : role === 'moderator' ? '#9b59b6' : 'var(--ui-accent)';

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      {editTribeOpen && (
        <EditTribeModal
          tribe={tribe}
          tag={tag}
          onClose={() => setEditTribeOpen(false)}
          onSaved={(updated) => {
            setTribe(t => ({ ...t, ...updated }));
            setEditTribeOpen(false);
          }}
        />
      )}

      {/* Tribe Header */}
      <div className="card" style={{
        padding: '30px',
        marginBottom: '30px',
        borderTop: `4px solid ${tribe.color}`,
        background: `linear-gradient(135deg, ${tribe.color}11 0%, transparent 100%)`
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <TribeIcon icon={tribe.icon} size={64} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ margin: '0 0 5px' }}>{tribe.name}</h1>
                <TribeFlagStrip icon={tribe.icon} height={18} style={{ marginBottom: 5 }} />
              </div>
              <span className="tribe-tag" style={{ background: tribe.color, color: '#fff', padding: '4px 12px', borderRadius: '15px', fontSize: '14px', fontWeight: '500' }}>
                #{tribe.tag}
              </span>
              <p style={{ margin: '10px 0 0', color: "var(--ui-muted)" }}>{tribe.description}</p>
              <p style={{ margin: '10px 0 0', fontSize: '14px', color: "var(--ui-muted)" }}>
                👥 {tribe.member_count} members · Created by @{tribe.owner_username}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {(isOwner || isSiteAdmin) && (
              <button onClick={() => setEditTribeOpen(true)} className="btn btn-secondary">✏️ Edit Tribe</button>
            )}
            {user && !membership && (
              <button onClick={handleJoin} className="btn btn-primary">Join Tribe</button>
            )}
            {membership && (
              <>
                <span style={{ background: roleBg(membership.role), color: roleBg(membership.role) === 'var(--ui-accent)' ? 'var(--ui-on-accent)' : '#fff', padding: '4px 12px', borderRadius: '15px', fontSize: '12px', textTransform: 'capitalize' }}>
                  {membership.role}
                </span>
                {membership.role !== 'owner' && (
                  <button onClick={handleLeave} className="btn btn-secondary">Leave</button>
                )}
              </>
            )}
            {!user && (
              <Link to={`${BASE_PATH}/login`} className="btn btn-primary">Log in to Join</Link>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button onClick={() => setActiveTab('posts')} className={activeTab === 'posts' ? 'btn btn-primary' : 'btn btn-secondary'}>
          📝 Posts
        </button>
        <button onClick={() => setActiveTab('members')} className={activeTab === 'members' ? 'btn btn-primary' : 'btn btn-secondary'}>
          👥 Members ({members.length})
        </button>
      </div>

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <div>
          {/* Announcement form — mods/admins/owner only */}
          {canAnnounce && (
            <div className="card" style={{ padding: '20px', marginBottom: '16px', borderLeft: `4px solid ${tribe.color}` }}>
              <p style={{ margin: '0 0 8px', fontWeight: 600, color: tribe.color, fontSize: 13 }}>📢 Post Announcement</p>
              <form onSubmit={handlePost}>
                <textarea
                  value={newPost}
                  onChange={e => setNewPost(e.target.value)}
                  placeholder="Announcements are pinned to the top of the tribe..."
                  rows={2}
                  style={{ width: '100%', marginBottom: '10px' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" className="btn btn-primary" disabled={posting || !newPost.trim()}>
                    {posting ? 'Posting…' : '📢 Post Announcement'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Standard PostEditor for all members */}
          {canPost && (
            showPostEditor ? (
              <PostEditor
                initialTribe={tribe}
                onPostCreated={handleFeedPostCreated}
                onCancel={() => setShowPostEditor(false)}
              />
            ) : (
              <div
                className="card"
                style={{ padding: '14px 20px', marginBottom: '20px', cursor: 'text', color: "var(--ui-muted)", display: 'flex', alignItems: 'center', gap: 12 }}
                onClick={() => setShowPostEditor(true)}
              >
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: tribe.color, flexShrink: 0 }} />
                <span>Write a post to #{tribe.tag}…</span>
              </div>
            )
          )}
          {!user && (
            <div style={{ textAlign: 'center', padding: '20px 0 24px', color: "var(--ui-muted)" }}>
              <Link to={`${BASE_PATH}/login`} className="btn btn-primary">Log in to post</Link>
            </div>
          )}
          {user && !membership && (
            <p style={{ textAlign: 'center', color: "var(--ui-muted)", marginBottom: 24 }}>Join this tribe to post here.</p>
          )}

          {/* Announcements (tribe_posts, is_announcement=true) */}
          {posts.filter(p => p.is_announcement).map(post => (
            <div key={`ann-${post.id}`} className="card" style={{ padding: '20px', marginBottom: '15px', borderLeft: `4px solid ${tribe.color}`, background: `${tribe.color}08` }}>
              <div style={{ color: tribe.color, fontWeight: '600', fontSize: '12px', marginBottom: '10px' }}>📢 ANNOUNCEMENT</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Link to={`/${post.username}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: 'inherit' }}>
                  {post.profile_picture
                    ? <SocialImage src={post.profile_picture} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${tribe.color} 0%, #764ba2 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>{(post.first_name || post.username || '?')[0].toUpperCase()}</div>
                  }
                  <div>
                    <span style={{ fontWeight: '500' }}>{post.first_name && post.last_name ? `${post.first_name} ${post.last_name}` : `@${post.username}`}</span>
                    <p style={{ margin: 0, fontSize: '12px', color: "var(--ui-muted)" }}>{new Date(post.created_at).toLocaleString()}</p>
                  </div>
                </Link>
                {user && (user.id === post.user_id || canAnnounce) && (
                  <button onClick={() => handleDeletePost(post.id)} style={{ background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer' }}>✕</button>
                )}
              </div>
              <p style={{ marginTop: '15px', lineHeight: '1.6' }}>{post.content}</p>
            </div>
          ))}

          {/* Non-announcement tribe_posts */}
          {posts.filter(p => !p.is_announcement).map(post => (
            <div key={`tp-${post.id}`} className="card" style={{ padding: '20px', marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Link to={`/${post.username}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: 'inherit' }}>
                  {post.profile_picture
                    ? <SocialImage src={post.profile_picture} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${tribe.color} 0%, #764ba2 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>{(post.first_name || post.username || '?')[0].toUpperCase()}</div>
                  }
                  <div>
                    <span style={{ fontWeight: '500' }}>{post.first_name && post.last_name ? `${post.first_name} ${post.last_name}` : `@${post.username}`}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 6px', borderRadius: 8, background: roleBg(post.poster_role), color: roleBg(post.poster_role) === 'var(--ui-accent)' ? 'var(--ui-on-accent)' : '#fff', textTransform: 'capitalize' }}>{post.poster_role}</span>
                    <p style={{ margin: 0, fontSize: '12px', color: "var(--ui-muted)" }}>{new Date(post.created_at).toLocaleString()}</p>
                  </div>
                </Link>
                {user && (user.id === post.user_id || canAnnounce) && (
                  <button onClick={() => handleDeletePost(post.id)} style={{ background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer' }}>✕</button>
                )}
              </div>
              <p style={{ marginTop: '15px', lineHeight: '1.6' }}>{post.content}</p>
            </div>
          ))}

          {/* Main-feed posts tagged to this tribe */}
          {feedPosts.length > 0 && (
            <>
              {posts.length > 0 && (
                <div style={{ margin: '20px 0 12px', fontSize: 12, fontWeight: 700, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: 1 }}>Recent Posts</div>
              )}
              {feedPosts.map(post => (
                <PostCard
                  key={`fp-${post.id}`}
                  post={post}
                  isOwner={user && user.id === post.user_id}
                  onDelete={handleDeletePost}
                />
              ))}
            </>
          )}

          {posts.length === 0 && feedPosts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: "var(--ui-muted)" }}>
              No posts yet. {canPost ? 'Be the first to post!' : 'Join the tribe to start posting!'}
            </div>
          )}
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {members.map(member => {
            const canManage = canManageMembers && member.role !== 'owner';
            return (
              <div key={member.id} className="card" style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Link to={`/${member.username}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none', color: 'inherit', flex: 1 }}>
                  {member.profile_picture
                    ? <SocialImage src={member.profile_picture} alt="" style={{ width: 45, height: 45, borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: 45, height: 45, borderRadius: '50%', background: `linear-gradient(135deg, ${tribe.color} 0%, #764ba2 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>{(member.first_name || member.username)[0].toUpperCase()}</div>
                  }
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '500' }}>
                      {member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : `@${member.username}`}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', color: "var(--ui-muted)" }}>@{member.username}</span>
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: roleBg(member.role), color: roleBg(member.role) === 'var(--ui-accent)' ? 'var(--ui-on-accent)' : '#fff', textTransform: 'capitalize' }}>{member.role}</span>
                    </div>
                  </div>
                </Link>
                {canManage && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <select
                      value={member.role}
                      onChange={e => handleUpdateMemberRole(member.user_id, e.target.value)}
                      className="tribe-role-select"
                      onClick={e => e.preventDefault()}
                    >
                      <option value="member">member</option>
                      <option value="moderator">moderator</option>
                      <option value="admin">admin</option>
                    </select>
                    <button onClick={() => handleRemoveMember(member.user_id)} className="tribe-remove-btn">Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Tribe Tag Component (for profile display)
const TribeTags = ({ userId, username }) => {
  const [tribes, setTribes] = useState([]);

  useEffect(() => {
    const fetchTribes = async () => {
      try {
        const identifier = username || userId;
        const response = await fetch(`${API_URL}/users/${identifier}/tribes`);
        if (response.ok) {
          const data = await response.json();
          setTribes(data);
        }
      } catch (err) {
        console.error('Failed to fetch tribes:', err);
      }
    };
    fetchTribes();
  }, [userId, username]);

  if (tribes.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
      {tribes.map(tribe => (
        <Link
          key={tribe.id}
          to={`${BASE_PATH}/tribe/${tribe.tag}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            background: tribe.color,
            color: '#fff',
            borderRadius: '12px',
            fontSize: '12px',
            textDecoration: 'none',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          <TribeIcon icon={tribe.icon} size={20} />
          <span>#{tribe.tag}</span>
          {tribe.role === 'owner' && <span>👑</span>}
        </Link>
      ))}
    </div>
  );
};

// ── MessageAttachment ────────────────────────────────────────────────────────
const MessageAttachment = ({ att }) => {
  const { file_url, file_name, file_type, file_size } = att;
  const src = mediaUrl(file_url);
  if (file_type && file_type.startsWith('image/')) {
    return (
      <SocialImage
        src={src}
        alt={file_name}
        className="msg-attachment-img"
        onClick={() => window.open(src, '_blank')}
      />
    );
  }
  if (file_type && file_type.startsWith('video/')) {
    return <SocialVideo src={src} controls className="msg-attachment-video" />;
  }
  if (file_type && file_type.startsWith('audio/')) {
    return <SocialAudio src={src} controls className="msg-attachment-audio" />;
  }
  const sizeMb = file_size ? (file_size / (1024 * 1024)).toFixed(1) : '?';
  return (
    <a href={mediaUrl(src)} download={file_name} target="_blank" rel="noreferrer" className="msg-attachment-file">
      <span>📎</span>
      <div>
        <div className="msg-attachment-filename">{file_name}</div>
        <div className="msg-attachment-size">{sizeMb} MB</div>
      </div>
    </a>
  );
};

// ── MessageGallery ────────────────────────────────────────────────────────────
const MessageGallery = ({ attachments, layout }) => {
  const images = attachments.filter(a => a.file_type && a.file_type.startsWith('image/'));
  const others = attachments.filter(a => !a.file_type || !a.file_type.startsWith('image/'));
  const galleryClass = images.length <= 1 ? 'msg-gallery msg-gallery-single' : `msg-gallery msg-gallery-${layout || 'auto'}`;
  return (
    <>
      {images.length > 0 && (
        <div className={galleryClass}>
          {images.map(att => {
            const src = att.file_url.startsWith('http') ? att.file_url : `${window.location.origin}${att.file_url}`;
            return (
              <SocialImage
                key={att.id}
                src={src}
                alt={att.file_name}
                className="msg-gallery-img"
                onClick={() => window.open(src, '_blank')}
              />
            );
          })}
        </div>
      )}
      {others.map(att => <MessageAttachment key={att.id} att={att} />)}
    </>
  );
};

// ── LinkEmbed ────────────────────────────────────────────────────────────────
const LinkEmbed = ({ url }) => {
  const [embed, setEmbed] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const apiBase = `${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}`;
    fetch(`${apiBase}/api/embed/preview?url=${encodeURIComponent(url)}`, { signal: controller.signal })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setEmbed(data); })
      .catch(() => {});
    return () => controller.abort();
  }, [url]);

  // YouTube embed
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
  if (ytMatch) {
    return (
      <div className="link-embed-video">
        <iframe
          src={`https://www.youtube.com/embed/${ytMatch[1]}`}
          width="360" height="202" frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen title="YouTube"
        />
      </div>
    );
  }

  // Vimeo embed
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return (
      <div className="link-embed-video">
        <iframe
          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
          width="360" height="202" frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen title="Vimeo"
        />
      </div>
    );
  }

  if (!embed) return null;

  return (
    <a href={url} target="_blank" rel="noreferrer" className="link-embed-card">
      {embed.image && <SocialImage src={embed.image} alt="" className="link-embed-image" />}
      <div className="link-embed-body">
        {embed.site_name && <div className="link-embed-site">{embed.site_name}</div>}
        {embed.title && <div className="link-embed-title">{embed.title}</div>}
        {embed.description && <div className="link-embed-desc">{embed.description.slice(0, 120)}{embed.description.length > 120 ? '…' : ''}</div>}
      </div>
    </a>
  );
};

// ── Sticker system ───────────────────────────────────────────────────────────
// Stickers are defined as named shortcodes mapped to image paths in /stickers/
const STICKERS = [
  { name: 'heart', label: '❤️ heart', shortcode: ':heart:', emoji: '❤️' },
  { name: 'sparkles', label: '✨ sparkles', shortcode: ':sparkles:', emoji: '✨' },
  { name: 'rainbow', label: '🌈 rainbow', shortcode: ':rainbow:', emoji: '🌈' },
  { name: 'trans', label: '⚧ trans', shortcode: ':trans:', emoji: '⚧️' },
  { name: 'pride', label: '🏳️‍🌈 pride', shortcode: ':pride:', emoji: '🏳️‍🌈' },
  { name: 'hug', label: '🤗 hug', shortcode: ':hug:', emoji: '🤗' },
  { name: 'celebrate', label: '🎉 celebrate', shortcode: ':celebrate:', emoji: '🎉' },
  { name: 'wave', label: '👋 wave', shortcode: ':wave:', emoji: '👋' },
];

function renderMessageWithStickers(text, customStickers = []) {
  if (!text) return null;
  const allStickers = [...STICKERS, ...customStickers];
  const parts = [];
  let lastIndex = 0;
  let match;
  const regex = /:(\w+):/g;
  while ((match = regex.exec(text)) !== null) {
    const sticker = allStickers.find(s => s.name === match[1]);
    if (sticker) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      parts.push(
        sticker.file_url
          ? <SocialImage key={match.index} src={sticker.file_url} alt={sticker.name} className="sticker-inline-img" title={`:${sticker.name}:`} />
          : <span key={match.index} className="sticker-inline" title={sticker.shortcode}>{sticker.emoji}</span>
      );
      lastIndex = match.index + match[0].length;
    }
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : text;
}

const StickerPicker = ({ onSelect, onClose, customStickers: initialCustom, onStickerUploaded }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = React.useState(false);
  const [shortcode, setShortcode] = React.useState('');
  const [error, setError] = React.useState('');
  const [pendingFile, setPendingFile] = React.useState(null);
  const fileRef = React.useRef(null);
  const [customStickers, setCustomStickers] = React.useState(initialCustom || []);

  React.useEffect(() => {
    if (!initialCustom) {
      fetch(`${window.location.origin}/api/stickers`)
        .then(r => r.json())
        .then(data => setCustomStickers(data.map(s => ({ id: s.id, name: s.shortcode, file_url: s.file_url }))))
        .catch(() => {});
    }
  }, [initialCustom]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width !== img.height) {
        setError('Image must be square (equal width and height)');
        setPendingFile(null);
      } else {
        setPendingFile(file);
      }
    };
    img.src = url;
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!pendingFile) { setError('Choose an image first'); return; }
    if (!shortcode.trim()) { setError('Enter a shortcode first'); return; }
    setUploading(true);
    setError('');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const fd = new FormData();
      fd.append('file', pendingFile);
      fd.append('shortcode', shortcode.trim().toLowerCase());
      const res = await fetch(`${window.location.origin}/api/stickers/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Upload failed'); }
      else {
        setCustomStickers(prev => [...prev, { id: data.id, name: data.shortcode, file_url: data.file_url }]);
        setShortcode('');
        setPendingFile(null);
        if (onStickerUploaded) onStickerUploaded(data);
      }
    } catch { setError('Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await fetch(`${window.location.origin}/api/stickers/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCustomStickers(prev => prev.filter(s => s.id !== id));
    } catch {}
  };

  const [search, setSearch] = React.useState('');
  const filteredStickers = customStickers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="sticker-picker">
      <div className="sticker-picker-header">
        <span>Stickers</span>
        <button onClick={onClose} className="sticker-picker-close">×</button>
      </div>
      <input
        className="sticker-search-input"
        type="text"
        placeholder="Search stickers..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      <div className="sticker-picker-grid">
        {filteredStickers.map(s => (
          <div key={s.name} className="sticker-pick-wrap">
            <button className="sticker-pick-btn" title={`:${s.name}:`} onClick={() => { onSelect(`:${s.name}:`); onClose(); }}>
              <SocialImage src={s.file_url} alt={s.name} className="sticker-pick-img" />
              <span className="sticker-pick-label">{s.name}</span>
            </button>
            {user && s.id && (
              <button className="sticker-delete-btn" title="Delete sticker" onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}>×</button>
            )}
          </div>
        ))}
        {filteredStickers.length === 0 && <div className="sticker-empty">No stickers found</div>}
      </div>
      {user && (
        <div className="sticker-upload-area">
          <button className="sticker-img-btn" onClick={() => fileRef.current?.click()} title="Choose image">
            {pendingFile ? '✓' : '🖼'}
          </button>
          <input
            className="sticker-shortcode-input"
            placeholder=":shortcode: — Image must be a square"
            value={shortcode}
            onChange={e => setShortcode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
          />
          <button className="sticker-upload-btn" onClick={handleSubmit} disabled={uploading}>
            {uploading ? '⏳' : '+ Add'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
          {error && <div className="sticker-upload-error">{error}</div>}
        </div>
      )}
    </div>
  );
};

// ── WebRTC CallManager ───────────────────────────────────────────────────────
const STUN_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]
};

const callBtnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 50,
  padding: '12px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', minWidth: 80
});

const CallManager = () => {
  const { user } = useAuth();
  const [callState, setCallState] = useState(null); // null | 'incoming' | 'outgoing' | 'connected'
  const [callType, setCallType] = useState('audio');
  const [remoteUser, setRemoteUser] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingOfferRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const remoteStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const endCall = useCallback((notifyRemote = true) => {
    if (notifyRemote && remoteUser) {
      getSocket().emit('call-end', { targetUserId: remoteUser.id });
    }
    if (peerRef.current) { peerRef.current.close(); peerRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    remoteStreamRef.current = null;
    pendingCandidatesRef.current = [];
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    setCallState(null);
    setRemoteUser(null);
    setIsMuted(false);
    setIsCameraOff(false);
    setIsScreenSharing(false);
  }, [remoteUser]);

  const initPeer = useCallback((targetUser) => {
    const peer = new RTCPeerConnection(STUN_SERVERS);
    peer.onicecandidate = (e) => {
      if (e.candidate && targetUser) {
        getSocket().emit('ice-candidate', { targetUserId: targetUser.id, candidate: e.candidate });
      }
    };
    peer.ontrack = (e) => {
      remoteStreamRef.current = e.streams[0];
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0];
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = e.streams[0];
    };
    peerRef.current = peer;
    return peer;
  }, []);

  useEffect(() => {
    if (!user) return;
    const sock = getSocket();

    const onCallOffer = ({ fromUserId, offer, callType: ct }) => {
      pendingOfferRef.current = offer;
      setRemoteUser({ id: fromUserId });
      setCallType(ct || 'audio');
      setCallState('incoming');
    };
    const onCallAnswer = async ({ answer }) => {
      if (peerRef.current) {
        await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
        for (const c of pendingCandidatesRef.current) {
          try { await peerRef.current.addIceCandidate(new RTCIceCandidate(c)); } catch(e) {}
        }
        pendingCandidatesRef.current = [];
        setCallState('connected');
      }
    };
    const onIceCandidate = async ({ candidate }) => {
      if (!candidate) return;
      if (peerRef.current?.remoteDescription) {
        try { await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch(e) {}
      } else {
        pendingCandidatesRef.current.push(candidate);
      }
    };
    const onCallEnd = () => endCall(false);
    const onCallDecline = () => { setCallState(null); setRemoteUser(null); };

    sock.on('call-offer', onCallOffer);
    sock.on('call-answer', onCallAnswer);
    sock.on('ice-candidate', onIceCandidate);
    sock.on('call-end', onCallEnd);
    sock.on('call-decline', onCallDecline);

    // Listen to event bus for outgoing calls
    const handleBusEvent = async ({ targetUser, type }) => {
      setRemoteUser(targetUser);
      setCallType(type || 'audio');
      setCallState('outgoing');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: type === 'video' }).catch(() => null);
      if (!stream) { setCallState(null); return; }
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const peer = initPeer(targetUser);
      stream.getTracks().forEach(t => peer.addTrack(t, stream));
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      getSocket().emit('call-offer', { targetUserId: targetUser.id, offer, callType: type });
    };

    callEventBus.on(handleBusEvent);

    return () => {
      sock.off('call-offer', onCallOffer);
      sock.off('call-answer', onCallAnswer);
      sock.off('ice-candidate', onIceCandidate);
      sock.off('call-end', onCallEnd);
      sock.off('call-decline', onCallDecline);
      callEventBus.off(handleBusEvent);
    };
  }, [user, endCall, initPeer]);

  const acceptCall = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: callType === 'video' }).catch(() => null);
    if (!stream) return;
    localStreamRef.current = stream;
    if (localVideoRef.current) localVideoRef.current.srcObject = stream;

    const peer = initPeer(remoteUser);
    stream.getTracks().forEach(t => peer.addTrack(t, stream));
    await peer.setRemoteDescription(new RTCSessionDescription(pendingOfferRef.current));
    for (const c of pendingCandidatesRef.current) {
      try { await peer.addIceCandidate(new RTCIceCandidate(c)); } catch(e) {}
    }
    pendingCandidatesRef.current = [];
    const answer = await peer.createAnswer();
    await peer.setLocalDescription(answer);
    getSocket().emit('call-answer', { targetUserId: remoteUser.id, answer });
    setCallState('connected');
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(m => !m);
    }
  };

  const toggleCamera = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsCameraOff(c => !c);
    }
  };

  const toggleScreenShare = async () => {
    if (!isScreenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];
        if (peerRef.current) {
          const sender = peerRef.current.getSenders().find(s => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(screenTrack);
        }
        screenTrack.onended = () => toggleScreenShare();
        setIsScreenSharing(true);
      } catch (e) { console.error('Screenshare error:', e); }
    } else {
      const camTrack = localStreamRef.current?.getVideoTracks()[0];
      if (peerRef.current && camTrack) {
        const sender = peerRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender) sender.replaceTrack(camTrack);
      }
      setIsScreenSharing(false);
    }
  };

  if (!callState) return null;

  // Ringing overlay (incoming/outgoing) — centered card, disappears on accept/decline
  if (callState === 'incoming' || callState === 'outgoing') {
    return (
      <div className="call-ringing-overlay">
        <SocialAudio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
        <div className="call-ringing-card">
          <div className="call-icon">{callType === 'video' ? '📹' : '📞'}</div>
          <p className="call-ringing-label">{callState === 'incoming' ? 'Incoming call' : 'Calling...'}</p>
          <h2 className="call-ringing-name">{remoteUser?.username ? `@${remoteUser.username}` : '...'}</h2>
          <div className="call-ringing-actions">
            {callState === 'incoming' ? (
              <>
                <button onClick={() => { getSocket().emit('call-decline', { targetUserId: remoteUser?.id }); endCall(false); }} style={callBtnStyle('#ef4444')}>Decline</button>
                <button onClick={acceptCall} style={callBtnStyle('#22c55e')}>Accept</button>
              </>
            ) : (
              <button onClick={() => endCall(true)} style={callBtnStyle('#ef4444')}>Cancel</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Active call — slim bar under topbar
  return (
    <div className="call-active-bar">
      <SocialAudio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      <span className="call-bar-label">
        {callType === 'video' ? '📹' : '📞'} @{remoteUser?.username || '...'}
      </span>
      <div className="call-bar-actions">
        <button onClick={toggleMute} className={`call-bar-btn ${isMuted ? 'call-bar-btn-active' : ''}`}>
          {isMuted ? 'Unmute' : 'Mute'}
        </button>
        {callType === 'video' && (
          <>
            <button onClick={toggleCamera} className={`call-bar-btn ${isCameraOff ? 'call-bar-btn-active' : ''}`}>
              {isCameraOff ? 'Cam On' : 'Cam Off'}
            </button>
            <button onClick={toggleScreenShare} className={`call-bar-btn ${isScreenSharing ? 'call-bar-btn-active' : ''}`}>
              {isScreenSharing ? 'Stop Share' : 'Share'}
            </button>
          </>
        )}
        <button onClick={() => endCall(true)} className="call-bar-btn call-bar-btn-end">End</button>
      </div>
    </div>
  );
};

// Messages Page
const MessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]); // pre-uploaded attachments
  const [galleryLayout, setGalleryLayout] = useState('auto');
  const [uploading, setUploading] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [customStickers, setCustomStickers] = useState([]);
  const messagesEndRef = React.useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useEffect(() => {
    fetch(`${window.location.origin}/api/stickers`)
      .then(r => r.json())
      .then(data => setCustomStickers(data.map(s => ({ name: s.shortcode, file_url: s.file_url }))))
      .catch(() => {});
  }, []);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/messages/conversations/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();

        // Smart scroll: only scroll to bottom if already near the bottom
        const container = messagesEndRef.current?.parentElement;
        const isNearBottom = container ? (container.scrollHeight - container.scrollTop - container.clientHeight < 100) : true;

        setMessages(data.messages);
        setParticipants(data.participants);
        if (isNearBottom) {
          setTimeout(scrollToBottom, 100);
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user]);

  useEffect(() => {
    if (!selectedConversation) return;

    fetchMessages(selectedConversation);

    const sock = getSocket();
    sock.emit('join-conversation', selectedConversation);

    const handleNewMessage = (msg) => {
      if (msg.conversation_id === selectedConversation) {
        setMessages(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        fetchConversations();
      }
    };
    const handleTypingStart = ({ userId }) => {
      if (userId !== user?.id) setOtherUserTyping(true);
    };
    const handleTypingStop = ({ userId }) => {
      if (userId !== user?.id) setOtherUserTyping(false);
    };

    sock.on('new-message', handleNewMessage);
    sock.on('typing-start', handleTypingStart);
    sock.on('typing-stop', handleTypingStop);

    return () => {
      sock.off('new-message', handleNewMessage);
      sock.off('typing-start', handleTypingStart);
      sock.off('typing-stop', handleTypingStop);
    };
  }, [selectedConversation, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/messages/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const err = await response.json();
        alert(err.error || 'Upload failed');
        return;
      }
      const data = await response.json();
      setAttachments(prev => [...prev, data]);
    } catch (error) {
      console.error('File upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || sending || !selectedConversation) return;

    setSending(true);
    // Stop typing indicator
    clearTimeout(typingTimeoutRef.current);
    getSocket().emit('typing-stop', { conversationId: selectedConversation });

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_URL}/messages/conversations/${selectedConversation}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newMessage,
          attachment_ids: attachments.map(a => a.attachmentId),
          gallery_layout: attachments.filter(a => a.file_type && a.file_type.startsWith('image/')).length > 1 ? galleryLayout : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Server also emits via Socket.io; add locally to avoid duplicate via de-dupe in socket handler
        setMessages(prev => prev.find(m => m.id === data.message.id) ? prev : [...prev, data.message]);
        setNewMessage('');
        setAttachments([]);
        setGalleryLayout('auto');
        fetchConversations();
      } else {
        const data = await response.json().catch(() => ({}));
        if (data.code === 'BANNED') alert('Your account is restricted. You cannot send messages.');
        else if (data.code === 'MUTED') alert('Your account is muted. You cannot send messages.');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
          <p>Please <Link to={`${BASE_PATH}/login`}>log in</Link> to view messages.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
          <p>Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`messages-page${selectedConversation ? ' chat-open' : ''}`}>
      {/* Conversations List */}
      <div className="conversations-list">
        <h3 className="conversations-header">Messages</h3>
        {conversations.length > 0 ? (
          conversations.map(conv => (
            <div
              key={conv.id}
              className={`conversation-item ${selectedConversation === conv.id ? 'selected' : ''}`}
              onClick={() => setSelectedConversation(conv.id)}
            >
              <div className="conv-avatar">
                {conv.participants[0]?.profile_picture ? (
                  <SocialImage src={conv.participants[0].profile_picture} alt="" />
                ) : (
                  <div className="conv-avatar-placeholder">
                    {conv.participants[0]?.username?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                {conv.unread_count > 0 && (
                  <span className="unread-badge">{conv.unread_count}</span>
                )}
              </div>
              <div className="conv-info">
                <span className="conv-username">
                  {conv.participants.map(p => `@${p.username}`).join(', ')}
                </span>
                <span className="conv-preview">
                  {conv.last_message ? (conv.last_message.length > 30 ? conv.last_message.substring(0, 30) + '...' : conv.last_message) : 'No messages yet'}
                </span>
              </div>
            </div>
          ))
        ) : (
          <p className="no-conversations">No conversations yet. Message someone from their profile!</p>
        )}
      </div>

      {/* Chat Area */}
      <div className="chat-area">
        {selectedConversation ? (
          <>
            <div className="chat-header">
              <button className="chat-back-btn" onClick={() => setSelectedConversation(null)}>← Back</button>
              {participants.map(p => (
                <Link key={p.id} to={`${BASE_PATH}/${p.username}`} className="chat-participant">
                  {p.profile_picture ? (
                    <SocialImage src={p.profile_picture} alt="" className="chat-avatar" />
                  ) : (
                    <div className="chat-avatar-placeholder">{p.username[0].toUpperCase()}</div>
                  )}
                  <span>@{p.username}</span>
                </Link>
              ))}
              {participants.length > 0 && (
                <div className="chat-call-buttons">
                  <button
                    title="Audio call"
                    className="call-icon-btn"
                    onClick={() => callEventBus.emit({ targetUser: participants[0], type: 'audio' })}
                  >📞</button>
                  <button
                    title="Video call"
                    className="call-icon-btn"
                    onClick={() => callEventBus.emit({ targetUser: participants[0], type: 'video' })}
                  >📹</button>
                </div>
              )}
            </div>

            <div className="messages-container">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`message ${msg.sender_id === user.id ? 'sent' : 'received'}`}
                >
                  {msg.sender_id !== user.id && (
                    <div className="message-avatar">
                      {msg.profile_picture ? (
                        <SocialImage src={msg.profile_picture} alt="" />
                      ) : (
                        <div className="avatar-placeholder">{msg.username[0].toUpperCase()}</div>
                      )}
                    </div>
                  )}
                  <div className="message-content">
                    {msg.content && <p>{renderMessageWithStickers(msg.content, customStickers)}</p>}
                    {(msg.attachments || []).length > 0 && (
                      <MessageGallery attachments={msg.attachments} layout={msg.gallery_layout} />
                    )}
                    {msg.content && detectUrls(msg.content).map(url => (
                      <LinkEmbed key={url} url={url} />
                    ))}
                    <span className="message-time">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
              {otherUserTyping && (
                <div className="typing-indicator">
                  <span className="typing-dots"><span/><span/><span/></span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Attachment preview strip */}
            {attachments.length > 0 && (
              <div className="attachment-preview-strip">
                {attachments.filter(a => a.file_type && a.file_type.startsWith('image/')).length > 1 && (
                  <div className="gallery-layout-picker">
                    <span className="gallery-layout-label">Layout:</span>
                    {[
                      { value: 'auto', label: 'Auto' },
                      { value: '2col', label: '2 col' },
                      { value: '3col', label: '3 col' },
                      { value: '4col', label: '4 col' },
                      { value: '2row', label: '2 row' },
                      { value: '3row', label: '3 row' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`gallery-layout-btn${galleryLayout === opt.value ? ' active' : ''}`}
                        onClick={() => setGalleryLayout(opt.value)}
                      >{opt.label}</button>
                    ))}
                  </div>
                )}
                {attachments.map((att, i) => (
                  <div key={i} className="attachment-preview-item">
                    {att.file_type && att.file_type.startsWith('image/') ? (
                      <SocialImage src={att.file_url} alt={att.file_name} />
                    ) : (
                      <div className="attachment-preview-file">
                        <span>📎</span>
                        <span className="attachment-preview-name">{att.file_name}</span>
                      </div>
                    )}
                    <button
                      className="attachment-remove-btn"
                      onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                    >×</button>
                  </div>
                ))}
              </div>
            )}

            {showStickerPicker && (
              <StickerPicker
                onSelect={(shortcode) => setNewMessage(prev => prev + shortcode)}
                onClose={() => setShowStickerPicker(false)}
                customStickers={customStickers}
                onStickerUploaded={(s) => setCustomStickers(prev => [...prev, { name: s.shortcode, file_url: s.file_url }])}
              />
            )}
            <form onSubmit={handleSendMessage} className="message-input-form">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Attach file"
              >{uploading ? '⏳' : '📎'}</button>
              <button
                type="button"
                className="attach-btn"
                onClick={() => setShowStickerPicker(prev => !prev)}
                title="Stickers"
              >🎨</button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => {
                  if (user?.is_banned || user?.is_muted) return;
                  setNewMessage(e.target.value);
                  const sock = getSocket();
                  sock.emit('typing-start', { conversationId: selectedConversation });
                  clearTimeout(typingTimeoutRef.current);
                  typingTimeoutRef.current = setTimeout(() => {
                    sock.emit('typing-stop', { conversationId: selectedConversation });
                  }, 2000);
                }}
                placeholder={user?.is_banned ? 'Your account is restricted.' : user?.is_muted ? 'You are muted — you cannot send messages.' : 'Type a message...'}
                disabled={user?.is_banned || user?.is_muted}
                className="message-input"
              />
              <button
                type="submit"
                disabled={sending || user?.is_banned || user?.is_muted || (!newMessage.trim() && attachments.length === 0)}
                className="send-button"
              >
                {sending ? '...' : 'Send'}
              </button>
            </form>
          </>
        ) : (
          <div className="no-chat-selected">
            <p>Select a conversation to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
};

// XP drop chime sound (coin/level-up feel)
const playXpSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const t = ctx.currentTime;
    // Two-tone ascending chime
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);
    osc1.frequency.setValueAtTime(523, t);       // C5
    osc1.frequency.setValueAtTime(659, t + 0.1); // E5
    osc2.frequency.setValueAtTime(784, t + 0.1); // G5
    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.35);
    osc1.start(t);
    osc1.stop(t + 0.2);
    osc2.start(t + 0.1);
    osc2.stop(t + 0.35);
  } catch (e) {}
};

// App Component — unified drop notifications (XP + notifications)
const XpDrops = () => {
  const { user } = useAuth();
  const [drops, setDrops] = useState([]);
  const displayedIds = useRef(new Set());

  const addDrop = (drop) => {
    const id = Math.random().toString(36).substr(2, 9);
    setDrops(prev => [...prev, { ...drop, id }]);
    if (drop.isXp) playXpSound();
    setTimeout(() => {
      setDrops(prev => prev.filter(d => d.id !== id));
    }, drop.isXp ? 6000 : 8000);
  };

  const getNotifLink = (data) => {
    if (data.type === 'level_up') return `${BASE_PATH}/skills`;
    if (data.type === 'follow' && data.actorUsername) return `${BASE_PATH}/${data.actorUsername}`;
    if (data.postId) return `${BASE_PATH}/post/${data.postId}`;
    return `${BASE_PATH}/feed`;
  };

  // Real-time drops via Socket.io
  useEffect(() => {
    if (user) {
      const socket = getSocket();

      // XP drops
      const handleXpDrop = (data) => {
        addDrop({ isXp: true, skill_name: data.skillName, xp_gained: data.xpGained });
      };
      socket.on('xp-drop', handleXpDrop);

      // Notification drops (likes, comments, follows, etc.)
      const handleNotifDrop = (data) => {
        addDrop({
          isXp: false,
          title: data.title || 'Notification',
          body: data.body || '',
          type: data.type,
          postId: data.postId,
          actorUsername: data.actorUsername,
          link: getNotifLink(data),
        });
      };
      socket.on('new-notification', handleNotifDrop);

      // Polling fallback for XP from own actions
      const fetchRecentXp = async () => {
        try {
          const token = localStorage.getItem(AUTH_TOKEN_KEY);
          if (!token) return;
          const response = await fetch(`${API_URL}/skills/recent-xp`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            (Array.isArray(data) ? data : []).forEach(item => {
              const key = `${item.skill_name}-${item.xp_gained}-${item.created_at}`;
              if (!displayedIds.current.has(key)) {
                displayedIds.current.add(key);
                addDrop({ isXp: true, skill_name: item.skill_name, xp_gained: item.xp_gained });
              }
            });
          }
        } catch (err) {}
      };
      const interval = setInterval(fetchRecentXp, 5000);

      return () => {
        socket.off('xp-drop', handleXpDrop);
        socket.off('new-notification', handleNotifDrop);
        clearInterval(interval);
      };
    }
  }, [user]);

  const removeDrop = (id) => setDrops(prev => prev.filter(d => d.id !== id));

  return (
    <div style={{
      position: 'fixed',
      top: '80px',
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    }}>
      {drops.map(drop => drop.isXp ? (
        <div key={drop.id} style={{
          background: 'rgba(0, 0, 0, 0.85)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '30px',
          fontSize: '14px',
          fontWeight: 'bold',
          animation: 'xpDropFadeIn 0.3s ease-out, xpDropFadeOut 0.5s ease-in 5.5s forwards',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.1)',
          pointerEvents: 'none',
        }}>
          <span style={{ color: '#ffd700' }}>+{drop.xp_gained}</span>
          <span>{drop.skill_name} XP</span>
          <span style={{ fontSize: '18px' }}>✨</span>
        </div>
      ) : (
        <Link key={drop.id} to={drop.link} onClick={() => removeDrop(drop.id)} style={{
          background: "var(--ui-accent)",
          color: "var(--ui-on-accent)",
          padding: '12px 18px',
          borderRadius: '14px',
          fontSize: '13px',
          animation: 'xpDropFadeIn 0.3s ease-out, xpDropFadeOut 0.5s ease-in 7.5s forwards',
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
          boxShadow: '0 4px 16px rgba(102,126,234,0.4)',
          textDecoration: 'none',
          cursor: 'pointer',
          maxWidth: '280px',
          transition: 'transform 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span style={{ fontWeight: '700', fontSize: '14px' }}>{drop.title}</span>
          <span style={{ opacity: 0.85 }}>{drop.body}</span>
        </Link>
      ))}
      <style>{`
        @keyframes xpDropFadeIn {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes xpDropFadeOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-30px); }
        }
      `}</style>
    </div>
  );
};

// ============================================
// TRENDS PAGE
// ============================================
const TrendsPage = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${BASE_PATH}/api/trends?days=${days}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  const maxScore = data?.words?.[0]?.score || 1;
  const wordSize = score => (1 + (score / maxScore) * 2).toFixed(2);

  return (
    <div className="trends-page">
      <div className="trends-header">
        <h1>🔥 Trending</h1>
        <div className="trends-period-btns">
          {[[1,'24h'],[7,'7d'],[30,'30d']].map(([d, label]) => (
            <button key={d} className={`btn${days === d ? ' active' : ''}`} onClick={() => setDays(d)}>{label}</button>
          ))}
        </div>
      </div>

      {loading && <p className="trends-loading">Loading trends…</p>}

      {!loading && data && (
        <>
          <section className="trends-section">
            <h2 className="trends-section-title">Trending Words</h2>
            <div className="trends-word-cloud">
              {data.words.length === 0
                ? <p style={{ color: "var(--ui-muted)" }}>Not enough posts yet.</p>
                : data.words.map(({ word, score, count }) => (
                    <span key={word} className="trend-word"
                      style={{ fontSize: `${wordSize(score)}rem` }}
                      title={`${count} post${count !== 1 ? 's' : ''} · score ${score}`}>
                      {word}
                    </span>
                  ))
              }
            </div>
          </section>

          <section className="trends-section">
            <h2 className="trends-section-title">Hot Posts</h2>
            {data.posts.length === 0
              ? <p style={{ color: "var(--ui-muted)" }}>No posts in this period.</p>
              : (
                <div className="trend-leaderboard">
                  {data.posts.slice(0, 3).map((post, i) => {
                    const medals = ['🥇','🥈','🥉'];
                    const eng = (post.like_count|0) + (post.comment_count|0)*2 + (post.repost_count|0)*3 + (post.favorite_count|0)*2;
                    return (
                      <Link key={post.id} to={`${BASE_PATH}/post/${post.id}`} className={`trend-lb-card trend-lb-rank-${i+1}`}>
                        <div className="trend-lb-medal">{medals[i]}</div>
                        <div className="trend-lb-meta">
                          <SocialImage src={post.profile_picture || `${BASE_PATH}/images/profile.svg`} alt="" className="trend-avatar" />
                          <span className="trend-post-user">@{post.username}</span>
                        </div>
                        {post.tagline && <p className="trend-lb-tagline">{post.tagline}</p>}
                        <p className="trend-lb-content">{(post.content || '').replace(/<[^>]*>/g, ' ').trim()}</p>
                        <div className="trend-lb-score">
                          <span className="trend-lb-score-num">{eng}</span>
                          <span className="trend-lb-score-label">pts</span>
                        </div>
                        <div className="trend-post-stats">
                          <span><SocialImage src={`${BASE_PATH}/Skills/Love.svg`} alt="" style={{ width: 14, height: 14, verticalAlign: 'middle' }} /> {post.like_count}</span>
                          <span>💬 {post.comment_count}</span>
                          <span>🔁 {post.repost_count}</span>
                          <span>⭐ {post.favorite_count}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )
            }
            {data.posts.length > 3 && (
              <div className="trend-ranked-list">
                {data.posts.slice(3).map((post, i) => {
                  const rank = i + 4;
                  const eng = (post.like_count|0) + (post.comment_count|0)*2 + (post.repost_count|0)*3 + (post.favorite_count|0)*2;
                  return (
                    <Link key={post.id} to={`${BASE_PATH}/post/${post.id}`} className="trend-ranked-row">
                      <span className="trend-ranked-num">#{rank}</span>
                      {post.profile_picture
                        ? <SocialImage src={post.profile_picture} alt="" className="trend-avatar" />
                        : <div className="trend-avatar-placeholder" />}
                      <div className="trend-ranked-text">
                        <span className="trend-ranked-user">@{post.username}</span>
                        {post.tagline && <span className="trend-ranked-tagline">{post.tagline}</span>}
                      </div>
                      <span className="trend-ranked-score">{eng} pts</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

// ============================================
// MOBILE BOTTOM NAVIGATION
// ============================================
const MobileNav = () => {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <nav className="mobile-nav">
      <Link to={`${BASE_PATH}/feed`} className="mobile-nav-btn">
        <span className="mobile-nav-icon"><SocialImage src={`${BASE_PATH}/images/explore-trends.svg`} alt="Explore" className="mobile-nav-svg" /></span>
        <span className="mobile-nav-label">Explore</span>
      </Link>
      <Link to={`${BASE_PATH}/skills`} className="mobile-nav-btn">
        <span className="mobile-nav-icon"><SocialImage src={`${BASE_PATH}/images/skills.svg`} alt="Skills" className="mobile-nav-svg" /></span>
        <span className="mobile-nav-label">Skills</span>
      </Link>
      <Link to={`${BASE_PATH}/marketplace`} className="mobile-nav-btn">
        <span className="mobile-nav-icon"><SocialImage src={`${BASE_PATH}/images/market.svg`} alt="Market" className="mobile-nav-svg" /></span>
        <span className="mobile-nav-label">Market</span>
      </Link>
      <Link to={`${BASE_PATH}/create`} className="mobile-nav-btn">
        <span className="mobile-nav-icon"><SocialImage src={`${BASE_PATH}/Skills/Create.svg`} alt="Create" className="mobile-nav-svg" /></span>
        <span className="mobile-nav-label">Create</span>
      </Link>
      <Link to={`${BASE_PATH}/tribes`} className="mobile-nav-btn">
        <span className="mobile-nav-icon"><SocialImage src={`${BASE_PATH}/images/tribe.svg`} alt="Tribes" className="mobile-nav-svg" /></span>
        <span className="mobile-nav-label">Tribes</span>
      </Link>
      <Link to={`${BASE_PATH}/messages`} className="mobile-nav-btn">
        <span className="mobile-nav-icon"><SocialImage src={`${BASE_PATH}/Skills/Message.svg`} alt="Messages" className="mobile-nav-svg" /></span>
        <span className="mobile-nav-label">Messages</span>
      </Link>
      <Link to={`${BASE_PATH}/${user.username}`} className="mobile-nav-btn">
        <span className="mobile-nav-icon">
          {user.profile_picture
            ? <SocialImage src={user.profile_picture} alt="" className="mobile-nav-avatar" />
            : <SocialImage src={`${BASE_PATH}/images/profile.svg`} alt="Profile" className="mobile-nav-svg" />}
        </span>
        <span className="mobile-nav-label">Profile</span>
      </Link>
    </nav>
  );
};

// ============================================
// CREATE POST PAGE
// ============================================
const CreatePostPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!user) return <Navigate to={`${BASE_PATH}/login`} replace />;
  return (
    <div className="create-post-page">
      <div className="create-post-header">
        <button className="create-post-back" onClick={() => navigate(-1)}>←</button>
        <span className="create-post-title">New Post</span>
      </div>
      <div className="create-post-body">
        <PostEditor onPostCreated={() => navigate(`${BASE_PATH}/feed`)} />
      </div>
    </div>
  );
};

// ============================================
// REPORT MODAL
// ============================================
const ReportModal = ({ type, id, onClose }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const reasons = [
    { value: 'harassment', label: 'Harassment or bullying' },
    { value: 'hate_speech', label: 'Hate speech or discrimination' },
    { value: 'spam', label: 'Spam or scam' },
    { value: 'inappropriate', label: 'Inappropriate content' },
    { value: 'doxxing', label: 'Doxxing (sharing personal info)' },
    { value: 'outing', label: 'Outing (disclosing identity without consent)' },
    { value: 'self_harm', label: 'Self-harm or suicide' },
    { value: 'misinformation', label: 'Misinformation' },
    { value: 'other', label: 'Other' },
  ];

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reported_type: type, reported_id: id, reason, description: description.trim() || null })
      });
      if (!res.ok) { const data = await res.json(); throw new Error(data.error); }
      setSubmitted(true);
    } catch (err) {
      alert(err.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="report-modal-overlay" onClick={onClose}>
      <div className="report-modal" onClick={e => e.stopPropagation()}>
        {submitted ? (
          <>
            <h3 style={{ marginTop: 0 }}>Report Submitted</h3>
            <p style={{ color: "var(--ui-muted)" }}>Thank you. Our moderation team will review this report.</p>
            <button onClick={onClose} className="btn btn-primary" style={{ width: '100%' }}>Close</button>
          </>
        ) : (
          <>
            <h3 style={{ marginTop: 0 }}>Report {type}</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Reason</label>
              <select value={reason} onChange={e => setReason(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: "1px solid var(--ui-border)", fontSize: 14 }}>
                <option value="">Select a reason...</option>
                {reasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Details (optional)</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Provide additional context..." rows={3} style={{ width: '100%', padding: '10px', borderRadius: 8, border: "1px solid var(--ui-border)", fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
              <button onClick={handleSubmit} disabled={!reason || submitting} className="btn btn-primary" style={{ flex: 1, opacity: !reason || submitting ? 0.5 : 1 }}>
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// LEGAL DOCUMENT PAGE
// ============================================
const LegalDocPage = () => {
  const { category, slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/docs/${category}/${slug}`);
        if (!res.ok) throw new Error('Document not found');
        const data = await res.json();
        setDoc(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, slug]);

  if (loading) return <div className="container" style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (error) return <div className="container" style={{ padding: 40, textAlign: 'center', color: '#e74c3c' }}>{error}</div>;

  // Simple markdown-to-HTML conversion for display
  const renderMarkdown = (md) => {
    let html = md
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    return `<p>${html}</p>`;
  };

  return (
    <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: '20px 20px 60px' }}>
      <div className="card" style={{ padding: '30px 24px' }}>
        <div className="legal-doc-content" dangerouslySetInnerHTML={{ __html: richMediaHtml(renderMarkdown(doc.content)) }} />
        <p style={{ fontSize: 12, color: "var(--ui-muted)", marginTop: 24, borderTop: "1px solid var(--ui-border)", paddingTop: 12 }}>
          Last updated: {new Date(doc.lastUpdated).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

// ============================================
// RESTRICTED BANNER (shown when user is banned/suspended)
// ============================================
const RestrictedBanner = () => {
  const { user, refreshUser } = useAuth();
  const [verifying, setVerifying] = useState(false);

  const handleAgeVerify = async () => {
    setVerifying(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await fetch(`${API_URL}/auth/verify-age`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await refreshUser();
    } catch {} finally { setVerifying(false); }
  };

  if (user && !user.is_age_verified) {
    return (
      <div className="age-verify-banner">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: 0 }}>
          <input type="checkbox" disabled={verifying} onChange={handleAgeVerify} style={{ width: 18, height: 18, cursor: 'pointer' }} />
          {verifying ? 'Verifying...' : 'Are you older than 13? Check to confirm and unlock posting, commenting, and messaging.'}
        </label>
      </div>
    );
  }

  if (user?.is_suspended) {
    const expiryDate = user.suspension_expires ? new Date(user.suspension_expires).toLocaleDateString() : 'Unknown';
    return (
      <div className="account-restricted-banner" style={{ background: '#f59e0b' }}>
        <div>
          Your account is temporarily suspended until {expiryDate}.
          {user.suspension_reason && <span> Reason: {user.suspension_reason}</span>}
          <br />
          <span style={{ fontSize: 12 }}>To appeal, email <a href="mailto:eve@belonging.lgbt" style={{ color: "var(--ui-on-accent)", textDecoration: 'underline' }}>eve@belonging.lgbt</a> within 30 days.</span>
        </div>
      </div>
    );
  }

  if (!user?.is_banned && !user?.is_muted) return null;
  return (
    <div className="account-restricted-banner">
      <div>
        {user.is_banned ? 'Your account is currently restricted. You can browse content but cannot post or interact.' : 'Your account is muted. You can browse but cannot post or comment.'}
        <br />
        <span style={{ fontSize: 12 }}>To appeal, email <a href="mailto:eve@belonging.lgbt" style={{ color: "var(--ui-on-accent)", textDecoration: 'underline' }}>eve@belonging.lgbt</a> within 30 days.</span>
      </div>
    </div>
  );
};

// ============================================
// ADMIN PAGE
// ============================================
const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [tab, setTab] = useState('users');
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportFilter, setReportFilter] = useState('pending');
  const [importSources, setImportSources] = useState([]);
  const [importFeeds, setImportFeeds] = useState([]);
  const [importSettings, setImportSettings] = useState({ tumblr_api_key: '' });
  const [importForm, setImportForm] = useState({ platform: 'tumblr', account_name: '', custom_feed_id: '' });
  const [importStatus, setImportStatus] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== 'admin' && user.role !== 'moderator')) { navigate('/'); return; }
    fetchUsers();
    fetchReports();
    fetchImportSources();
    fetchImportFeeds();
    fetchImportSettings();
  }, [user, authLoading]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/reports?status=${reportFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setReports(await res.json());
    } catch (e) { console.error(e); }
    setReportsLoading(false);
  };

  useEffect(() => { if (user && (user.role === 'admin' || user.role === 'moderator')) fetchReports(); }, [reportFilter]);

  const fetchImportSources = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/social-import/sources`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setImportSources(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchImportFeeds = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/feeds`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setImportFeeds(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchImportSettings = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/social-import/settings`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setImportSettings(await res.json());
    } catch (e) { console.error(e); }
  };

  const saveImportSettings = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await fetch(`${API_URL}/admin/social-import/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tumblr_api_key: importSettings.tumblr_api_key })
      });
      setImportStatus('Settings saved.');
    } catch (e) { setImportStatus('Failed to save settings.'); }
  };

  const addImportSource = async () => {
    if (!importForm.account_name.trim()) return;
    setImportLoading(true);
    setImportStatus('Importing...');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/social-import/sources`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(importForm)
      });
      const data = await res.json();
      if (!res.ok) { setImportStatus(`Error: ${data.error}`); }
      else {
        setImportStatus(data.error ? `Added but fetch failed: ${data.error}` : `Done — ${data.imported} new post(s) imported.`);
        setImportForm({ platform: 'tumblr', account_name: '', custom_feed_id: importForm.custom_feed_id });
        fetchImportSources();
      }
    } catch (e) { setImportStatus('Request failed.'); }
    setImportLoading(false);
  };

  const refetchSource = async (id) => {
    setImportStatus('Fetching...');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/social-import/sources/${id}/fetch`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setImportStatus(res.ok ? `Fetched — ${data.imported} new post(s).` : `Error: ${data.error}`);
      fetchImportSources();
    } catch (e) { setImportStatus('Request failed.'); }
  };

  const deleteSource = async (id) => {
    if (!window.confirm('Remove this import source? (Existing imported posts are kept.)')) return;
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await fetch(`${API_URL}/admin/social-import/sources/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchImportSources();
    } catch (e) {}
  };

  const adminAction = async (userId, action, body) => {
    setSaving(s => ({ ...s, [`${userId}-${action}`]: true }));
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/users/${userId}/${action}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        await fetchUsers();
      } else {
        const data = await res.json();
        alert(data.error || 'Action failed');
      }
    } catch (e) { alert('Request failed'); }
    setSaving(s => ({ ...s, [`${userId}-${action}`]: false }));
  };

  const handleSuspend = async (userId) => {
    const days = prompt('Suspend for how many days? (1-365)');
    if (!days || isNaN(days) || days < 1 || days > 365) return;
    const reason = prompt('Reason for suspension (optional):') || '';
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ duration_days: parseInt(days), reason })
      });
      if (res.ok) { alert('User suspended'); await fetchUsers(); }
      else { const data = await res.json(); alert(data.error || 'Failed'); }
    } catch (e) { alert('Request failed'); }
  };

  const handleLiftSuspension = async (userId) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/users/${userId}/suspend`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { alert('Suspension lifted'); await fetchUsers(); }
      else { const data = await res.json(); alert(data.error || 'Failed'); }
    } catch (e) { alert('Request failed'); }
  };

  const handleReportAction = async (reportId, status) => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API_URL}/admin/reports/${reportId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) { await fetchReports(); }
      else { const data = await res.json(); alert(data.error || 'Failed'); }
    } catch (e) { alert('Request failed'); }
  };

  const filtered = users.filter(u =>
    !search || u.username?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (!user || (user.role !== 'admin' && user.role !== 'moderator')) return null;

  const tabStyle = (t, idx = 0) => ({
    padding: '10px 20px', cursor: 'pointer', border: tab === t ? "2px solid var(--ui-border)" : 'none', fontWeight: tab === t ? 900 : 600, fontSize: 14,
    background: tab === t ? "var(--ui-accent)" : 'transparent', color: tab === t ? "var(--ui-on-accent)" : "var(--ui-muted)",
    borderRadius: '8px 8px 0 0',
    textShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.4)' : 'none'
  });

  return (
    <div className="admin-page">
      <h1>Admin Dashboard</h1>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        <button style={tabStyle('users', 0)} onClick={() => setTab('users')}>Users</button>
        <button style={tabStyle('reports', 1)} onClick={() => setTab('reports')}>
          Reports {reports.length > 0 && <span style={{ background: '#e74c3c', color: '#fff', borderRadius: 10, padding: '2px 7px', fontSize: 11, marginLeft: 6 }}>{reports.length}</span>}
        </button>
        <button style={tabStyle('import', 2)} onClick={() => setTab('import')}>Social Import</button>
      </div>

      {tab === 'users' && (
        <>
          <input
            className="admin-search"
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {loading ? <p>Loading...</p> : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Member</th>
                  <th>Banned</th>
                  <th>Muted</th>
                  <th>Upload Limit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <AdminUserRow key={u.id} u={u} currentUser={user} adminAction={adminAction} saving={saving} onSuspend={handleSuspend} onLiftSuspension={handleLiftSuspension} />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'reports' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['pending', 'reviewed', 'actioned', 'dismissed'].map(s => (
              <button key={s} onClick={() => setReportFilter(s)} style={{
                padding: '6px 14px', borderRadius: 6, border: reportFilter === s ? "2px solid var(--ui-border)" : "1px solid var(--ui-border)",
                background: reportFilter === s ? "var(--ui-muted-surface)" : "var(--ui-surface)", cursor: 'pointer', fontWeight: 600, fontSize: 13,
                textTransform: 'capitalize'
              }}>{s}</button>
            ))}
          </div>
          {reportsLoading ? <p>Loading reports...</p> : reports.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: "var(--ui-muted)" }}>No {reportFilter} reports</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {reports.map(r => (
                <div key={r.id} className="card" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.reason?.replace('_', ' ')}</span>
                      <span style={{ color: "var(--ui-muted)", fontSize: 12, marginLeft: 8 }}>#{r.id}</span>
                    </div>
                    <span style={{ fontSize: 12, color: "var(--ui-muted)" }}>{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ui-muted)", marginBottom: 8 }}>
                    <strong>Type:</strong> {r.reported_type} &middot; <strong>Reporter:</strong> @{r.reporter_username || r.reporter_id}
                  </div>
                  {r.description && <p style={{ fontSize: 13, color: "var(--ui-text)", margin: '8px 0', padding: '8px 12px', background: "var(--ui-muted-surface)", borderRadius: 6 }}>{r.description}</p>}
                  {r.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => handleReportAction(r.id, 'reviewed')} className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }}>Mark Reviewed</button>
                      <button onClick={() => handleReportAction(r.id, 'actioned')} className="btn btn-primary" style={{ fontSize: 12, padding: '6px 12px' }}>Action Taken</button>
                      <button onClick={() => handleReportAction(r.id, 'dismissed')} className="btn" style={{ fontSize: 12, padding: '6px 12px', background: 'transparent', color: "var(--ui-muted)", border: "1px solid var(--ui-border)", borderRadius: 6, cursor: 'pointer' }}>Dismiss</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'import' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Tumblr API Key */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 12 }}>Tumblr API Key</h3>
            <p style={{ fontSize: 13, color: "var(--ui-muted)", marginBottom: 10 }}>
              Get a free consumer key at tumblr.com/oauth/apps (create an app).
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="password"
                value={importSettings.tumblr_api_key}
                onChange={e => setImportSettings(s => ({ ...s, tumblr_api_key: e.target.value }))}
                placeholder="Tumblr consumer key"
                style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: "1px solid var(--ui-border)", fontSize: 14 }}
              />
              <button onClick={saveImportSettings} className="btn btn-primary" style={{ padding: '8px 16px' }}>Save</button>
            </div>
          </div>

          {/* Add new source */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 12 }}>Import from Account</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={importForm.platform}
                  onChange={e => setImportForm(f => ({ ...f, platform: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: 6, border: "1px solid var(--ui-border)", fontSize: 14 }}
                >
                  <option value="tumblr">Tumblr</option>
                  <option value="facebook">Facebook</option>
                </select>
                <input
                  type="text"
                  value={importForm.account_name}
                  onChange={e => setImportForm(f => ({ ...f, account_name: e.target.value }))}
                  placeholder={importForm.platform === 'tumblr' ? 'blog-name or blog.tumblr.com' : 'Page name or username'}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: "1px solid var(--ui-border)", fontSize: 14 }}
                />
              </div>
              <select
                value={importForm.custom_feed_id}
                onChange={e => setImportForm(f => ({ ...f, custom_feed_id: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 6, border: "1px solid var(--ui-border)", fontSize: 14 }}
              >
                <option value="">No custom feed (posts saved but not in a feed)</option>
                {importFeeds.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              {importForm.platform === 'facebook' && (
                <p style={{ fontSize: 12, color: '#e67e22', margin: 0 }}>
                  Note: Facebook scraping is limited — only public pages with basic HTML may work. Results vary.
                </p>
              )}
              <button
                onClick={addImportSource}
                disabled={importLoading || !importForm.account_name.trim()}
                className="btn btn-primary"
                style={{ alignSelf: 'flex-start', padding: '8px 20px' }}
              >
                {importLoading ? 'Importing...' : 'Import Posts'}
              </button>
              {importStatus && <p style={{ fontSize: 13, color: importStatus.startsWith('Error') ? '#e74c3c' : '#27ae60', margin: 0 }}>{importStatus}</p>}
            </div>
          </div>

          {/* Saved sources */}
          {importSources.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ marginBottom: 12 }}>Saved Import Sources</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {importSources.map(src => (
                  <div key={src.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: "var(--ui-muted-surface)", borderRadius: 8, gap: 12 }}>
                    <div>
                      <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{src.platform}</span>
                      <span style={{ margin: '0 6px', color: "var(--ui-muted)" }}>·</span>
                      <span>{src.account_name}</span>
                      {src.feed_name && <span style={{ marginLeft: 8, fontSize: 12, color: "var(--ui-accent)" }}>→ {src.feed_name}</span>}
                      <div style={{ fontSize: 12, color: "var(--ui-muted)", marginTop: 2 }}>
                        {src.post_count} posts · {src.last_fetched ? `Last fetched ${new Date(src.last_fetched).toLocaleString()}` : 'Never fetched'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => refetchSource(src.id)} className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}>Fetch new</button>
                      <button onClick={() => deleteSource(src.id)} style={{ fontSize: 12, padding: '5px 12px', background: 'transparent', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: 6, cursor: 'pointer' }}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminUserRow = ({ u, currentUser, adminAction, saving, onSuspend, onLiftSuspension }) => {
  const [limitInput, setLimitInput] = useState(u.upload_limit_mb != null ? String(u.upload_limit_mb) : '');
  const [ipLinks, setIpLinks] = useState(null);
  const [showIpLinks, setShowIpLinks] = useState(false);
  const isSelf = u.id === currentUser.id;
  const isAdmin = currentUser.role === 'admin';

  const fetchIpLinks = async () => {
    if (ipLinks) { setShowIpLinks(!showIpLinks); return; }
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await fetch(`/api/admin/users/${u.id}/ip-links`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setIpLinks(Array.isArray(data) ? data : []);
      setShowIpLinks(true);
    } catch { setIpLinks([]); setShowIpLinks(true); }
  };

  return (
    <tr>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {u.profile_picture && <SocialImage src={u.profile_picture} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />}
          <span>@{u.username}</span>
        </div>
      </td>
      <td>
        <select
          value={u.role}
          disabled={!isAdmin || isSelf || (u.handle === 'eve' || u.username === 'eve')}
          onChange={e => adminAction(u.id, 'role', { role: e.target.value })}
          className="admin-role-select"
        >
          <option value="user">user</option>
          <option value="moderator">moderator</option>
          <option value="admin">admin</option>
        </select>
      </td>
      <td><span style={{ color: u.is_member ? '#27ae60' : "var(--ui-muted)" }}>{u.is_member ? 'Yes' : 'No'}</span></td>
      <td><span style={{ color: u.is_banned ? '#e74c3c' : "var(--ui-muted)" }}>{u.is_banned ? 'Yes' : 'No'}</span></td>
      <td><span style={{ color: u.is_muted ? '#e67e22' : "var(--ui-muted)" }}>{u.is_muted ? 'Yes' : 'No'}</span></td>
      <td>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <input
            type="number"
            min="1"
            placeholder="MB (blank=default)"
            value={limitInput}
            onChange={e => setLimitInput(e.target.value)}
            className="admin-limit-input"
          />
          <button
            className="admin-btn-save"
            disabled={saving[`${u.id}-upload-limit`]}
            onClick={() => adminAction(u.id, 'upload-limit', { upload_limit_mb: limitInput ? parseInt(limitInput) : null })}
          >
            {saving[`${u.id}-upload-limit`] ? '...' : 'Save'}
          </button>
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {!isSelf && (
            <>
              {isAdmin && (
                <button
                  className={u.is_banned ? 'admin-btn-unban' : 'admin-btn-ban'}
                  disabled={saving[`${u.id}-ban`]}
                  onClick={() => adminAction(u.id, 'ban', { is_banned: !u.is_banned })}
                >
                  {saving[`${u.id}-ban`] ? '...' : u.is_banned ? 'Unban' : 'Ban'}
                </button>
              )}
              <button
                className={u.is_muted ? 'admin-btn-unmute' : 'admin-btn-mute'}
                disabled={saving[`${u.id}-mute`]}
                onClick={() => adminAction(u.id, 'mute', { is_muted: !u.is_muted })}
              >
                {saving[`${u.id}-mute`] ? '...' : u.is_muted ? 'Unmute' : 'Mute'}
              </button>
              {isAdmin && (
                <button onClick={() => onSuspend(u.id)} style={{
                  padding: '4px 10px', fontSize: 12, borderRadius: 4, border: '1px solid #f59e0b',
                  background: 'transparent', color: '#f59e0b', cursor: 'pointer', fontWeight: 600
                }}>Suspend</button>
              )}
              {isAdmin && u.is_suspended && (
                <button onClick={() => onLiftSuspension(u.id)} style={{
                  padding: '4px 10px', fontSize: 12, borderRadius: 4, border: '1px solid #22c55e',
                  background: '#22c55e', color: '#fff', cursor: 'pointer', fontWeight: 600
                }}>Lift Suspension</button>
              )}
              {isAdmin && (
                <button onClick={fetchIpLinks} style={{
                  padding: '4px 10px', fontSize: 12, borderRadius: 4, border: "1px solid var(--ui-border)",
                  background: 'transparent', color: "var(--ui-accent)", cursor: 'pointer', fontWeight: 600
                }}>IP Links</button>
              )}
            </>
          )}
          {isSelf && <span style={{ color: "var(--ui-muted)", fontSize: '12px' }}>You</span>}
        </div>
        {showIpLinks && ipLinks && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--ui-muted-surface)', borderRadius: 6, fontSize: 12 }}>
            {ipLinks.length === 0 ? (
              <span style={{ color: "var(--ui-muted)" }}>No linked accounts found</span>
            ) : (
              <div>
                <strong style={{ color: "var(--ui-accent)" }}>{ipLinks.length} linked account(s):</strong>
                {ipLinks.map(l => (
                  <div key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    <span>@{l.username}</span>
                    {l.is_banned && <span style={{ color: '#e74c3c', fontSize: 10 }}>BANNED</span>}
                    {l.is_muted && <span style={{ color: '#f59e0b', fontSize: 10 }}>MUTED</span>}
                    <span style={{ color: "var(--ui-muted)" }}>joined {new Date(l.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

const CRISIS_RESOURCES = [
  { name: 'Trans Lifeline', phone: '877-565-8860', url: 'https://translifeline.org' },
  { name: 'Trevor Project', phone: '866-488-7386', url: 'https://www.thetrevorproject.org' },
  { name: '988 Suicide & Crisis Lifeline', phone: '988', url: 'https://988lifeline.org' },
  { name: 'Crisis Text Line', phone: 'Text HOME to 741741', url: 'https://www.crisistextline.org' },
];

const SettingsPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ notify_likes: true, notify_comments: true, notify_follows: true, notify_messages: true });
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  // MFA
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [showMfaDisable, setShowMfaDisable] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    fetch(`/api/auth/notification-prefs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setNotifPrefs(d)).catch(() => {});
    fetch(`/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setIsPrivate(!!d.is_private); setMfaEnabled(!!d.mfa_enabled); }).catch(() => {});
  }, []);

  if (!user) return <div className="container"><h2>Settings</h2><ThemeSetting /><p>Please log in to view account settings.</p></div>;

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`/api/auth/export`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `belonging-data-export-${user.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePassword) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`/api/auth/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: deletePassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      logout();
    } catch (err) {
      alert(err.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const toggleNotifPref = async (key) => {
    const newVal = !notifPrefs[key];
    setNotifPrefs(p => ({ ...p, [key]: newVal }));
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    fetch(`/api/auth/notification-prefs`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ [key]: newVal })
    }).catch(() => setNotifPrefs(p => ({ ...p, [key]: !newVal })));
  };

  const togglePrivacy = async () => {
    setPrivacyLoading(true);
    const newVal = !isPrivate;
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await fetch(`/api/auth/privacy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_private: newVal })
      });
      setIsPrivate(newVal);
    } catch { setIsPrivate(!newVal); }
    setPrivacyLoading(false);
  };

  const handleMfaSetup = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await fetch(`/api/auth/mfa/setup`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setMfaSetup(data);
    } catch { alert('Failed to start MFA setup'); }
  };

  const handleMfaVerify = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await fetch(`/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: mfaCode })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setMfaEnabled(true); setMfaSetup(null); setMfaCode('');
      alert('Two-factor authentication enabled!');
    } catch (err) { alert(err.message || 'Invalid code'); }
  };

  const handleMfaDisable = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    try {
      const res = await fetch(`/api/auth/mfa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: mfaDisablePassword })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setMfaEnabled(false); setShowMfaDisable(false); setMfaDisablePassword('');
    } catch (err) { alert(err.message || 'Failed to disable MFA'); }
  };

  const sectionTitle = { marginTop: 0, marginBottom: 12, fontSize: 14, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: '0.5px' };
  const btnBlock = { display: 'block', textAlign: 'center', marginBottom: 8 };
  const toggleStyle = (on) => ({
    width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
    background: on ? "var(--ui-accent)" : "var(--ui-muted-surface)", position: 'relative', transition: 'background 0.2s'
  });
  const toggleDot = (on) => ({
    position: 'absolute', top: 2, left: on ? 22 : 2, width: 20, height: 20,
    borderRadius: 10, background: on ? 'var(--ui-on-accent)' : 'var(--ui-text)', transition: 'left 0.2s'
  });

  return (
    <div className="container" style={{ maxWidth: 600, margin: '0 auto', padding: '20px' }}>
      <h2>Settings</h2>
      <ThemeSetting />

      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <h3 style={sectionTitle}>Account</h3>
        <Link to={`${BASE_PATH}/edit-profile`} className="btn btn-secondary" style={btnBlock}>Edit Profile</Link>
        <Link to={`${BASE_PATH}/edit-profile`} className="btn btn-secondary" style={btnBlock}>Change Password</Link>
        <button onClick={handleExport} className="btn btn-secondary" disabled={exporting} style={{ ...btnBlock, width: '100%', cursor: 'pointer' }}>
          {exporting ? 'Preparing...' : 'Download My Data'}
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <span style={{ fontSize: 14 }}>Private Account</span>
          <button onClick={togglePrivacy} disabled={privacyLoading} style={toggleStyle(isPrivate)}>
            <div style={toggleDot(isPrivate)} />
          </button>
        </div>
        <p style={{ fontSize: 11, color: "var(--ui-muted)", marginTop: 0 }}>When enabled, only approved followers can see your posts.</p>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <h3 style={sectionTitle}>Notifications</h3>
        {[
          { key: 'notify_likes', label: 'Likes' },
          { key: 'notify_comments', label: 'Comments' },
          { key: 'notify_follows', label: 'Follows' },
          { key: 'notify_messages', label: 'Messages' },
        ].map(({ key, label }) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: "1px solid var(--ui-border)" }}>
            <span style={{ fontSize: 14 }}>{label}</span>
            <button onClick={() => toggleNotifPref(key)} style={toggleStyle(notifPrefs[key])}>
              <div style={toggleDot(notifPrefs[key])} />
            </button>
          </div>
        ))}
      </div>

      {(user.role === 'admin' || user.role === 'moderator') && (
        <div className="card" style={{ marginBottom: 16, padding: 20 }}>
          <h3 style={sectionTitle}>Two-Factor Authentication</h3>
          {mfaEnabled ? (
            <div>
              <p style={{ fontSize: 13, color: '#22c55e', fontWeight: 600, marginBottom: 8 }}>MFA is enabled</p>
              {!showMfaDisable ? (
                <button onClick={() => setShowMfaDisable(true)} className="btn btn-secondary" style={{ ...btnBlock, width: '100%' }}>Disable MFA</button>
              ) : (
                <div>
                  <input type="password" placeholder="password" value={mfaDisablePassword} onChange={e => setMfaDisablePassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: "1px solid var(--ui-border)", marginBottom: 8, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setShowMfaDisable(false)} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
                    <button onClick={handleMfaDisable} disabled={!mfaDisablePassword} className="btn" style={{ flex: 1, background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', cursor: 'pointer', fontWeight: 600 }}>Disable</button>
                  </div>
                </div>
              )}
            </div>
          ) : mfaSetup ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 13, marginBottom: 8 }}>Scan this QR code with your authenticator app:</p>
              <SocialImage src={mfaSetup.qrCode} alt="MFA QR Code" style={{ maxWidth: 200, margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: 11, color: "var(--ui-muted)", wordBreak: 'break-all' }}>Manual key: {mfaSetup.secret}</p>
              <input type="text" placeholder="Enter 6-digit code" value={mfaCode} onChange={e => setMfaCode(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: "1px solid var(--ui-border)", marginBottom: 8, boxSizing: 'border-box', textAlign: 'center', letterSpacing: 4, fontSize: 18 }} />
              <button onClick={handleMfaVerify} disabled={mfaCode.length < 6} className="btn" style={{ width: '100%', background: "var(--ui-accent)", color: "var(--ui-on-accent)", border: 'none', borderRadius: 8, padding: '12px', cursor: 'pointer', fontWeight: 600 }}>Verify & Enable</button>
            </div>
          ) : (
            <button onClick={handleMfaSetup} className="btn btn-secondary" style={{ ...btnBlock, width: '100%' }}>Set Up MFA</button>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <h3 style={sectionTitle}>Legal</h3>
        <Link to={`${BASE_PATH}/docs/legal/terms-of-service`} className="btn btn-secondary" style={btnBlock}>Terms of Service</Link>
        <Link to={`${BASE_PATH}/docs/legal/privacy-policy`} className="btn btn-secondary" style={btnBlock}>Privacy Policy</Link>
        <Link to={`${BASE_PATH}/docs/legal/community-guidelines`} className="btn btn-secondary" style={btnBlock}>Community Guidelines</Link>
        <Link to={`${BASE_PATH}/docs/legal/safety-anti-harassment-policy`} className="btn btn-secondary" style={btnBlock}>Safety & Anti-Harassment</Link>
        <Link to={`${BASE_PATH}/docs/legal/acceptable-use-policy`} className="btn btn-secondary" style={btnBlock}>Acceptable Use Policy</Link>
        <Link to={`${BASE_PATH}/docs/legal/accessibility-statement`} className="btn btn-secondary" style={btnBlock}>Accessibility Statement</Link>
        <Link to={`${BASE_PATH}/docs/legal/advertising-policy`} className="btn btn-secondary" style={btnBlock}>Advertising Policy</Link>
        <Link to={`${BASE_PATH}/docs/legal/ccpa-notice`} className="btn btn-secondary" style={btnBlock}>CCPA Notice</Link>
        <Link to={`${BASE_PATH}/docs/legal/coppa-policy`} className="btn btn-secondary" style={btnBlock}>Children's Privacy (COPPA)</Link>
        <Link to={`${BASE_PATH}/docs/legal/consumer-health-data`} className="btn btn-secondary" style={btnBlock}>Consumer Health Data</Link>
        <Link to={`${BASE_PATH}/docs/legal/content-moderation`} className="btn btn-secondary" style={btnBlock}>Content Moderation</Link>
        <Link to={`${BASE_PATH}/docs/legal/cookies-policy`} className="btn btn-secondary" style={btnBlock}>Cookies Policy</Link>
        <Link to={`${BASE_PATH}/docs/legal/data-protection-policy`} className="btn btn-secondary" style={btnBlock}>Data Protection</Link>
        <Link to={`${BASE_PATH}/docs/legal/data-retention`} className="btn btn-secondary" style={btnBlock}>Data Retention</Link>
        <Link to={`${BASE_PATH}/docs/legal/disclaimer`} className="btn btn-secondary" style={btnBlock}>Disclaimer</Link>
        <Link to={`${BASE_PATH}/docs/legal/dmca-policy`} className="btn btn-secondary" style={btnBlock}>DMCA / Copyright</Link>
        <Link to={`${BASE_PATH}/docs/legal/gdpr-notice`} className="btn btn-secondary" style={btnBlock}>GDPR Notice</Link>
        <Link to={`${BASE_PATH}/docs/legal/law-enforcement`} className="btn btn-secondary" style={btnBlock}>Law Enforcement Guidelines</Link>
        <Link to={`${BASE_PATH}/docs/legal/third-party-services`} className="btn btn-secondary" style={btnBlock}>Third-Party Services</Link>
        <Link to={`${BASE_PATH}/dmca`} className="btn btn-secondary" style={btnBlock}>Submit DMCA Request</Link>
        <Link to={`${BASE_PATH}/transparency`} className="btn btn-secondary" style={{ ...btnBlock, marginBottom: 0 }}>Transparency Report</Link>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <h3 style={sectionTitle}>Marketplace</h3>
        <Link to={`${BASE_PATH}/docs/marketplace/seller-agreement`} className="btn btn-secondary" style={btnBlock}>Seller Agreement</Link>
        <Link to={`${BASE_PATH}/docs/marketplace/buyer-protection`} className="btn btn-secondary" style={btnBlock}>Buyer Protection</Link>
        <Link to={`${BASE_PATH}/docs/marketplace/refund-policy`} className="btn btn-secondary" style={btnBlock}>Refund & Returns Policy</Link>
        <Link to={`${BASE_PATH}/docs/marketplace/shipping-policy`} className="btn btn-secondary" style={btnBlock}>Shipping Policy</Link>
        <Link to={`${BASE_PATH}/docs/marketplace/prohibited-items`} className="btn btn-secondary" style={btnBlock}>Prohibited Items</Link>
        <Link to={`${BASE_PATH}/docs/marketplace/fee-structure`} className="btn btn-secondary" style={{ ...btnBlock, marginBottom: 0 }}>Fee Structure</Link>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <h3 style={sectionTitle}>Crisis Resources</h3>
        <p style={{ fontSize: 13, color: "var(--ui-muted)", marginTop: 0, marginBottom: 12 }}>If you or someone you know is in crisis, please reach out:</p>
        {CRISIS_RESOURCES.map(r => (
          <div key={r.name} style={{ marginBottom: 10, padding: '10px 12px', background: "var(--ui-muted-surface)", borderRadius: 8 }}>
            <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: "var(--ui-accent)", fontSize: 14, textDecoration: 'none' }}>{r.name}</a>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: "var(--ui-muted)" }}>{r.phone}</p>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <h3 style={sectionTitle}>About</h3>
        <Link to={`${BASE_PATH}/docs`} className="btn btn-secondary" style={{ ...btnBlock, marginBottom: 0 }}>Help & Info</Link>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <button onClick={logout} className="btn" style={{ display: 'block', width: '100%', background: '#e0245e', color: '#fff', border: 'none', borderRadius: 8, padding: '12px', cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
          Logout
        </button>
      </div>

      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ ...sectionTitle, color: '#e74c3c' }}>Danger Zone</h3>
        {!showDeleteModal ? (
          <button onClick={() => setShowDeleteModal(true)} className="btn" style={{ display: 'block', width: '100%', background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: 8, padding: '12px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            Delete My Account
          </button>
        ) : (
          <div>
            <p style={{ fontSize: 13, color: "var(--ui-muted)", marginBottom: 12 }}>This action is permanent and cannot be undone. All your data will be deleted.</p>
            <input type="password" placeholder="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: "1px solid var(--ui-border)", marginBottom: 8, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletePassword(''); }} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting || !deletePassword} className="btn" style={{ flex: 1, background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', cursor: 'pointer', fontWeight: 600, opacity: deleting || !deletePassword ? 0.5 : 1 }}>
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        )}
      </div>

      <p style={{ textAlign: 'center', color: "var(--ui-muted)", marginTop: 20, fontSize: 12 }}>Arawra v1.0.0</p>
    </div>
  );
};

const HRTFundPage = () => {
  const [form, setForm] = useState({
    chosen_name: '', legal_name: '', email: '', age: '',
    description_of_need: '', income_bracket: '', had_bottom_surgery: '',
    medications_needed: '', insurance_status: '', insurance_details: '',
    has_provider: '', estimated_monthly_cost: '', preferred_assistance: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/hrt-fund/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, age: parseInt(form.age, 10) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="hrt-fund-page">
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <h2>Application Received</h2>
          <p style={{ color: "var(--ui-muted)", marginTop: 12, lineHeight: 1.6 }}>
            Thank you for submitting your application. We will send an acknowledgment to <strong>{form.email}</strong> within 5 business days.
          </p>
          <p style={{ color: "var(--ui-muted)", lineHeight: 1.6 }}>
            Decisions are typically made within 30 days. If you have an emergency need, please email us at <a href="mailto:eve@belonging.lgbt" style={{ color: "var(--ui-accent)" }}>eve@belonging.lgbt</a>.
          </p>
          <Link to={`${BASE_PATH}/`} className="btn btn-primary" style={{ display: 'inline-block', marginTop: 20 }}>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="hrt-fund-page">
      <h1>HRT Fund</h1>
      <p style={{ textAlign: 'center', color: "var(--ui-muted)", marginBottom: 28 }}>
        Financial assistance for hormone replacement therapy
      </p>

      <div className="card hrt-fund-section">
        <p style={{ lineHeight: 1.7, color: "var(--ui-text)" }}>
          The Arawra HRT Fund provides financial assistance to transgender, non-binary, and gender-diverse individuals who cannot afford hormone replacement therapy. Access to gender-affirming healthcare is a critical need, and this fund aims to remove financial barriers.
        </p>
      </div>

      <div className="card hrt-fund-section">
        <h3 style={{ marginTop: 0 }}>What We Cover</h3>
        <ul>
          <li>Hormone medications (estrogen, testosterone, anti-androgens, etc.)</li>
          <li>Lab work and blood tests related to HRT</li>
          <li>Initial consultation with a prescribing provider</li>
          <li>Follow-up appointments related to HRT management</li>
          <li>Travel expenses to access care</li>
        </ul>

        <h3>What Is Not Covered</h3>
        <ul>
          <li>Surgeries (top surgery, bottom surgery, FFS, etc.)</li>
          <li>Non-HRT medications</li>
          <li>Lost wages</li>
        </ul>
      </div>

      <div className="card hrt-fund-section">
        <h3 style={{ marginTop: 0 }}>Eligibility</h3>
        <ul>
          <li>Transgender, non-binary, or gender-diverse individuals who need HRT</li>
          <li>At least 18 years of age (or have parental/guardian consent with a provider's recommendation if under 18)</li>
          <li>Reside in the United States</li>
          <li>Distribution is need-based</li>
        </ul>
      </div>

      <div className="card hrt-fund-section">
        <h3 style={{ marginTop: 0 }}>Your Privacy</h3>
        <p style={{ lineHeight: 1.7, color: "var(--ui-text)" }}>
          All application information is <strong>strictly confidential</strong>. Your data is stored securely, never linked to an Arawra social media account, and only accessible by the HRT Fund Committee. You do <strong>not</strong> need an Arawra account to apply.
        </p>
        <h3>What We Do NOT Require</h3>
        <ul>
          <li>Government ID</li>
          <li>Proof of diagnosis</li>
          <li>Letters from therapists or mental health providers</li>
          <li>Detailed financial statements or tax returns</li>
        </ul>
      </div>

      <div className="card hrt-fund-section">
        <h3 style={{ marginTop: 0 }}>Apply for Assistance</h3>
        <p style={{ color: "var(--ui-muted)", marginBottom: 20, fontSize: 14 }}>
          Fields marked with * are required. Your information is kept confidential.
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Chosen Name *</label>
            <input type="text" value={form.chosen_name} onChange={handleChange('chosen_name')} required placeholder="The name you go by" />
          </div>

          <div className="form-group">
            <label>Legal Name</label>
            <input type="text" value={form.legal_name} onChange={handleChange('legal_name')} placeholder="Only if needed for pharmacy or insurance" />
            <div className="hrt-fund-field-hint">Optional. Only provide if different from chosen name and needed for prescriptions.</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={handleChange('email')} required placeholder="your@email.com" />
            </div>
            <div className="form-group">
              <label>Age *</label>
              <input type="number" value={form.age} onChange={handleChange('age')} required min="13" max="120" placeholder="Age" />
            </div>
          </div>

          <div className="form-group">
            <label>Brief Description of Need *</label>
            <textarea value={form.description_of_need} onChange={handleChange('description_of_need')} required rows={4} placeholder="Tell us about your situation and why you need assistance" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
            <div className="form-group">
              <label>Income Bracket *</label>
              <select value={form.income_bracket} onChange={handleChange('income_bracket')} required>
                <option value="">Select...</option>
                <option value="under_15k">Under $15,000</option>
                <option value="15k_30k">$15,000 - $30,000</option>
                <option value="30k_50k">$30,000 - $50,000</option>
                <option value="50k_75k">$50,000 - $75,000</option>
                <option value="75k_plus">$75,000+</option>
              </select>
            </div>
            <div className="form-group">
              <label>Have you had bottom surgery? *</label>
              <select value={form.had_bottom_surgery} onChange={handleChange('had_bottom_surgery')} required>
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
              <div className="hrt-fund-field-hint">Helps us assess medical urgency.</div>
            </div>
          </div>

          <div className="form-group">
            <label>Medications Needed *</label>
            <textarea value={form.medications_needed} onChange={handleChange('medications_needed')} required rows={3} placeholder="e.g., Estradiol, Spironolactone, Testosterone cypionate" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
            <div className="form-group">
              <label>Insurance Status *</label>
              <select value={form.insurance_status} onChange={handleChange('insurance_status')} required>
                <option value="">Select...</option>
                <option value="insured">Insured</option>
                <option value="uninsured">Uninsured</option>
              </select>
            </div>
            <div className="form-group">
              <label>Do you have a prescribing provider? *</label>
              <select value={form.has_provider} onChange={handleChange('has_provider')} required>
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          {form.insurance_status === 'insured' && (
            <div className="form-group">
              <label>Insurance Details</label>
              <textarea value={form.insurance_details} onChange={handleChange('insurance_details')} rows={2} placeholder="Insurance provider name, what it covers/doesn't cover for HRT" />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
            <div className="form-group">
              <label>Estimated Monthly Cost</label>
              <input type="text" value={form.estimated_monthly_cost} onChange={handleChange('estimated_monthly_cost')} placeholder="e.g., $50/month" />
              <div className="hrt-fund-field-hint">Optional. Your best estimate.</div>
            </div>
            <div className="form-group">
              <label>Preferred Assistance Method *</label>
              <select value={form.preferred_assistance} onChange={handleChange('preferred_assistance')} required>
                <option value="">Select...</option>
                <option value="pharmacy_payment">Direct pharmacy payment</option>
                <option value="provider_payment">Provider payment</option>
                <option value="reimbursement">Reimbursement</option>
                <option value="pharmacy_gift_card">Pharmacy gift card</option>
              </select>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', marginTop: 10 }}>
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>

      <div className="card hrt-fund-section hrt-fund-resources">
        <h3 style={{ marginTop: 0 }}>Additional Resources</h3>
        <p style={{ color: "var(--ui-muted)", marginBottom: 12, fontSize: 14 }}>
          Even if we can't help right away, these resources may be useful:
        </p>
        <ul>
          <li><a href="https://www.plannedparenthood.org" target="_blank" rel="noopener noreferrer">Planned Parenthood</a> — Many locations offer HRT on a sliding scale</li>
          <li><a href="https://www.goodrx.com" target="_blank" rel="noopener noreferrer">GoodRx</a> — Prescription discount cards can significantly reduce medication costs</li>
          <li><strong>LGBTQ+ community health centers</strong> — Often offer affordable HRT services</li>
          <li><strong>Patient assistance programs</strong> — Many pharmaceutical manufacturers offer free or reduced-cost medications</li>
          <li><strong>State Medicaid programs</strong> — Many states now cover HRT under Medicaid</li>
          <li><strong>ACA Insurance Marketplace</strong> — ACA plans are required to cover gender-affirming care in many states</li>
        </ul>
      </div>

      <div style={{ textAlign: 'center', color: "var(--ui-muted)", fontSize: 13, marginTop: 20, marginBottom: 40 }}>
        <p>Questions? Contact us at <a href="mailto:eve@belonging.lgbt" style={{ color: "var(--ui-accent)" }}>eve@belonging.lgbt</a></p>
        <p style={{ marginTop: 8 }}>belonging.lgbt, Inc. — 501(c)(3) nonprofit</p>
      </div>
    </div>
  );
};

const DmcaRequestPage = () => {
  const [form, setForm] = useState({ reporter_name: '', reporter_email: '', content_url: '', copyrighted_work: '', attestation: false });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.attestation) { setError('You must attest that you are the copyright owner.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/dmca`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setSubmitted(true);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  if (submitted) return (
    <div className="container" style={{ maxWidth: 600, margin: '0 auto', padding: 20, textAlign: 'center' }}>
      <div className="card" style={{ padding: 40 }}>
        <h2>DMCA Request Submitted</h2>
        <p style={{ color: "var(--ui-muted)", marginTop: 12 }}>We will review your request and respond within 10 business days.</p>
        <Link to={`${BASE_PATH}/`} className="btn btn-primary" style={{ display: 'inline-block', marginTop: 20 }}>Back to Home</Link>
      </div>
    </div>
  );

  const fieldStyle = { width: '100%', padding: 10, borderRadius: 8, border: "1px solid var(--ui-border)", marginBottom: 12, boxSizing: 'border-box', fontSize: 14 };
  return (
    <div className="container" style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
      <h2>DMCA Takedown Request</h2>
      <p style={{ color: "var(--ui-muted)", marginBottom: 16, fontSize: 14 }}>Use this form to submit a DMCA takedown notice for copyrighted content hosted on Arawra.</p>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Your Name *</label>
        <input style={fieldStyle} required value={form.reporter_name} onChange={e => setForm(f => ({ ...f, reporter_name: e.target.value }))} />
        <label style={{ fontSize: 13, fontWeight: 600 }}>Your Email *</label>
        <input style={fieldStyle} type="email" required value={form.reporter_email} onChange={e => setForm(f => ({ ...f, reporter_email: e.target.value }))} />
        <label style={{ fontSize: 13, fontWeight: 600 }}>URL of Infringing Content *</label>
        <input style={fieldStyle} required value={form.content_url} onChange={e => setForm(f => ({ ...f, content_url: e.target.value }))} placeholder="https://arawra.net/social/post/..." />
        <label style={{ fontSize: 13, fontWeight: 600 }}>Description of Copyrighted Work *</label>
        <textarea style={{ ...fieldStyle, minHeight: 80 }} required value={form.copyrighted_work} onChange={e => setForm(f => ({ ...f, copyrighted_work: e.target.value }))} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.attestation} onChange={e => setForm(f => ({ ...f, attestation: e.target.checked }))} />
          <span style={{ fontSize: 13 }}>I attest that I am the copyright owner or authorized to act on behalf of the copyright owner.</span>
        </label>
        <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>{loading ? 'Submitting...' : 'Submit DMCA Request'}</button>
      </form>
    </div>
  );
};

const TransparencyPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/transparency`)
      .then(r => r.json()).then(d => { setStats(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const statCard = (label, value) => (
    <div className="card" style={{ padding: 16, textAlign: 'center', flex: '1 1 140px' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: "var(--ui-accent)" }}>{value ?? '—'}</div>
      <div style={{ fontSize: 12, color: "var(--ui-muted)", marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div className="container" style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
      <h2 style={{ textAlign: 'center' }}>Transparency Report</h2>
      <p style={{ textAlign: 'center', color: "var(--ui-muted)", marginBottom: 20, fontSize: 14 }}>Aggregate moderation statistics for Arawra</p>
      {loading ? <p style={{ textAlign: 'center' }}>Loading...</p> : stats ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {statCard('Users', stats.users)}
          {statCard('Posts', stats.posts)}
          {statCard('Bans', stats.bans)}
          {statCard('Mutes', stats.mutes)}
          {statCard('Suspensions', stats.suspensions)}
          {statCard('DMCA Requests', stats.dmca_requests)}
          {statCard('Pending Reports', stats.reports?.pending || 0)}
          {statCard('Actioned Reports', stats.reports?.actioned || 0)}
        </div>
      ) : <p style={{ textAlign: 'center', color: '#e74c3c' }}>Failed to load data</p>}
      <p style={{ textAlign: 'center', color: "var(--ui-muted)", marginTop: 20, fontSize: 11 }}>Data is generated in real-time and updated continuously.</p>
    </div>
  );
};

const StorageBanner = () => {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('storage_consent_dismissed') === '1');
  if (dismissed) return null;
  return (
    <div style={{ background: "var(--ui-muted-surface)", borderBottom: "1px solid var(--ui-border)", padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: "var(--ui-muted)" }}>
      <span>This site uses localStorage for authentication and preferences. By continuing, you accept this usage. <Link to={`${BASE_PATH}/docs/legal/privacy-policy`} style={{ color: "var(--ui-accent)" }}>Learn more</Link></span>
      <button onClick={() => { localStorage.setItem('storage_consent_dismissed', '1'); setDismissed(true); }} style={{ background: "var(--ui-accent)", color: "var(--ui-on-accent)", border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, marginLeft: 12, whiteSpace: 'nowrap' }}>Got it</button>
    </div>
  );
};

const BreachBanner = () => {
  const [breaches, setBreaches] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/breach-notifications`)
      .then(r => r.json()).then(d => setBreaches(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  if (breaches.length === 0) return null;
  return (
    <div>
      {breaches.map(b => (
        <div key={b.id} style={{ background: '#dc2626', color: '#fff', padding: '10px 20px', fontSize: 13, fontWeight: 500 }}>
          <strong>{b.title}</strong>: {b.description}
          {b.recommended_action && <span style={{ display: 'block', fontSize: 12, marginTop: 4, opacity: 0.9 }}>Recommended: {b.recommended_action}</span>}
        </div>
      ))}
    </div>
  );
};

const DocsPage = () => {
  const SECTIONS = [
    { title: 'Feed', desc: 'Browse and interact with posts from the community', path: '/feed' },
    { title: 'Trends', desc: 'See what topics are trending', path: '/trends' },
    { title: 'Skills', desc: 'Explore and share skills with the community', path: '/skills' },
    { title: 'Tribes', desc: 'Join communities based on shared identity and interests', path: '/tribes' },
    { title: 'Create Tribe', desc: 'Start a new tribe with custom flags and icon', path: '/tribes/create' },
    { title: 'Messages', desc: 'Chat with other members of the community', path: '/messages' },
    { title: 'Search', desc: 'Find users, posts, and tribes', path: '/search' },
    { title: 'Edit Profile', desc: 'Change your name, bio, email, and photo', path: '/edit-profile' },
    { title: 'Settings', desc: 'Account settings, notifications, and logout', path: '/settings' },
    { title: 'Admin', desc: 'Admin dashboard (admin only)', path: '/admin' },
  ];

  return (
    <div className="container" style={{ maxWidth: 600, margin: '0 auto', padding: '20px' }}>
      <h2 style={{ textAlign: 'center' }}>Arawra</h2>
      <p style={{ textAlign: 'center', color: "var(--ui-muted)", marginBottom: 24 }}>A safe social network for the LGBTQIA+ community</p>

      <h3 style={{ fontSize: 14, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Pages</h3>
      {SECTIONS.map(s => (
        <Link key={s.title} to={`${BASE_PATH}${s.path}`} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
          <div className="card" style={{ padding: '14px 16px', cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, color: "var(--ui-text)" }}>{s.title}</div>
            <div style={{ fontSize: 13, color: "var(--ui-muted)", marginTop: 2 }}>{s.desc}</div>
          </div>
        </Link>
      ))}

      <h3 style={{ fontSize: 14, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 24, marginBottom: 8 }}>Legal & Policies</h3>
      {[
        { title: 'Terms of Service', path: '/docs/legal/terms-of-service' },
        { title: 'Privacy Policy', path: '/docs/legal/privacy-policy' },
        { title: 'Community Guidelines', path: '/docs/legal/community-guidelines' },
        { title: 'Safety & Anti-Harassment', path: '/docs/legal/safety-anti-harassment-policy' },
        { title: 'Acceptable Use Policy', path: '/docs/legal/acceptable-use-policy' },
        { title: 'Accessibility Statement', path: '/docs/legal/accessibility-statement' },
        { title: 'Advertising Policy', path: '/docs/legal/advertising-policy' },
        { title: 'CCPA Notice', path: '/docs/legal/ccpa-notice' },
        { title: "Children's Privacy (COPPA)", path: '/docs/legal/coppa-policy' },
        { title: 'Consumer Health Data', path: '/docs/legal/consumer-health-data' },
        { title: 'Content Moderation', path: '/docs/legal/content-moderation' },
        { title: 'Cookies Policy', path: '/docs/legal/cookies-policy' },
        { title: 'Data Protection', path: '/docs/legal/data-protection-policy' },
        { title: 'Data Retention', path: '/docs/legal/data-retention' },
        { title: 'Disclaimer', path: '/docs/legal/disclaimer' },
        { title: 'DMCA / Copyright', path: '/docs/legal/dmca-policy' },
        { title: 'GDPR Notice', path: '/docs/legal/gdpr-notice' },
        { title: 'Law Enforcement Guidelines', path: '/docs/legal/law-enforcement' },
        { title: 'Third-Party Services', path: '/docs/legal/third-party-services' },
      ].map(d => (
        <Link key={d.title} to={`${BASE_PATH}${d.path}`} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
          <div className="card" style={{ padding: '14px 16px', cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, color: "var(--ui-text)" }}>{d.title}</div>
          </div>
        </Link>
      ))}

      <h3 style={{ fontSize: 14, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 24, marginBottom: 8 }}>Nonprofit</h3>
      {[
        { title: 'Articles of Incorporation', path: '/docs/nonprofit/articles-of-incorporation' },
        { title: 'Bylaws', path: '/docs/nonprofit/bylaws' },
        { title: 'HRT Fund Policy', path: '/docs/nonprofit/hrt-fund-policy' },
      ].map(d => (
        <Link key={d.title} to={`${BASE_PATH}${d.path}`} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
          <div className="card" style={{ padding: '14px 16px', cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, color: "var(--ui-text)" }}>{d.title}</div>
          </div>
        </Link>
      ))}

      <h3 style={{ fontSize: 14, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 24, marginBottom: 8 }}>Marketplace Policies</h3>
      {[
        { title: 'Seller Agreement', path: '/docs/marketplace/seller-agreement' },
        { title: 'Buyer Protection', path: '/docs/marketplace/buyer-protection' },
        { title: 'Refund & Returns Policy', path: '/docs/marketplace/refund-policy' },
        { title: 'Shipping Policy', path: '/docs/marketplace/shipping-policy' },
        { title: 'Prohibited Items', path: '/docs/marketplace/prohibited-items' },
        { title: 'Fee Structure', path: '/docs/marketplace/fee-structure' },
      ].map(d => (
        <Link key={d.title} to={`${BASE_PATH}${d.path}`} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
          <div className="card" style={{ padding: '14px 16px', cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, color: "var(--ui-text)" }}>{d.title}</div>
          </div>
        </Link>
      ))}

      <h3 style={{ fontSize: 14, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 24, marginBottom: 8 }}>Resources</h3>
      <Link to={`${BASE_PATH}/transparency`} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
        <div className="card" style={{ padding: '14px 16px', cursor: 'pointer' }}>
          <div style={{ fontWeight: 600, color: "var(--ui-text)" }}>Transparency Report</div>
          <div style={{ fontSize: 13, color: "var(--ui-muted)", marginTop: 2 }}>Moderation statistics and platform data</div>
        </div>
      </Link>

      <div className="card" style={{ marginTop: 16, padding: 20 }}>
        <h3 style={{ fontSize: 14, color: "var(--ui-muted)", textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 0, marginBottom: 12 }}>Crisis Resources</h3>
        {CRISIS_RESOURCES.map(r => (
          <div key={r.name} style={{ marginBottom: 10, padding: '10px 12px', background: "var(--ui-muted-surface)", borderRadius: 8 }}>
            <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: "var(--ui-accent)", fontSize: 14, textDecoration: 'none' }}>{r.name}</a>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: "var(--ui-muted)" }}>{r.phone}</p>
          </div>
        ))}
      </div>

      <p style={{ textAlign: 'center', color: "var(--ui-muted)", marginTop: 20, fontSize: 12 }}>v1.0.0</p>
    </div>
  );
};

// ============================================
// DONATE PAGE
// ============================================
const DonatePage = () => {
  const { user } = useAuth();
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [designation, setDesignation] = useState('general');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [card, setCard] = useState(null);

  const PRESETS = [500, 1000, 2500, 5000, 10000];

  useEffect(() => {
    let payments;
    const initSquare = async () => {
      try {
        const res = await fetch(`${API_URL}/payments/config`);
        const config = await res.json();
        if (!window.Square) return;
        payments = window.Square.payments(config.applicationId, config.locationId);
        const c = await payments.card();
        await c.attach('#card-container');
        setCard(c);
      } catch (e) { console.error('Square init error:', e); }
    };
    initSquare();
  }, []);

  const handleDonate = async () => {
    if (!card) return;
    setLoading(true); setError('');
    try {
      const result = await card.tokenize();
      if (result.status !== 'OK') throw new Error(result.errors?.[0]?.message || 'Card error');
      const finalAmount = customAmount ? Math.round(parseFloat(customAmount) * 100) : amount;
      if (finalAmount < 100) throw new Error('Minimum donation is $1.00');
      const res = await fetch(`${API_URL}/donations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem(AUTH_TOKEN_KEY) ? { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` } : {}) },
        body: JSON.stringify({ amount_cents: finalAmount, nonce: result.token, designation }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setSuccess(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  if (success) return (
    <div className="container" style={{ maxWidth: 500, textAlign: 'center', padding: '60px 20px' }}>
      <h1 style={{ color: '#22c55e', fontSize: 48 }}>♥</h1>
      <h2>Thank you for your donation!</h2>
      <p style={{ color: "var(--ui-muted)" }}>Your contribution of <strong>${((customAmount ? parseFloat(customAmount) * 100 : amount) / 100).toFixed(2)}</strong> helps support the LGBTQIA+ community.</p>
      <p style={{ color: "var(--ui-muted)", fontSize: 13 }}>As a 501(c)(3) nonprofit, your donation is tax-deductible to the extent permitted by law.</p>
    </div>
  );

  return (
    <div className="container" style={{ maxWidth: 500, padding: '20px' }}>
      <h1 style={{ textAlign: 'center' }}>Donate to Arawra</h1>
      <p style={{ textAlign: 'center', color: "var(--ui-muted)" }}>Support the LGBTQIA+ community. All donations are tax-deductible.</p>

      <div style={{ marginTop: 20 }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>Designation</label>
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          {['general', 'hrt_fund'].map(d => (
            <button key={d} onClick={() => setDesignation(d)}
              style={{ flex: 1, padding: '10px', borderRadius: 12, border: designation === d ? "2px solid var(--ui-border)" : "2px solid var(--ui-border)", background: designation === d ? "var(--ui-muted-surface)" : "var(--ui-surface)", cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              {d === 'general' ? 'General Fund' : 'HRT Fund'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>Amount</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 6 }}>
          {PRESETS.map(p => (
            <button key={p} onClick={() => { setAmount(p); setCustomAmount(''); }}
              style={{ padding: '10px', borderRadius: 12, border: amount === p && !customAmount ? "2px solid var(--ui-border)" : "2px solid var(--ui-border)", background: amount === p && !customAmount ? "var(--ui-muted-surface)" : "var(--ui-surface)", cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              ${(p / 100).toFixed(0)}
            </button>
          ))}
        </div>
        <input type="number" placeholder="Custom amount ($)" value={customAmount} onChange={e => { setCustomAmount(e.target.value); setAmount(0); }}
          style={{ width: '100%', padding: 12, borderRadius: 12, border: "2px solid var(--ui-border)", marginTop: 8, fontSize: 14, boxSizing: 'border-box' }} />
      </div>

      <div style={{ marginTop: 20 }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>Payment</label>
        <div id="card-container" style={{ marginTop: 6 }}></div>
      </div>

      {error && <p style={{ color: '#dc3545', marginTop: 8, fontSize: 14 }}>{error}</p>}
      <button onClick={handleDonate} disabled={loading} className="btn btn-primary" style={{ width: '100%', marginTop: 16, padding: 14 }}>
        {loading ? 'Processing...' : `Donate $${((customAmount ? parseFloat(customAmount || '0') * 100 : amount) / 100).toFixed(2)}`}
      </button>
    </div>
  );
};

// ============================================
// MEMBERSHIP PAGE
// ============================================
const MembershipPage = () => {
  const { user, refreshUser } = useAuth();
  const [membership, setMembership] = useState(null);
  const [memberLoading, setMemberLoading] = useState(true);
  const [amount, setAmount] = useState(500);
  const [customAmount, setCustomAmount] = useState('');
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!user) { setMemberLoading(false); return; }
    fetch(`${API_URL}/membership/status`, { headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` } })
      .then(r => r.json()).then(d => setMembership(d)).catch(() => {}).finally(() => setMemberLoading(false));
  }, [user]);

  useEffect(() => {
    const initSquare = async () => {
      try {
        const res = await fetch(`${API_URL}/payments/config`);
        const config = await res.json();
        if (!window.Square) return;
        const payments = window.Square.payments(config.applicationId, config.locationId);
        const c = await payments.card();
        await c.attach('#member-card-container');
        setCard(c);
      } catch (e) { console.error('Square init:', e); }
    };
    if (!membership) initSquare();
  }, [membership]);

  const handleSubscribe = async () => {
    if (!card) return;
    setLoading(true); setError('');
    try {
      const result = await card.tokenize();
      if (result.status !== 'OK') throw new Error(result.errors?.[0]?.message || 'Card error');
      const finalAmount = customAmount ? Math.round(parseFloat(customAmount) * 100) : amount;
      if (finalAmount < 100) throw new Error('Minimum is $1.00/month');
      const res = await fetch(`${API_URL}/membership/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` },
        body: JSON.stringify({ amount_cents: finalAmount, nonce: result.token }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setSuccess(true);
      refreshUser();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your membership?')) return;
    try {
      await fetch(`${API_URL}/membership/cancel`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` } });
      setMembership({ ...membership, status: 'cancelled' });
      refreshUser();
    } catch (e) { setError(e.message); }
  };

  if (!user) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Please log in to manage your membership.</p></div>;

  if (success) return (
    <div className="container" style={{ maxWidth: 500, textAlign: 'center', padding: '60px 20px' }}>
      <h1 style={{ color: "var(--ui-accent)", fontSize: 48 }}>⭐</h1>
      <h2>Welcome, Member!</h2>
      <p style={{ color: "var(--ui-muted)" }}>Your membership is now active. Thank you for supporting Arawra!</p>
    </div>
  );

  if (memberLoading) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Loading...</p></div>;

  if (membership && membership.status === 'active') return (
    <div className="container" style={{ maxWidth: 500, padding: 20 }}>
      <h1 style={{ textAlign: 'center' }}>Your Membership</h1>
      <div className="card" style={{ padding: 20, marginTop: 16 }}>
        <p><strong>Status:</strong> <span style={{ color: '#22c55e' }}>Active ⭐</span></p>
        <p><strong>Monthly Amount:</strong> ${(membership.amount_cents / 100).toFixed(2)}/mo</p>
        <p><strong>Member Since:</strong> {new Date(membership.started_at).toLocaleDateString()}</p>
        {membership.next_billing_date && <p><strong>Next Billing:</strong> {new Date(membership.next_billing_date).toLocaleDateString()}</p>}
      </div>
      <button onClick={handleCancel} className="btn btn-danger" style={{ width: '100%', marginTop: 16 }}>Cancel Membership</button>
    </div>
  );

  return (
    <div className="container" style={{ maxWidth: 500, padding: 20 }}>
      <h1 style={{ textAlign: 'center' }}>Become a Member</h1>
      <p style={{ textAlign: 'center', color: "var(--ui-muted)" }}>Support Arawra with a monthly contribution. Pay what you want!</p>

      <div className="card" style={{ padding: 20, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Member Benefits</h3>
        <ul style={{ color: "var(--ui-muted)", lineHeight: 2 }}>
          <li>⭐ Member badge on your profile and posts</li>
          <li>📁 10GB file upload limit (vs 100MB)</li>
          <li>🛍️ Access to create a marketplace shop</li>
          <li>💜 Support the LGBTQIA+ community</li>
        </ul>
      </div>

      <div style={{ marginTop: 20 }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>Monthly Amount (min $1)</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 6 }}>
          {[100, 300, 500, 1000].map(p => (
            <button key={p} onClick={() => { setAmount(p); setCustomAmount(''); }}
              style={{ padding: 10, borderRadius: 12, border: amount === p && !customAmount ? "2px solid var(--ui-border)" : "2px solid var(--ui-border)", background: amount === p && !customAmount ? "var(--ui-muted-surface)" : "var(--ui-surface)", cursor: 'pointer', fontWeight: 600 }}>
              ${(p / 100).toFixed(0)}/mo
            </button>
          ))}
        </div>
        <input type="number" placeholder="Custom amount ($)" value={customAmount} onChange={e => { setCustomAmount(e.target.value); setAmount(0); }}
          style={{ width: '100%', padding: 12, borderRadius: 12, border: "2px solid var(--ui-border)", marginTop: 8, fontSize: 14, boxSizing: 'border-box' }} />
      </div>

      <div style={{ marginTop: 20 }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>Payment</label>
        <div id="member-card-container" style={{ marginTop: 6 }}></div>
      </div>

      {error && <p style={{ color: '#dc3545', marginTop: 8, fontSize: 14 }}>{error}</p>}
      <button onClick={handleSubscribe} disabled={loading} className="btn btn-primary" style={{ width: '100%', marginTop: 16, padding: 14 }}>
        {loading ? 'Processing...' : `Subscribe $${((customAmount ? parseFloat(customAmount || '0') * 100 : amount) / 100).toFixed(2)}/mo`}
      </button>
    </div>
  );
};

// ============================================
// MARKETPLACE PAGE
// ============================================
const ADMIN_HANDLES = ['eve', 'evepanzarino', 'belonging'];

const MarketplacePage = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [hasShop, setHasShop] = useState(null);
  const [marketTab, setMarketTab] = useState('queerShops');
  const navigate = useNavigate();

  const isAdmin = user && ADMIN_HANDLES.includes(user.username);

  const fetchProducts = (searchVal, tabOverride) => {
    const activeTab = tabOverride || marketTab;
    const params = new URLSearchParams();
    const q = searchVal !== undefined ? searchVal : search;
    if (q) params.set('search', q);
    if (typeFilter && activeTab === 'merch') params.set('type', typeFilter);
    params.set('external', activeTab === 'queerShops' ? '1' : '0');
    setLoading(true);
    fetch(`${API_URL}/products?${params}`).then(r => r.json()).then(d => setProducts(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchProducts(); }, [typeFilter, marketTab]);

  useEffect(() => {
    const t = setTimeout(() => fetchProducts(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!user) { setHasShop(false); return; }
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    fetch(`${API_URL}/shops/my`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setHasShop(!!(d && d.id)))
      .catch(() => setHasShop(false));
  }, [user]);

  const handleProductClick = (p) => {
    if (p.external_url) {
      window.open(p.external_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`${BASE_PATH}/marketplace/product/${p.id}`);
    }
  };

  const marketTabStyle = (isActive, idx = 0) => ({
    padding: '10px 20px',
    border: isActive ? "2px solid var(--ui-border)" : 'none',
    background: isActive ? "var(--ui-accent)" : "var(--ui-muted-surface)",
    color: isActive ? "var(--ui-on-accent)" : "var(--ui-muted)",
    cursor: 'pointer',
    borderRadius: '12px',
    fontWeight: isActive ? 900 : '600',
    fontSize: '14px',
    textShadow: isActive ? '0 1px 3px rgba(0,0,0,0.4)' : 'none',
  });

  return (
    <div className="container" style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Marketplace</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {user && hasShop === true && <Link to={`${BASE_PATH}/marketplace/seller`} className="btn btn-secondary" style={{ fontSize: 13 }}>Manage Shop</Link>}
          {user && hasShop === false && <Link to={`${BASE_PATH}/marketplace/seller`} className="btn btn-primary" style={{ fontSize: 13 }}>Create Shop</Link>}
          {user && marketTab === 'merch' && <Link to={`${BASE_PATH}/marketplace/cart`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', gap: 2 }}>
            <SocialImage src={`${BASE_PATH}/Skills/shop.svg`} alt="Cart" style={{ width: 28, height: 28 }} />
            <span style={{ fontSize: 10, fontWeight: 600, color: "var(--ui-muted)" }}>Cart</span>
          </Link>}
        </div>
      </div>

      {/* Marketplace tabs */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={marketTabStyle(marketTab === 'queerShops', 0)} onClick={() => { setMarketTab('queerShops'); }}>
          Shop LGBTQIA+ Creators
        </button>
        <button style={marketTabStyle(marketTab === 'merch', 1)} onClick={() => { setMarketTab('merch'); }}>
          Arawra Shop
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchProducts()} placeholder="Search products..."
          style={{ flex: 1, minWidth: 200, padding: 10, borderRadius: 12, border: "2px solid var(--ui-border)", fontSize: 14 }} />
        {marketTab === 'merch' && (
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: 10, borderRadius: 12, border: "2px solid var(--ui-border)", fontSize: 14 }}>
            <option value="">All Types</option>
            <option value="physical">Physical</option>
            <option value="digital">Digital</option>
            <option value="service">Service</option>
          </select>
        )}
      </div>

      {loading ? <p style={{ textAlign: 'center', marginTop: 40 }}>Loading...</p> :
       products.length === 0 ? <p style={{ textAlign: 'center', marginTop: 40, color: "var(--ui-muted)" }}>
        {marketTab === 'merch' ? 'No products yet.' : 'No queer shop products yet.'}
       </p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
          {products.map(p => {
            const imgs = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
            return (
              <div key={p.id} onClick={() => handleProductClick(p)} className="card" style={{ cursor: 'pointer', padding: 0, overflow: 'hidden' }}>
                <div style={{ aspectRatio: '1/1', background: "var(--ui-muted-surface)", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {imgs && imgs.length > 0 ? <SocialImage src={`${API_URL.replace('/api', '')}${imgs[0]}`} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                    <span style={{ color: "var(--ui-muted)", fontSize: 40 }}>{p.type === 'digital' ? '📁' : p.type === 'service' ? '🛠️' : '📦'}</span>}
                </div>
                <div style={{ padding: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</h3>
                  <p style={{ margin: '4px 0 0', fontWeight: 700, color: "var(--ui-accent)" }}>${(p.price_cents / 100).toFixed(2)}</p>
                  {p.shop_name && <p style={{ margin: '4px 0 0', fontSize: 12, color: "var(--ui-muted)" }}>{p.shop_name}</p>}
                  {p.description && <p style={{ margin: '2px 0 0', fontSize: 11, color: "var(--ui-muted)", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description.replace(/<[^>]*>/g, ' ').slice(0, 80)}</p>}
                  {p.hrt_donation_percent > 0 && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9b59b6', fontWeight: 600 }}>{p.hrt_donation_percent}% to HRT Fund</p>}
                  {p.external_url && <p style={{ margin: '4px 0 0', fontSize: 11, color: "var(--ui-accent)" }}>Opens in new tab</p>}
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: "var(--ui-muted)" }}>{p.type}</p>
                  {p.avg_rating && <p style={{ margin: '2px 0 0', fontSize: 12, color: '#f5c518' }}>{'★'.repeat(Math.round(p.avg_rating))} ({p.review_count})</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// PRODUCT PAGE
// ============================================
const ProductPage = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/products/${id}`).then(r => r.json()).then(d => setProduct(d)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const addToCart = async () => {
    setAddingToCart(true);
    try {
      const res = await fetch(`${API_URL}/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` },
        body: JSON.stringify({ product_id: Number(id), quantity }),
      });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      alert('Added to cart!');
    } catch { alert('Failed to add to cart'); }
    finally { setAddingToCart(false); }
  };

  if (loading) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Loading...</p></div>;
  if (!product) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Product not found</p></div>;

  const imgs = typeof product.images === 'string' ? JSON.parse(product.images) : (product.images || []);
  const resolveImg = (url) => url && url.startsWith('http') ? url : `${API_URL.replace('/api', '')}${url}`;

  return (
    <div className="container" style={{ maxWidth: 800, padding: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: imgs.length > 0 ? '1fr 1fr' : '1fr', gap: 24 }}>
        {imgs.length > 0 && (
          <div>
            <SocialImage src={resolveImg(imgs[0])} alt={product.name} style={{ width: '100%', borderRadius: 12, objectFit: 'cover' }} />
            {imgs.length > 1 && <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {imgs.slice(1, 5).map((img, i) => <SocialImage key={i} src={resolveImg(img)} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />)}
            </div>}
          </div>
        )}
        <div>
          <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 20, background: "var(--ui-muted-surface)", color: "var(--ui-accent)", fontSize: 12, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>{product.type}</span>
          <h1 style={{ margin: '0 0 8px' }}>{product.name}</h1>
          <p style={{ fontSize: 28, fontWeight: 700, color: "var(--ui-accent)", margin: '0 0 12px' }}>${(product.price_cents / 100).toFixed(2)}</p>
          <p style={{ color: "var(--ui-muted)", fontSize: 14 }}>by <Link to={`${BASE_PATH}/marketplace/shop/${product.shop_slug}`} style={{ color: "var(--ui-accent)" }}>{product.shop_name}</Link> · @{product.seller_username}</p>
          {product.avg_rating && <p style={{ color: '#f5c518', fontSize: 14 }}>{'★'.repeat(Math.round(product.avg_rating))} {product.avg_rating.toFixed(1)} ({product.review_count} reviews)</p>}
          {product.stock !== null && <p style={{ fontSize: 13, color: product.stock > 0 ? '#22c55e' : '#dc3545' }}>{product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}</p>}
          {user && product.user_id === user.id && (
            <Link to={`${BASE_PATH}/marketplace/product/${id}/edit`} className="btn btn-secondary" style={{ fontSize: 13, marginTop: 12, display: 'inline-block' }}>Edit Product</Link>
          )}
          {product.description && (
            /<[^>]+>/.test(product.description)
              ? <div style={{ marginTop: 16, lineHeight: 1.6, maxWidth: '100%', overflow: 'hidden' }} className="post-content-wrapper" dangerouslySetInnerHTML={{ __html: richMediaHtml(product.description) }} />
              : <p style={{ marginTop: 16, lineHeight: 1.6 }}>{product.description}</p>
          )}
          {product.hrt_donation_percent > 0 && (
            <div style={{ background: "var(--ui-muted-surface)", borderRadius: 10, padding: 12, marginTop: 16, textAlign: 'center' }}>
              <span style={{ fontSize: 13, color: "var(--ui-accent)", fontWeight: 600 }}>{product.hrt_donation_percent}% of this purchase goes to the HRT Fund</span>
            </div>
          )}
          {product.shipping_info && <p style={{ fontSize: 13, color: "var(--ui-muted)", marginTop: 8 }}>📦 {product.shipping_info}</p>}
          {product.service_details && <p style={{ fontSize: 13, color: "var(--ui-muted)", marginTop: 8 }}>🛠️ {product.service_details}</p>}
          {user && product.user_id !== user.id && (product.stock === null || product.stock > 0) && (
            <div style={{ marginTop: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" min="1" max={product.stock || 99} value={quantity} onChange={e => setQuantity(Number(e.target.value))}
                style={{ width: 60, padding: 10, borderRadius: 12, border: "2px solid var(--ui-border)", textAlign: 'center' }} />
              <button onClick={addToCart} disabled={addingToCart} className="btn btn-primary" style={{ flex: 1, padding: 14 }}>
                {addingToCart ? 'Adding...' : 'Add to Cart'}
              </button>
            </div>
          )}
        </div>
      </div>

      {product.reviews && product.reviews.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2>Reviews</h2>
          {product.reviews.map(r => (
            <div key={r.id} className="card" style={{ padding: 16, marginBottom: 8 }}>
              <p style={{ fontWeight: 600, fontSize: 14 }}>@{r.username} <span style={{ color: '#f5c518' }}>{'★'.repeat(r.rating)}</span></p>
              {r.review && <p style={{ color: "var(--ui-muted)", marginTop: 4, fontSize: 14 }}>{r.review}</p>}
              <p style={{ color: "var(--ui-muted)", fontSize: 12, marginTop: 4 }}>{new Date(r.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// PRODUCT CONTENT ROWS EDITOR (image grids + text blocks, like PostEditor)
// ============================================
const parseProductContentHtml = (html) => {
  if (!html || !/<[^>]+>/.test(html)) return { text: html || '', rows: [] };
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const rows = [];
  let textParts = [];
  const genId = () => Math.random().toString(36).slice(2);
  for (const node of doc.body.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent.trim()) textParts.push(node.textContent);
    } else if (node.classList?.contains('post-image-grid')) {
      const cols = parseInt(node.dataset?.cols) || 1;
      const urls = [];
      for (const child of node.children) {
        if (child.tagName === 'IMG' && child.src) urls.push(child.src);
        else if (child.tagName === 'VIDEO' && child.src) urls.push(child.src);
      }
      if (urls.length > 0) rows.push({ id: genId(), type: 'image', urls, layout: cols });
    } else if (node.tagName === 'P' || node.tagName === 'DIV') {
      if (rows.length > 0) {
        rows.push({ id: genId(), type: 'text', content: node.textContent || '' });
      } else {
        textParts.push(node.textContent || '');
      }
    } else {
      textParts.push(node.textContent || '');
    }
  }
  return { text: textParts.join('\n').trim(), rows };
};

const ProductContentRows = React.forwardRef((props, ref) => {
  const [imageRows, setImageRows] = useState(props.initialRows || []);
  const [dragSrc, setDragSrc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const pendingUploadRowId = useRef(null);
  const modalFileInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const isVideoUrl = (url) => /\.(mp4|webm|mov|avi|mkv)$/i.test(url);
  const genRowId = () => Math.random().toString(36).slice(2);

  const setRowLayout = (rowId, layout) =>
    setImageRows(prev => prev.map(r => r.id === rowId ? { ...r, layout, urls: (r.urls || []).slice(0, layout) } : r));

  const moveRow = (rowId, dir) =>
    setImageRows(prev => {
      const i = prev.findIndex(r => r.id === rowId);
      if (i + dir < 0 || i + dir >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[i + dir]] = [next[i + dir], next[i]];
      return next;
    });

  const removeRow = (rowId) => setImageRows(prev => prev.filter(r => r.id !== rowId));

  const removeRowImage = (rowId, imgIdx) =>
    setImageRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, urls: r.urls.filter((_, i) => i !== imgIdx) } : r
    ).filter(r => r.type === 'text' || r.urls.length > 0));

  const addTextRow = () =>
    setImageRows(prev => [...prev, { id: genRowId(), type: 'text', content: '' }]);

  const updateRowText = (rowId, content) =>
    setImageRows(prev => prev.map(r => r.id === rowId ? { ...r, content } : r));

  const handleImgDragStart = (e, rowId, imgIdx) => {
    setDragSrc({ rowId, imgIdx });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImgDrop = (e, targetRowId, targetImgIdx) => {
    e.preventDefault();
    if (!dragSrc) return;
    setImageRows(prev => {
      const next = prev.map(r => ({ ...r, urls: [...(r.urls || [])] }));
      const src = next.find(r => r.id === dragSrc.rowId && r.type !== 'text');
      const tgt = next.find(r => r.id === targetRowId && r.type !== 'text');
      if (!src || !tgt) return prev;
      if (dragSrc.rowId === targetRowId && dragSrc.imgIdx === targetImgIdx) return prev;
      const srcUrl = src.urls[dragSrc.imgIdx];
      const tgtUrl = tgt.urls[targetImgIdx];
      if (tgtUrl !== undefined) { src.urls[dragSrc.imgIdx] = tgtUrl; tgt.urls[targetImgIdx] = srcUrl; }
      else { if (tgt.urls.length >= (tgt.layout || 3)) return prev; tgt.urls.push(srcUrl); src.urls.splice(dragSrc.imgIdx, 1); }
      return next.filter(r => r.type === 'text' || r.urls.length > 0);
    });
    setDragSrc(null);
  };

  const uploadFileWithProgress = (file, token, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('image', file);
      xhr.upload.addEventListener('progress', e => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); });
      xhr.addEventListener('load', () => { if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText)); else reject(new Error('Upload failed')); });
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.open('POST', `${API_URL}/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`);
      xhr.send(formData);
    });

  const handleModalUpload = async (files) => {
    const fileArray = Array.from(files);
    if (!fileArray.length) return;
    setUploadProgress(fileArray.map(f => ({ name: f.name, progress: 0, done: false, error: false })));
    setUploading(true);
    const newUrls = [];
    for (let i = 0; i < fileArray.length; i++) {
      try {
        const data = await uploadFileWithProgress(fileArray[i], localStorage.getItem(AUTH_TOKEN_KEY), pct => {
          setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, progress: pct } : p));
        });
        setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, progress: 100, done: true } : p));
        newUrls.push(`${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${data.url}`);
      } catch {
        setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, error: true } : p));
      }
    }
    setUploading(false);
    const targetId = pendingUploadRowId.current;
    if (newUrls.length > 0) {
      if (targetId) {
        setImageRows(prev => prev.map(r => {
          if (r.id !== targetId) return r;
          const max = r.layout || 3;
          return { ...r, urls: [...r.urls, ...newUrls].slice(0, max) };
        }));
      } else {
        const layout = Math.min(newUrls.length, 3);
        setImageRows(prev => [...prev, { id: genRowId(), type: 'image', urls: newUrls.slice(0, layout), layout }]);
      }
      pendingUploadRowId.current = null;
    }
    setTimeout(() => { setUploadModal(false); setUploadProgress([]); }, 600);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const newUrls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        const response = await fetch(`${API_URL}/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
        if (!response.ok) throw new Error('Failed to upload');
        const data = await response.json();
        newUrls.push(`${window.location.origin}${BASE_PATH === '' ? '' : BASE_PATH}${data.url}`);
      }
      const targetId = pendingUploadRowId.current;
      if (targetId) {
        setImageRows(prev => prev.map(r => { if (r.id !== targetId) return r; return { ...r, urls: [...r.urls, ...newUrls].slice(0, r.layout || 3) }; }));
      } else {
        setImageRows(prev => [...prev, { id: genRowId(), type: 'image', urls: newUrls.slice(0, 3), layout: Math.min(newUrls.length, 3) }]);
      }
      pendingUploadRowId.current = null;
    } catch (err) { console.error('Upload failed:', err); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const addNewImageRow = () => {
    const newId = genRowId();
    setImageRows(prev => [...prev, { id: newId, type: 'image', urls: [], layout: 1 }]);
    pendingUploadRowId.current = newId;
    setUploadProgress([]);
    setUploadModal(true);
  };

  useImperativeHandle(ref, () => ({
    getRows: () => imageRows,
    buildHtml: () => {
      let html = '';
      for (const row of imageRows) {
        if (row.type === 'text') {
          if (row.content?.trim()) html += '<p>' + row.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split('\n').join('<br/>') + '</p>';
        } else if (row.urls?.length > 0) {
          const mediaHtml = row.urls.map(url =>
            /\.(mp4|webm|mov|avi|mkv)$/i.test(url) ? `<video src="${url}" controls></video>` : `<img src="${url}" alt="" />`
          ).join('');
          html += `<div class="post-image-grid" data-cols="${row.layout}">${mediaHtml}</div>`;
        }
      }
      return html;
    },
    reset: () => setImageRows([]),
  }));

  return (
    <>
      <div style={{ margin: '16px 0 8px' }}>
        <label style={{ fontWeight: 600, fontSize: 13, color: "var(--ui-muted)", display: 'block', marginBottom: 8 }}>Page Content</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={addTextRow} className="row-add-btn" style={{ padding: '8px 16px', borderRadius: 8, border: "2px solid var(--ui-border)", background: "var(--ui-muted-surface)", cursor: 'pointer', fontSize: 13, fontWeight: 700, color: "var(--ui-accent)" }}>+ Text Block</button>
          <button type="button" onClick={addNewImageRow} className="row-add-btn" style={{ padding: '8px 16px', borderRadius: 8, border: "2px solid var(--ui-border)", background: "var(--ui-muted-surface)", cursor: 'pointer', fontSize: 13, fontWeight: 700, color: "var(--ui-accent)" }}>+ Media</button>
        </div>
      </div>

      {imageRows.length > 0 && (
        <div className="composer-image-rows">
          {imageRows.map((row, rowIdx) => (
            <div key={row.id} className="composer-row">
              <div className="composer-row-header">
                {row.type === 'text' && <span className="row-type-label">¶ Text</span>}
                {row.type !== 'text' && (
                  <div className="row-layout-btns">
                    {[1, 2, 3].map(n => (
                      <button key={n} type="button"
                        className={`row-layout-btn${row.layout === n ? ' active' : ''}`}
                        onClick={() => setRowLayout(row.id, n)}
                        title={n === 1 ? '1 column' : n === 2 ? '2 columns' : '3 columns'}
                      >{n}</button>
                    ))}
                  </div>
                )}
                <div style={{ flex: 1 }} />
                {rowIdx > 0 && <button type="button" className="row-ctrl-btn" onClick={() => moveRow(row.id, -1)}>↑</button>}
                {rowIdx < imageRows.length - 1 && <button type="button" className="row-ctrl-btn" onClick={() => moveRow(row.id, 1)}>↓</button>}
                <button type="button" className="row-ctrl-btn row-ctrl-remove" onClick={() => removeRow(row.id)}>×</button>
              </div>
              {row.type === 'text' ? (
                <textarea
                  className="composer-draft row-text-area"
                  placeholder="Add text…"
                  value={row.content || ''}
                  onChange={e => updateRowText(row.id, e.target.value)}
                  rows={3}
                />
              ) : (
                <div className={`composer-image-grid cols-${row.layout}`}>
                  {(row.urls || []).map((url, imgIdx) => (
                    <div key={imgIdx}
                      className={`composer-image-wrap${dragSrc?.rowId === row.id && dragSrc?.imgIdx === imgIdx ? ' dragging' : ''}`}
                      draggable onDragStart={e => handleImgDragStart(e, row.id, imgIdx)}
                      onDragOver={e => e.preventDefault()} onDrop={e => handleImgDrop(e, row.id, imgIdx)}
                    >
                      {isVideoUrl(url) ? <SocialVideo src={url} className="composer-image-preview" /> : <SocialImage src={url} alt="" className="composer-image-preview" />}
                      <button type="button" className="composer-image-remove" onClick={() => removeRowImage(row.id, imgIdx)}>×</button>
                    </div>
                  ))}
                  {(row.urls || []).length < (row.layout || 3) && (
                    <div className="composer-image-wrap composer-add-slot"
                      onClick={() => { pendingUploadRowId.current = row.id; setUploadProgress([]); setUploadModal(true); }}
                      onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                      onDragLeave={e => e.currentTarget.classList.remove('dragover')}
                      onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('dragover'); if (e.dataTransfer.files?.length) { pendingUploadRowId.current = row.id; handleImageUpload({ target: { files: e.dataTransfer.files } }); } else { handleImgDrop(e, row.id, (row.urls || []).length); } }}
                    >+</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {uploadModal && (
        <div className="upload-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !uploading) { setUploadModal(false); pendingUploadRowId.current = null; } }}>
          <div className="upload-modal">
            <div className="upload-modal-header">
              <span>Upload Media</span>
              {!uploading && <button type="button" className="upload-modal-close" onClick={() => { setUploadModal(false); pendingUploadRowId.current = null; }}>×</button>}
            </div>
            {uploadProgress.length === 0 ? (
              <div className="upload-dropzone"
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                onDragLeave={e => e.currentTarget.classList.remove('dragover')}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('dragover'); handleModalUpload(e.dataTransfer.files); }}
                onClick={() => modalFileInputRef.current?.click()}
              >
                <div className="upload-dropzone-icon">🖼️</div>
                <p className="upload-dropzone-text">Drag & drop images or videos here</p>
                <p className="upload-dropzone-sub">or click to browse files</p>
                <input type="file" ref={modalFileInputRef} multiple accept="image/*,video/*" style={{ display: 'none' }} onChange={e => handleModalUpload(e.target.files)} />
              </div>
            ) : (
              <div className="upload-progress-list">
                {uploadProgress.map((p, i) => (
                  <div key={i} className="upload-progress-item">
                    <div className="upload-progress-name" title={p.name}>{p.name}</div>
                    <div className="upload-progress-bar-wrap">
                      <div className="upload-progress-bar" style={{ width: `${p.progress}%`, background: p.error ? '#e53e3e' : p.done ? '#38a169' : "var(--ui-accent)" }} />
                    </div>
                    <div className="upload-progress-pct">{p.error ? 'Failed' : p.done ? '✓' : `${p.progress}%`}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*,video/*" multiple style={{ display: 'none' }} />
    </>
  );
});

// ============================================
// EDIT PRODUCT PAGE
// ============================================
const EditProductPage = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', type: 'physical', category: '', stock: '', shipping_info: '', service_details: '', weight_oz: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPhotos, setNewPhotos] = useState([]);
  const [initialContentRows, setInitialContentRows] = useState([]);
  const editContentRowsRef = useRef(null);
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/products/${id}`).then(r => r.json()).then(d => {
      setProduct(d);
      const parsed = parseProductContentHtml(d.description || '');
      setForm({
        name: d.name || '', description: parsed.text, price: (d.price_cents / 100).toFixed(2),
        type: d.type || 'physical', category: d.category || '', stock: d.stock !== null && d.stock !== undefined ? String(d.stock) : '',
        shipping_info: d.shipping_info || '', service_details: d.service_details || '', weight_oz: d.weight_oz ? String(d.weight_oz) : '',
      });
      setInitialContentRows(parsed.rows);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    if (!form.name || !form.price) return alert('Name and price are required');
    setSaving(true);
    try {
      const rowsHtml = editContentRowsRef.current?.buildHtml() || '';
      const descText = form.description ? form.description.split('\n').join('<br/>') : '';
      const fullDescription = descText + rowsHtml;
      const res = await fetch(`${API_URL}/products/${id}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({
        name: form.name, description: fullDescription, price_cents: Math.round(parseFloat(form.price) * 100),
        category: form.category || null, stock: form.stock ? Number(form.stock) : null,
        shipping_info: form.shipping_info || null, service_details: form.service_details || null,
        weight_oz: form.type === 'physical' && form.weight_oz ? Number(form.weight_oz) : null,
      })});
      if (!res.ok) { const d = await res.json(); alert(d.error); setSaving(false); return; }
      if (newPhotos.length > 0) {
        const formData = new FormData();
        for (const f of newPhotos) formData.append('images', f);
        await fetch(`${API_URL}/products/${id}/images`, { method: 'POST', headers, body: formData });
      }
      alert('Product updated!');
      navigate(`${BASE_PATH}/marketplace/seller`);
    } catch { alert('Failed to update product'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Loading...</p></div>;
  if (!product) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Product not found</p></div>;

  const existingImgs = typeof product.images === 'string' ? JSON.parse(product.images) : (product.images || []);

  return (
    <div className="container" style={{ maxWidth: 600, padding: 20 }}>
      <h1>Edit Product</h1>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Current Photos</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {existingImgs.map((img, i) => (
            <div key={i} style={{ width: 80, height: 80, borderRadius: 10, overflow: 'hidden', border: i === 0 ? "3px solid var(--ui-border)" : "2px solid var(--ui-border)" }}>
              <SocialImage src={`${API_URL.replace('/api', '')}${img}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ))}
          {newPhotos.map((file, i) => (
            <div key={`new-${i}`} style={{ position: 'relative', width: 80, height: 80, borderRadius: 10, overflow: 'hidden', border: '2px solid #22c55e' }}>
              <SocialImage src={URL.createObjectURL(file)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <button onClick={() => setNewPhotos(prev => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer' }}>X</button>
            </div>
          ))}
          {(existingImgs.length + newPhotos.length) < 8 && (
            <label style={{ width: 80, height: 80, borderRadius: 10, border: "2px dashed var(--ui-border)", display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: "var(--ui-muted)", fontSize: 24 }}>
              +
              <input type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={e => { const files = Array.from(e.target.files || []); if (files.length > 0) setNewPhotos(prev => [...prev, ...files].slice(0, 8 - existingImgs.length)); e.target.value = ''; }} />
            </label>
          )}
        </div>
      </div>
      <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
      <div className="form-group"><label>Price ($)</label><input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></div>
      <div className="form-group"><label>Type</label>
        <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} disabled>
          <option value="physical">Physical</option><option value="digital">Digital</option><option value="service">Service</option>
        </select>
      </div>
      <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} /></div>
      <ProductContentRows ref={editContentRowsRef} initialRows={initialContentRows} key={initialContentRows.length} />
      <div className="form-group"><label>Category</label><input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
      <div className="form-group"><label>Stock (empty = unlimited)</label><input type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} /></div>
      {form.type === 'physical' && <div className="form-group"><label>Weight (oz)</label><input type="number" step="0.1" value={form.weight_oz} onChange={e => setForm({ ...form, weight_oz: e.target.value })} placeholder="e.g. 8" /><small style={{ color: "var(--ui-muted)" }}>Used for shipping rate calculation</small></div>}
      {form.type === 'physical' && <div className="form-group"><label>Shipping Info</label><textarea value={form.shipping_info} onChange={e => setForm({ ...form, shipping_info: e.target.value })} rows={2} /></div>}
      {form.type === 'service' && <div className="form-group"><label>Service Details</label><textarea value={form.service_details} onChange={e => setForm({ ...form, service_details: e.target.value })} rows={2} /></div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ flex: 1 }}>{saving ? 'Saving...' : 'Save Changes'}</button>
        <button onClick={() => navigate(`${BASE_PATH}/marketplace/seller`)} className="btn btn-secondary">Cancel</button>
      </div>
    </div>
  );
};

// ============================================
// CART PAGE
// ============================================
const CartPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCart = () => {
    fetch(`${API_URL}/cart`, { headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` } })
      .then(r => r.json()).then(d => setItems(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { if (user) fetchCart(); else setLoading(false); }, [user]);

  const updateQty = async (itemId, qty) => {
    await fetch(`${API_URL}/cart/${itemId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` }, body: JSON.stringify({ quantity: qty }) });
    fetchCart();
  };

  const removeItem = async (itemId) => {
    await fetch(`${API_URL}/cart/${itemId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` } });
    fetchCart();
  };

  if (!user) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Please log in to view your cart.</p></div>;

  const subtotal = items.reduce((sum, i) => sum + i.price_cents * i.quantity, 0);
  const platformFee = Math.round(subtotal * 0.05);
  const hrtDonation = items.reduce((sum, i) => sum + Math.round(i.price_cents * i.quantity * (Number(i.hrt_donation_percent || 5) / 100)), 0);
  const total = subtotal + platformFee + hrtDonation;

  return (
    <div className="container" style={{ maxWidth: 600, padding: 20 }}>
      <h1>Shopping Cart</h1>
      {loading ? <p>Loading...</p> : items.length === 0 ? <p style={{ color: "var(--ui-muted)" }}>Your cart is empty.</p> : (
        <>
          {items.map(item => {
            const imgs = typeof item.images === 'string' ? JSON.parse(item.images) : item.images;
            return (
              <div key={item.id} className="card" style={{ padding: 12, marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 60, height: 60, borderRadius: 8, background: "var(--ui-muted-surface)", overflow: 'hidden', flexShrink: 0 }}>
                  {imgs && imgs.length > 0 && <SocialImage src={`${API_URL.replace('/api', '')}${imgs[0]}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, margin: 0, fontSize: 14 }}>{item.name}</p>
                  <p style={{ color: "var(--ui-accent)", fontWeight: 700, margin: '2px 0', fontSize: 14 }}>${(item.price_cents / 100).toFixed(2)}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => updateQty(item.id, Math.max(1, item.quantity - 1))} style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid var(--ui-border)", cursor: 'pointer', background: "var(--ui-surface)" }}>−</button>
                  <span style={{ width: 28, textAlign: 'center', fontWeight: 600 }}>{item.quantity}</span>
                  <button onClick={() => updateQty(item.id, item.quantity + 1)} style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid var(--ui-border)", cursor: 'pointer', background: "var(--ui-surface)" }}>+</button>
                </div>
                <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>
            );
          })}
          <div className="card" style={{ padding: 16, marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span>Subtotal</span><span>${(subtotal / 100).toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13, color: "var(--ui-muted)" }}><span>Platform fee (5%)</span><span>${(platformFee / 100).toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: "var(--ui-muted)" }}><span>HRT Fund donation</span><span>${(hrtDonation / 100).toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 18, borderTop: "1px solid var(--ui-border)", paddingTop: 8 }}><span>Total</span><span style={{ color: "var(--ui-accent)" }}>${(total / 100).toFixed(2)}</span></div>
          </div>
          <button onClick={() => navigate(`${BASE_PATH}/marketplace/checkout`)} className="btn btn-primary" style={{ width: '100%', marginTop: 12, padding: 14 }}>
            Proceed to Checkout
          </button>
        </>
      )}
    </div>
  );
};

// ============================================
// CHECKOUT PAGE
// ============================================
const CheckoutPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [address, setAddress] = useState({ name: '', street: '', city: '', state: '', zip: '', country: 'US' });
  const [shippingRates, setShippingRates] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [selectedRates, setSelectedRates] = useState({});
  const navigate = useNavigate();
  const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` };

  useEffect(() => {
    fetch(`${API_URL}/cart`, { headers: authHeaders })
      .then(r => r.json()).then(d => setItems(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setFetchLoading(false));
  }, []);

  useEffect(() => {
    const initSquare = async () => {
      try {
        const res = await fetch(`${API_URL}/payments/config`);
        const config = await res.json();
        if (!window.Square) return;
        const payments = window.Square.payments(config.applicationId, config.locationId);
        const c = await payments.card();
        await c.attach('#checkout-card-container');
        setCard(c);
      } catch (e) { console.error('Square init:', e); }
    };
    initSquare();
  }, []);

  const hasPhysical = items.some(i => i.type === 'physical');
  const subtotal = items.reduce((sum, i) => sum + i.price_cents * i.quantity, 0);
  const platformFee = Math.round(subtotal * 0.05);
  const hrtDonation = items.reduce((sum, i) => sum + Math.round(i.price_cents * i.quantity * (Number(i.hrt_donation_percent || 5) / 100)), 0);
  const totalShipping = Object.values(selectedRates).reduce((sum, r) => sum + (r.rate_cents || 0), 0);
  const total = subtotal + platformFee + hrtDonation + totalShipping;

  const fetchShippingRates = async () => {
    if (!address.street || !address.city || !address.state || !address.zip) return;
    setShippingLoading(true);
    try {
      const res = await fetch(`${API_URL}/shipping/rates`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination: address }),
      });
      const data = await res.json();
      setShippingRates(data.rates || []);
      // Auto-select cheapest rate for each shop
      const autoSelected = {};
      for (const shopRate of (data.rates || [])) {
        if (shopRate.rates && shopRate.rates.length > 0) {
          autoSelected[shopRate.shop_id] = { ...shopRate.rates[0], shipment_id: shopRate.shipment_id };
        }
      }
      setSelectedRates(autoSelected);
    } catch { setShippingRates([]); }
    finally { setShippingLoading(false); }
  };

  const handleCheckout = async () => {
    if (!card) return;
    if (hasPhysical && (!address.name || !address.street || !address.city || !address.state || !address.zip)) {
      setError('Please fill in your shipping address'); return;
    }
    if (hasPhysical && shippingRates === null) {
      setError('Please calculate shipping rates first'); return;
    }
    setLoading(true); setError('');
    try {
      const result = await card.tokenize();
      if (result.status !== 'OK') throw new Error(result.errors?.[0]?.message || 'Card error');
      const shippingSelections = Object.entries(selectedRates).map(([shopId, rate]) => ({
        shop_id: Number(shopId), rate_cents: rate.rate_cents, shipment_id: rate.shipment_id,
      }));
      const res = await fetch(`${API_URL}/orders/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          nonce: result.token, shipping_address: hasPhysical ? address : null,
          shipping_cost_cents: totalShipping, shipping_selections: shippingSelections,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setSuccess(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  if (!user) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Please log in.</p></div>;
  if (success) return (
    <div className="container" style={{ maxWidth: 500, textAlign: 'center', padding: '60px 20px' }}>
      <h1 style={{ color: '#22c55e', fontSize: 48 }}>✓</h1>
      <h2>Order Placed!</h2>
      <p style={{ color: "var(--ui-muted)" }}>Your order has been placed successfully. Check your orders page for updates.</p>
      <button onClick={() => navigate(`${BASE_PATH}/marketplace`)} className="btn btn-primary" style={{ marginTop: 16 }}>Continue Shopping</button>
    </div>
  );

  return (
    <div className="container" style={{ maxWidth: 600, padding: 20 }}>
      <h1>Checkout</h1>
      {fetchLoading ? <p>Loading...</p> : items.length === 0 ? <p>Cart is empty</p> : (
        <>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Order Summary</h3>
            {items.map(i => <p key={i.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, margin: '4px 0' }}><span>{i.name} × {i.quantity}</span><span>${(i.price_cents * i.quantity / 100).toFixed(2)}</span></p>)}
            <div style={{ borderTop: "1px solid var(--ui-border)", paddingTop: 8, marginTop: 8, fontSize: 13, color: "var(--ui-muted)" }}>
              <p style={{ display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}><span>Subtotal</span><span>${(subtotal / 100).toFixed(2)}</span></p>
              <p style={{ display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}><span>Platform fee (5%)</span><span>${(platformFee / 100).toFixed(2)}</span></p>
              <p style={{ display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}><span>HRT Fund donation</span><span>${(hrtDonation / 100).toFixed(2)}</span></p>
              {totalShipping > 0 && <p style={{ display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}><span>Shipping</span><span>${(totalShipping / 100).toFixed(2)}</span></p>}
            </div>
            <p style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: "1px solid var(--ui-border)", paddingTop: 8, marginTop: 8 }}><span>Total</span><span style={{ color: "var(--ui-accent)" }}>${(total / 100).toFixed(2)}</span></p>
          </div>

          {hasPhysical && (
            <div className="card" style={{ padding: 16, marginTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>Shipping Address</h3>
              {['name', 'street', 'city', 'state', 'zip'].map(f => (
                <input key={f} placeholder={f.charAt(0).toUpperCase() + f.slice(1)} value={address[f]} onChange={e => setAddress({ ...address, [f]: e.target.value })}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: "1px solid var(--ui-border)", marginBottom: 8, boxSizing: 'border-box', fontSize: 14 }} />
              ))}
              <button onClick={fetchShippingRates} disabled={shippingLoading || !address.street || !address.city || !address.state || !address.zip}
                className="btn btn-secondary" style={{ width: '100%', marginTop: 4 }}>
                {shippingLoading ? 'Calculating...' : 'Calculate Shipping Rates'}
              </button>
            </div>
          )}

          {shippingRates && shippingRates.length > 0 && (
            <div className="card" style={{ padding: 16, marginTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>Shipping Options</h3>
              {shippingRates.map(shopRate => (
                <div key={shopRate.shop_id} style={{ marginBottom: 12 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{shopRate.shop_name}</p>
                  {shopRate.error ? (
                    <p style={{ color: '#dc3545', fontSize: 13 }}>{shopRate.error}</p>
                  ) : shopRate.rates.length === 0 ? (
                    <p style={{ color: "var(--ui-muted)", fontSize: 13 }}>No shipping rates available</p>
                  ) : (
                    shopRate.rates.slice(0, 5).map(rate => (
                      <label key={rate.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, marginBottom: 4,
                        border: selectedRates[shopRate.shop_id]?.id === rate.id ? "2px solid var(--ui-border)" : "1px solid var(--ui-border)", cursor: 'pointer', background: selectedRates[shopRate.shop_id]?.id === rate.id ? "var(--ui-muted-surface)" : "var(--ui-surface)" }}>
                        <input type="radio" name={`shipping-${shopRate.shop_id}`} checked={selectedRates[shopRate.shop_id]?.id === rate.id}
                          onChange={() => setSelectedRates(prev => ({ ...prev, [shopRate.shop_id]: { ...rate, shipment_id: shopRate.shipment_id } }))} />
                        <span style={{ flex: 1, fontSize: 13 }}>{rate.carrier} {rate.service}</span>
                        {rate.delivery_days && <span style={{ fontSize: 12, color: "var(--ui-muted)" }}>{rate.delivery_days} day{rate.delivery_days !== 1 ? 's' : ''}</span>}
                        <span style={{ fontWeight: 600, fontSize: 13 }}>${(rate.rate_cents / 100).toFixed(2)}</span>
                      </label>
                    ))
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <label style={{ fontWeight: 600, fontSize: 14 }}>Payment</label>
            <div id="checkout-card-container" style={{ marginTop: 6 }}></div>
          </div>

          {error && <p style={{ color: '#dc3545', marginTop: 8, fontSize: 14 }}>{error}</p>}
          <button onClick={handleCheckout} disabled={loading} className="btn btn-primary" style={{ width: '100%', marginTop: 16, padding: 14 }}>
            {loading ? 'Processing...' : `Pay $${(total / 100).toFixed(2)}`}
          </button>
        </>
      )}
    </div>
  );
};

// ============================================
// SHOP PAGE
// ============================================
const ShopPage = () => {
  const { slug } = useParams();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/shops/${slug}`).then(r => r.json()).then(d => setShop(d)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Loading...</p></div>;
  if (!shop) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Shop not found</p></div>;

  return (
    <div className="container" style={{ padding: 20 }}>
      {shop.banner_image && <SocialImage src={`${API_URL.replace('/api', '')}${shop.banner_image}`} alt="" style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 16, marginBottom: 16 }} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        {shop.icon && <SocialImage src={`${API_URL.replace('/api', '')}${shop.icon}`} alt="" style={{ width: 64, height: 64, borderRadius: 16, objectFit: 'cover' }} />}
        <div>
          <h1 style={{ margin: 0 }}>{shop.name}</h1>
          <p style={{ color: "var(--ui-muted)", margin: '4px 0 0' }}>by @{shop.username}</p>
        </div>
      </div>
      {shop.description && <p style={{ color: "var(--ui-muted)", marginBottom: 20 }}>{shop.description}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
        {(shop.products || []).map(p => {
          const imgs = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
          return (
            <div key={p.id} onClick={() => navigate(`${BASE_PATH}/marketplace/product/${p.id}`)} className="card" style={{ cursor: 'pointer', padding: 0, overflow: 'hidden' }}>
              <div style={{ aspectRatio: '1/1', background: "var(--ui-muted-surface)", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {imgs && imgs.length > 0 ? <SocialImage src={`${API_URL.replace('/api', '')}${imgs[0]}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                  <span style={{ color: "var(--ui-muted)", fontSize: 32 }}>{p.type === 'digital' ? '📁' : p.type === 'service' ? '🛠️' : '📦'}</span>}
              </div>
              <div style={{ padding: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14 }}>{p.name}</h3>
                <p style={{ margin: '4px 0 0', fontWeight: 700, color: "var(--ui-accent)" }}>${(p.price_cents / 100).toFixed(2)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// SELLER DASHBOARD PAGE
// ============================================
const SellerDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newShop, setNewShop] = useState({ name: '', description: '', slug: '', origin_street: '', origin_city: '', origin_state: '', origin_zip: '', origin_country: 'US' });
  const [newProduct, setNewProduct] = useState({ name: '', description: '', price: '', type: 'physical', category: '', stock: '', weight_oz: '', external_url: '', hrt_donation_percent: '' });
  const [productPhotos, setProductPhotos] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [productTab, setProductTab] = useState(ADMIN_HANDLES.includes(user?.username) ? 'merch' : 'queerShops');
  const contentRowsRef = useRef(null);

  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  const fetchShop = () => {
    fetch(`${API_URL}/shops/my`, { headers }).then(r => r.json()).then(d => {
      setShop(d);
      if (d && d.slug) {
        fetch(`${API_URL}/shops/${d.slug}`).then(r => r.json()).then(s => setProducts(s.products || []));
        fetch(`${API_URL}/orders/selling`, { headers }).then(r => r.json()).then(o => setOrders(Array.isArray(o) ? o : []));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { if (user) fetchShop(); else setLoading(false); }, [user]);

  const createShop = async () => {
    if (!newShop.name || !newShop.slug) return alert('Name and URL slug are required');
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/shops`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(newShop) });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      fetchShop();
    } catch { alert('Failed to create shop'); }
    finally { setCreating(false); }
  };

  const addProduct = async () => {
    if (!newProduct.name || !newProduct.price) return alert('Name and price are required');
    if (productTab === 'merch' && productPhotos.length === 0) return alert('At least one product photo is required');
    if (productTab === 'queerShops' && !newProduct.external_url) return alert('Product link is required for Queer Shop products');
    setCreating(true);
    try {
      const rowsHtml = contentRowsRef.current?.buildHtml() || '';
      const descText = newProduct.description ? newProduct.description.split('\n').join('<br/>') : '';
      const fullDescription = descText + rowsHtml;
      const res = await fetch(`${API_URL}/products`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({
        name: newProduct.name, description: fullDescription, price_cents: Math.round(parseFloat(newProduct.price) * 100),
        type: newProduct.type, category: newProduct.category || null, stock: newProduct.stock ? Number(newProduct.stock) : null,
        weight_oz: newProduct.type === 'physical' && newProduct.weight_oz ? Number(newProduct.weight_oz) : null,
        external_url: productTab === 'queerShops' ? (newProduct.external_url || null) : null,
        hrt_donation_percent: newProduct.hrt_donation_percent !== '' ? Number(newProduct.hrt_donation_percent) : null,
      })});
      if (!res.ok) { const d = await res.json(); alert(d.error); setCreating(false); return; }
      const product = await res.json();
      const productId = product.id || product.productId;
      if (productId && productPhotos.length > 0) {
        const formData = new FormData();
        for (const f of productPhotos) formData.append('images', f);
        await fetch(`${API_URL}/products/${productId}/images`, { method: 'POST', headers, body: formData });
      }
      setShowAddProduct(false);
      setNewProduct({ name: '', description: '', price: '', type: 'physical', category: '', stock: '', weight_oz: '', external_url: '', hrt_donation_percent: '' });
      setProductPhotos([]);
      contentRowsRef.current?.reset();
      fetchShop();
    } catch { alert('Failed to create product'); }
    finally { setCreating(false); }
  };

  const uploadImages = async (productId, files) => {
    const formData = new FormData();
    for (const f of files) formData.append('images', f);
    await fetch(`${API_URL}/products/${productId}/images`, { method: 'POST', headers, body: formData });
    fetchShop();
  };

  const updateOrderStatus = async (orderId, status) => {
    await fetch(`${API_URL}/orders/${orderId}/status`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ status }) });
    fetchShop();
  };

  const isAdmin = user && ADMIN_HANDLES.includes(user.username);

  if (!user) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Please log in.</p></div>;
  if (loading) return <div className="container" style={{ textAlign: 'center', padding: 60 }}><p>Loading...</p></div>;

  if (!shop) return (
    <div className="container" style={{ maxWidth: 500, padding: 20 }}>
      <h1>Create Shop</h1>
      <p style={{ color: "var(--ui-muted)" }}>Create your shop to list queer-run businesses and products.</p>
      <div className="form-group"><label>Shop Name</label><input value={newShop.name} onChange={e => setNewShop({ ...newShop, name: e.target.value })} /></div>
      <div className="form-group"><label>URL Slug</label><input value={newShop.slug} onChange={e => setNewShop({ ...newShop, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="my-shop" /><small style={{ color: "var(--ui-muted)" }}>Arawra/marketplace/shop/{newShop.slug || 'my-shop'}</small></div>
      <div className="form-group"><label>Description</label><textarea value={newShop.description} onChange={e => setNewShop({ ...newShop, description: e.target.value })} rows={3} /></div>
      <h3 style={{ marginTop: 24, marginBottom: 8 }}>Shop Location</h3>
      <p style={{ color: "var(--ui-muted)", fontSize: 13, marginBottom: 12 }}>Used to calculate shipping rates for physical products.</p>
      <div className="form-group"><label>Street Address</label><input value={newShop.origin_street} onChange={e => setNewShop({ ...newShop, origin_street: e.target.value })} placeholder="123 Main St" /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-group"><label>City</label><input value={newShop.origin_city} onChange={e => setNewShop({ ...newShop, origin_city: e.target.value })} placeholder="San Francisco" /></div>
        <div className="form-group"><label>State</label><input value={newShop.origin_state} onChange={e => setNewShop({ ...newShop, origin_state: e.target.value })} placeholder="CA" /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-group"><label>ZIP Code</label><input value={newShop.origin_zip} onChange={e => setNewShop({ ...newShop, origin_zip: e.target.value })} placeholder="94102" /></div>
        <div className="form-group"><label>Country</label><input value={newShop.origin_country} onChange={e => setNewShop({ ...newShop, origin_country: e.target.value })} placeholder="US" /></div>
      </div>
      <button onClick={createShop} disabled={creating} className="btn btn-primary" style={{ width: '100%' }}>{creating ? 'Creating...' : 'Create Shop'}</button>
    </div>
  );

  return (
    <div className="container" style={{ padding: 20 }}>
      <h1>{shop.name}</h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['products', 'orders', 'settings'].map((t, i) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 16px', borderRadius: 20, border: tab === t ? "2px solid var(--ui-border)" : 'none', background: tab === t ? "var(--ui-accent)" : "var(--ui-muted-surface)", color: tab === t ? "var(--ui-on-accent)" : "var(--ui-text)", cursor: 'pointer', fontWeight: tab === t ? 900 : 600, fontSize: 13, textTransform: 'capitalize', textShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.4)' : 'none' }}>{t}</button>
        ))}
      </div>

      {tab === 'products' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[['merch', 'Arawra Shop'], ['queerShops', 'Shop LGBTQIA+ Creators']].filter(([key]) => key !== 'merch' || isAdmin).map(([key, label], i) => (
              <button key={key} onClick={() => { setProductTab(key); setShowAddProduct(false); }} style={{ padding: '8px 16px', borderRadius: 12, border: productTab === key ? "2px solid var(--ui-border)" : 'none', background: productTab === key ? "var(--ui-accent)" : "var(--ui-muted-surface)", color: productTab === key ? "var(--ui-on-accent)" : "var(--ui-muted)", cursor: 'pointer', fontWeight: productTab === key ? 900 : 600, fontSize: 13, textShadow: productTab === key ? '0 1px 3px rgba(0,0,0,0.4)' : 'none' }}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>{productTab === 'merch' ? 'Arawra Shop' : 'Shop LGBTQIA+ Creators'} ({products.filter(p => productTab === 'merch' ? (!p.external_url) : (!!p.external_url)).length})</h2>
            <button onClick={() => { setShowAddProduct(!showAddProduct); if (showAddProduct) setProductPhotos([]); }} className="btn btn-primary" style={{ fontSize: 13 }}>{showAddProduct ? 'Cancel' : '+ Add Product'}</button>
          </div>
          {showAddProduct && (
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>Primary Photo <span style={{ color: '#e0245e' }}>*</span></label>
                <p style={{ fontSize: 12, color: "var(--ui-muted)", margin: '2px 0 8px' }}>This will be the main listing image. Add more images below with "+ Media".</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  {productPhotos.length > 0 ? (
                    <div style={{ position: 'relative', width: 80, height: 80, borderRadius: 10, overflow: 'hidden', border: "3px solid var(--ui-border)" }}>
                      <SocialImage src={URL.createObjectURL(productPhotos[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: "var(--ui-accent)", color: "var(--ui-on-accent)", fontSize: 9, textAlign: 'center', padding: 1, fontWeight: 700 }}>PRIMARY</div>
                      <button onClick={() => setProductPhotos([])} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer', lineHeight: '16px' }}>X</button>
                    </div>
                  ) : (
                    <label style={{ width: 80, height: 80, borderRadius: 10, border: "2px dashed var(--ui-border)", display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: "var(--ui-muted)", fontSize: 24, flexDirection: 'column' }}>
                      +
                      <span style={{ fontSize: 9, marginTop: 2 }}>Add Photo</span>
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const files = Array.from(e.target.files || []); if (files.length > 0) setProductPhotos([files[0]]); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
              </div>
              <div className="form-group"><label>Name</label><input value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} /></div>
              <div className="form-group"><label>Price ($)</label><input type="number" step="0.01" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} /></div>
              <div className="form-group"><label>Type</label>
                <select value={newProduct.type} onChange={e => setNewProduct({ ...newProduct, type: e.target.value })}>
                  <option value="physical">Physical</option><option value="digital">Digital</option><option value="service">Service</option>
                </select>
              </div>
              <div className="form-group"><label>Description</label><textarea value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} rows={3} placeholder="Brief description of your product" /></div>
              <ProductContentRows ref={contentRowsRef} />
              <div className="form-group"><label>Category</label><input value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} placeholder="e.g. clothing, art" /></div>
              <div className="form-group"><label>Stock (leave empty for unlimited)</label><input type="number" value={newProduct.stock} onChange={e => setNewProduct({ ...newProduct, stock: e.target.value })} /></div>
              {newProduct.type === 'physical' && (
                <div className="form-group"><label>Weight (oz)</label><input type="number" step="0.1" value={newProduct.weight_oz} onChange={e => setNewProduct({ ...newProduct, weight_oz: e.target.value })} placeholder="e.g. 8" /><small style={{ color: "var(--ui-muted)" }}>Used for shipping rate calculation</small></div>
              )}
              {productTab === 'queerShops' && (
                <div className="form-group">
                  <label>Product Link <span style={{ color: '#e0245e' }}>*</span></label>
                  <input type="url" value={newProduct.external_url} onChange={e => setNewProduct({ ...newProduct, external_url: e.target.value })} placeholder="https://example.com/product" />
                  <small style={{ color: "var(--ui-muted)" }}>Link to the external shop — opens in new tab when clicked</small>
                </div>
              )}
              <div className="form-group">
                <label>HRT Fund Donation % <span style={{ color: "var(--ui-muted)", fontWeight: 400, fontSize: '0.85rem' }}>(leave empty for shop default)</span></label>
                <input type="number" min="0" max="100" step="0.1" value={newProduct.hrt_donation_percent} onChange={e => setNewProduct({ ...newProduct, hrt_donation_percent: e.target.value })} placeholder={`Shop default: ${shop?.hrt_donation_percent ?? 5}%`} />
                <small style={{ color: '#9b59b6' }}>Percentage of this product's sale donated to the HRT Fund</small>
              </div>
              <button onClick={addProduct} disabled={creating || (productTab === 'merch' && productPhotos.length === 0) || (productTab === 'queerShops' && !newProduct.external_url)} className="btn btn-primary" style={{ width: '100%', opacity: (creating || (productTab === 'merch' && productPhotos.length === 0) || (productTab === 'queerShops' && !newProduct.external_url)) ? 0.5 : 1 }}>{creating ? 'Creating...' : 'Create Product'}</button>
            </div>
          )}
          {products.filter(p => productTab === 'merch' ? (!p.external_url) : (!!p.external_url)).map(p => {
            const imgs = typeof p.images === 'string' ? JSON.parse(p.images) : (p.images || []);
            return (
              <div key={p.id} className="card" style={{ padding: 12, marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 50, height: 50, borderRadius: 8, background: "var(--ui-muted-surface)", overflow: 'hidden', flexShrink: 0, cursor: 'pointer' }} onClick={() => p.external_url ? window.open(p.external_url, '_blank', 'noopener,noreferrer') : navigate(`${BASE_PATH}/marketplace/product/${p.id}`)}>
                  {imgs.length > 0 && <SocialImage src={`${API_URL.replace('/api', '')}${imgs[0]}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => p.external_url ? window.open(p.external_url, '_blank', 'noopener,noreferrer') : navigate(`${BASE_PATH}/marketplace/product/${p.id}`)}>
                  <p style={{ fontWeight: 600, margin: 0, fontSize: 14 }}>{p.name}</p>
                  <p style={{ color: "var(--ui-accent)", fontWeight: 700, margin: 0, fontSize: 13 }}>${(p.price_cents / 100).toFixed(2)} · {p.type}</p>
                  {p.external_url && <p style={{ margin: '2px 0 0', fontSize: 11, color: "var(--ui-muted)", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.external_url}</p>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Link to={`${BASE_PATH}/marketplace/product/${p.id}/edit`} style={{ fontSize: 12, color: "var(--ui-accent)", fontWeight: 600, textDecoration: 'none' }}>Edit</Link>
                  <button onClick={() => { if (window.confirm('Delete this product?')) fetch(`${API_URL}/products/${p.id}`, { method: 'DELETE', headers }).then(() => fetchShop()); }}
                    style={{ fontSize: 12, color: '#e0245e', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Delete</button>
                </div>
              </div>
            );
          })}
        </>
      )}

      {tab === 'orders' && (
        <>
          <h2>Incoming Orders ({orders.length})</h2>
          {orders.length === 0 ? <p style={{ color: "var(--ui-muted)" }}>No orders yet.</p> : orders.map(o => (
            <div key={o.id} className="card" style={{ padding: 16, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontWeight: 600, margin: 0, fontSize: 14 }}>Order #{o.id} from @{o.buyer_username}</p>
                  <p style={{ color: "var(--ui-muted)", margin: '2px 0', fontSize: 13 }}>{new Date(o.created_at).toLocaleDateString()} · ${(o.total_cents / 100).toFixed(2)}</p>
                  <p style={{ margin: '2px 0', fontSize: 13 }}>{o.items?.map(i => `${JSON.parse(i.product_snapshot || '{}').name} ×${i.quantity}`).join(', ')}</p>
                </div>
                <div>
                  <select value={o.status} onChange={e => updateOrderStatus(o.id, e.target.value)}
                    style={{ padding: 6, borderRadius: 8, border: "1px solid var(--ui-border)", fontSize: 12 }}>
                    <option value="paid">Paid</option><option value="shipped">Shipped</option><option value="delivered">Delivered</option><option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {tab === 'settings' && (
        <div style={{ maxWidth: 400 }}>
          <h2>Shop Settings</h2>
          <div className="form-group"><label>Shop Name</label>
            <input defaultValue={shop.name}
              onBlur={e => { const v = e.target.value.trim(); if (v && v !== shop.name) fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ name: v }) }).then(() => setShop(s => ({ ...s, name: v }))); }} />
          </div>
          <div className="form-group"><label>Description</label>
            <textarea rows={3} defaultValue={shop.description || ''}
              onBlur={e => fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ description: e.target.value }) }).then(() => setShop(s => ({ ...s, description: e.target.value })))} placeholder="Tell people about your shop..." />
          </div>
          <div className="form-group"><label>HRT Fund Donation %</label>
            <input type="number" min="0" max="100" step="0.5" defaultValue={shop.hrt_donation_percent}
              onBlur={e => fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ hrt_donation_percent: Number(e.target.value) }) })} />
            <small style={{ color: "var(--ui-muted)" }}>Percentage of each sale donated to the HRT Fund (default 5%)</small>
          </div>
          <h3 style={{ marginTop: 24, marginBottom: 8 }}>Shop Location</h3>
          <p style={{ color: "var(--ui-muted)", fontSize: 13, marginBottom: 12 }}>Used to calculate shipping rates for physical products.</p>
          <div className="form-group"><label>Street Address</label><input defaultValue={shop.origin_street || ''}
            onBlur={e => fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ origin_street: e.target.value }) })} placeholder="123 Main St" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>City</label><input defaultValue={shop.origin_city || ''}
              onBlur={e => fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ origin_city: e.target.value }) })} placeholder="San Francisco" /></div>
            <div className="form-group"><label>State</label><input defaultValue={shop.origin_state || ''}
              onBlur={e => fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ origin_state: e.target.value }) })} placeholder="CA" /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>ZIP Code</label><input defaultValue={shop.origin_zip || ''}
              onBlur={e => fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ origin_zip: e.target.value }) })} placeholder="94102" /></div>
            <div className="form-group"><label>Country</label><input defaultValue={shop.origin_country || 'US'}
              onBlur={e => fetch(`${API_URL}/shops/${shop.slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ origin_country: e.target.value }) })} placeholder="US" /></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// APP
// ============================================
function App() {
  const [latestLevelUp, setLatestLevelUp] = useState(null);

  return (
    <ThemeProvider>
    <Router basename={BASE_PATH}>
      <AuthProvider>
        <a href="#main-content" className="skip-to-content">Skip to content</a>
        <Navbar onLevelUpUpdate={setLatestLevelUp} />
        <LevelUpBanner
          notification={latestLevelUp}
          onClear={() => setLatestLevelUp(null)}
        />
        <XpDrops />
        <CallManager />
        <MobileNav />
        <RestrictedBanner />
        <StorageBanner />
        <BreachBanner />
        <main id="main-content" role="main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/complete-profile" element={<CompleteProfilePage />} />
          <Route path="/edit-profile" element={<EditProfilePage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/post/:postId" element={<SinglePostPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/skills" element={<SkillsPage />} />
          <Route path="/tribes" element={<TribesPage />} />
          <Route path="/create" element={<CreatePostPage />} />
          <Route path="/tribes/create" element={<CreateTribePage />} />
          <Route path="/tribe/:tag" element={<TribePage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/trends" element={<TrendsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:category/:slug" element={<LegalDocPage />} />
          <Route path="/hrt-fund" element={<HRTFundPage />} />
          <Route path="/dmca" element={<DmcaRequestPage />} />
          <Route path="/transparency" element={<TransparencyPage />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/product/:id" element={<ProductPage />} />
          <Route path="/marketplace/product/:id/edit" element={<EditProductPage />} />
          <Route path="/marketplace/shop/:slug" element={<ShopPage />} />
          <Route path="/shop/:slug" element={<ShopPage />} />
          <Route path="/marketplace/cart" element={<CartPage />} />
          <Route path="/marketplace/checkout" element={<CheckoutPage />} />
          <Route path="/marketplace/seller" element={<SellerDashboardPage />} />
          <Route path="/profile/:identifier" element={<UserProfilePage />} />
          <Route path="/user/:identifier" element={<UserProfilePage />} />
          <Route path="/:identifier" element={<UserProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>
      </AuthProvider>
    </Router>
    </ThemeProvider>
  );
}

export default App;
